import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
// React DnD imports removed to fix drag and drop conflict  
import moment from 'moment-timezone';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './App.css';
// Old calendar system completely replaced with SimpleCalendar
import SimpleCalendar from './components/SimpleCalendar';
import Sidebar from './components/common/Sidebar';
import PackageImage from './components/common/PackageImage';
import ProductImage from './components/common/ProductImage';
import AddonImage from './components/common/AddonImage';
import ConfirmDialog from './components/common/ConfirmDialog';
import Toast from './components/common/Toast';
import StatusBadge from './components/common/StatusBadge';
import LoadingSpinner from './components/common/LoadingSpinner';
import EmptyState from './components/common/EmptyState';
import Card from './components/common/Card';
import Badge from './components/common/Badge';
import Avatar from './components/common/Avatar';
import CommandCenter from './components/common/CommandCenter';
import PublicScheduling from './components/PublicScheduling';
import PropertyWebsite from './components/PropertyWebsite';
import TerritoryMap from './components/TerritoryMap';
import ClientPortal from './components/ClientPortal';
import ListingsPage from './components/ListingsPage';
import TasksPage from './components/TasksPage';
import AppointmentsPage from './components/AppointmentsPage';
import ClientsPage from './components/ClientsPage';
import ProductsPage from './components/ProductsPage';
import CouponsSettings from './components/CouponsSettings';
import TagsSettings from './components/TagsSettings';
import CouponApply from './components/CouponApply';
import TagSelector from './components/TagSelector';
import CouponAnalytics from './components/CouponAnalytics';
import DashboardContext from './contexts/DashboardContext';
import NotificationsPage from './pages/NotificationsPage';
import PayrollPage from './pages/PayrollPage';
import AdminPage from './pages/AdminPage';
import DashboardHome from './pages/DashboardHome';
import EditListingPage from './pages/EditListingPage';
import TeamPage from './pages/TeamPage';
import EditTeamMemberPage from './pages/EditTeamMemberPage';
import OrderFormPage from './pages/OrderFormPage';
import SettingsPage from './pages/SettingsPage';
import CalendarPage from './pages/CalendarPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Route to tab mapping for URL-based navigation
const ROUTE_TO_TAB = {
  '/app': 'dashboard',
  '/app/dashboard': 'dashboard',
  '/app/listings': 'media',
  '/app/clients': 'clients',
  '/app/appointments': 'appointments',
  '/app/calendar': 'calendar',
  '/app/tasks': 'tasks',
  '/app/team': 'team',
  '/app/products': 'products',
  '/app/settings': 'settings',
  '/app/order-form': 'orderform',
  '/app/notifications': 'notifications',
  '/app/payroll': 'payroll',
  '/app/admin': 'admin'
};

const TAB_TO_ROUTE = {
  'dashboard': '/app/dashboard',
  'media': '/app/listings',
  'clients': '/app/clients',
  'appointments': '/app/appointments',
  'calendar': '/app/calendar',
  'tasks': '/app/tasks',
  'team': '/app/team',
  'products': '/app/products',
  'settings': '/app/settings',
  'orderform': '/app/order-form',
  'notifications': '/app/notifications',
  'payroll': '/app/payroll',
  'admin': '/app/admin'
  // Note: edit-listing uses dynamic route /app/listings/edit/:id - handled separately
};

// Configure axios defaults for production environment
axios.defaults.timeout = 30000; // 30 seconds default timeout (was 120s)
axios.defaults.maxContentLength = Infinity;
axios.defaults.maxBodyLength = Infinity;

// Global axios retry interceptor for transient failures
axios.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config;
    const status = error.response?.status;
    
    // On 401 Unauthorized, the token is invalid — force re-login
    // Skip for login/register endpoints to avoid reload loops
    if (status === 401 && !config?.__is401Handled && !config?.url?.includes('/login') && !config?.url?.includes('/register')) {
      config.__is401Handled = true;
      console.error('Auth token invalid (401). Clearing session.');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('permissions');
      localStorage.removeItem('isMasterAdmin');
      localStorage.removeItem('originalAdminToken');
      window.location.reload();
      return Promise.reject(error);
    }
    
    // Don't retry if no config, already retried max times, or client error
    if (!config || config.__retryCount >= 3) {
      return Promise.reject(error);
    }
    
    const isRetryable = !status || status >= 500 || error.code === 'ECONNABORTED' || 
                        error.code === 'ERR_NETWORK' || error.message?.includes('timeout');
    
    // Don't retry client errors (4xx) or non-retryable errors
    if (status && status >= 400 && status < 500) {
      return Promise.reject(error);
    }
    
    if (!isRetryable) {
      return Promise.reject(error);
    }
    
    config.__retryCount = (config.__retryCount || 0) + 1;
    console.log(`Axios retry ${config.__retryCount}/3 for ${config.url}`);
    
    // Exponential backoff
    await new Promise(r => setTimeout(r, 500 * config.__retryCount));
    
    return axios(config);
  }
);

// Helper function for file uploads with retry and better error handling
const uploadWithRetry = async (url, formData, options = {}, maxRetries = 2) => {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await axios({
        method: options.method || 'POST',
        url,
        data: formData,
        timeout: options.timeout || 120000,
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...options.headers
        },
        onUploadProgress: options.onUploadProgress
      });
      return response;
    } catch (error) {
      lastError = error;
      console.warn(`Upload attempt ${attempt + 1} failed:`, error.message);
      
      // Don't retry for client errors (4xx)
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        throw error;
      }
      
      // Wait before retry (exponential backoff)
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
  }
  throw lastError;
};

// Auth - imported from contexts/AuthContext.js
import { useAuth, AuthProvider, AuthConsumer } from './contexts/AuthContext';
import LoginForm from './pages/auth/LoginForm';
import AcceptInvitation from './pages/auth/AcceptInvitation';
import ClientSignup from './pages/auth/ClientSignup';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AppointmentFormModal from './components/modals/AppointmentFormModal';
import AppointmentDetailsModal from './components/modals/AppointmentDetailsModal';
import ProductModals from './components/modals/ProductModals';
import AddTeamMemberModal from './components/modals/AddTeamMemberModal';
import TaskModals from './components/modals/TaskModals';
import MediaModals from './components/modals/MediaModals';
import CalendarEventModals from './components/modals/CalendarEventModals';
import DeleteBusinessModal from './components/modals/DeleteBusinessModal';

// --- Auth section removed (extracted to /contexts/AuthContext.js and /pages/auth/) ---

const Dashboard = () => {
  const { user, permissions, hasPermission, logout, isMasterAdmin, isImpersonating, setIsImpersonating, originalAdminToken, setOriginalAdminToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract listing ID from URL if on edit-listing route
  const getListingIdFromPath = (pathname) => {
    const match = pathname.match(/\/app\/listings\/edit\/([^/]+)/);
    return match ? match[1] : null;
  };
  
  // Determine initial tab from URL
  const getTabFromPath = (pathname) => {
    // Check exact matches first
    if (ROUTE_TO_TAB[pathname]) return ROUTE_TO_TAB[pathname];
    // Check for edit routes
    if (pathname.startsWith('/app/listings/edit')) return 'edit-listing';
    if (pathname.startsWith('/app/team/edit')) return 'edit-team-member';
    // Default to dashboard
    return 'dashboard';
  };
  
  const [activeTab, setActiveTabState] = useState(() => getTabFromPath(location.pathname));
  
  // Custom setActiveTab that also updates the URL
  const setActiveTab = useCallback((tab, options = {}) => {
    setActiveTabState(tab);
    
    // Handle special routes with IDs
    if (tab === 'edit-listing' && options.listingId) {
      navigate(`/app/listings/edit/${options.listingId}`);
      return;
    }
    
    const route = TAB_TO_ROUTE[tab];
    if (route && location.pathname !== route) {
      navigate(route);
    }
  }, [navigate, location.pathname]);
  
  // Sync activeTab when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const tabFromPath = getTabFromPath(location.pathname);
    if (tabFromPath !== activeTab) {
      setActiveTabState(tabFromPath);
    }
  }, [location.pathname]);
  
  const [adminData, setAdminData] = useState({ businesses: [], users: [] });
  const [showDeleteBusinessModal, setShowDeleteBusinessModal] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [deletingBusiness, setDeletingBusiness] = useState(false);
  const [selectedListingForEdit, setSelectedListingForEdit] = useState(null);
  const [showEditAddressModal, setShowEditAddressModal] = useState(false);
  const [editAddressForm, setEditAddressForm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState(null);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [selectedClientToAdd, setSelectedClientToAdd] = useState('');
  const [propertyClients, setPropertyClients] = useState([]);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showCreateNewClient, setShowCreateNewClient] = useState(false);
  const [newClientForm, setNewClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });


  const [mediaExpanded, setMediaExpanded] = useState({
    images: false,
    videos: false,
    floorPlans: false,
    tours3d: false,
    files: false
  });
  const [imageThumbnailSize, setImageThumbnailSize] = useState(160); // Default size in pixels (160px)
  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [slideshowIndex, setSlideshowIndex] = useState(0);
  const [propertyDetailsExpanded, setPropertyDetailsExpanded] = useState(false);
  const [listingTasksExpanded, setListingTasksExpanded] = useState(true); // Collapsed if all tasks complete
  const [clients, setClients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [appointmentFilters, setAppointmentFilters] = useState({ dateFrom: '', dateTo: '', clientName: '', status: '' });
  const [showAppointmentFilters, setShowAppointmentFilters] = useState(false);
  // listingFilters and showListingFilters moved to ListingsPage component
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Ensure arrays are never undefined
  const safeAppointments = Array.isArray(appointments) ? appointments : [];
  const safeClients = Array.isArray(clients) ? clients : [];
  const safeTeamMembers = Array.isArray(teamMembers) ? teamMembers : [];
  const [tasks, setTasks] = useState([]);
  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const [mediaFiles, setMediaFiles] = useState([]);
  const safeMediaFiles = Array.isArray(mediaFiles) ? mediaFiles : [];
  const [tasksSummary, setTasksSummary] = useState({ upcoming: [], overdue: [], stats: {} });
  const [taskSectionsExpanded, setTaskSectionsExpanded] = useState({
    overdue: true,
    dueToday: true,
    upcoming: true
  });
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [googleCalendarEvents, setGoogleCalendarEvents] = useState([]);
  const [businessHours, setBusinessHours] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [availableDates, setAvailableDates] = useState({});  // Store date availability
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [stats, setStats] = useState({});
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todaysAppointmentsView, setTodaysAppointmentsView] = useState('list'); // 'list' or 'map'
  
  // Dashboard customization state
  const [showDashboardCustomize, setShowDashboardCustomize] = useState(false);
  const [dashboardSections, setDashboardSections] = useState(() => {
    const defaultSections = [
      { id: 'todays-appointments', name: "Today's Appointments", visible: true },
      { id: 'tasks-overview', name: 'Tasks Overview', visible: true },
      { id: 'quotes-tracker', name: 'Quotes Tracker', visible: true },
      { id: 'coupon-analytics', name: 'Coupon Analytics', visible: true },
      { id: 'recent-appointments', name: 'Recent Appointments', visible: true },
      { id: 'recent-clients', name: 'Recent Clients', visible: true },
      { id: 'recent-activity', name: 'Recent Activity', visible: true },
      { id: 'stats', name: 'Stats Overview', visible: true }
    ];
    const saved = localStorage.getItem('dashboardSections');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Merge any new default sections that don't exist in saved config
        const savedIds = new Set(parsed.map(s => s.id));
        const newSections = defaultSections.filter(s => !savedIds.has(s.id));
        if (newSections.length > 0) {
          const merged = [...parsed, ...newSections];
          localStorage.setItem('dashboardSections', JSON.stringify(merged));
          return merged;
        }
        return parsed;
      } catch (e) {
        console.error('Failed to parse dashboard sections from localStorage');
      }
    }
    return defaultSections;
  });
  const [tempDashboardSections, setTempDashboardSections] = useState([]);
  const [draggedSection, setDraggedSection] = useState(null);

  // Form states
  const [showClientForm, setShowClientForm] = useState(false);
  const [clientFormActiveTab, setClientFormActiveTab] = useState('profile'); // 'profile' or 'listings'
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    appointment_id: '',
    title: '',
    description: '',
    due_date: '',
    assigned_to: '',
    priority: 'medium',
    status: 'pending',
    propertySearch: ''
  });
  const [taskSortBy, setTaskSortBy] = useState('due_date'); // due_date, status, priority, assigned_to
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [activeTasksExpanded, setActiveTasksExpanded] = useState(true);
  const [futureTasksExpanded, setFutureTasksExpanded] = useState(false); // Collapsed by default

  const [showEventDetails, setShowEventDetails] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [calendarView, setCalendarView] = useState('month');
  
  // Schedule blocks state
  const [scheduleBlocks, setScheduleBlocks] = useState([]);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [blockForm, setBlockForm] = useState({ title: 'Blocked', photographer_ids: [], start_date: '', start_time: '09:00', end_date: '', end_time: '17:00', all_day: false, reason: '' });
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [showBlockDetails, setShowBlockDetails] = useState(false);
  
  // Drive times visualization state for day view
  const [dayDriveTimes, setDayDriveTimes] = useState({});
  const [loadingDriveTimes, setLoadingDriveTimes] = useState(false);
  
  // Pending submission details modal state
  const [selectedPendingSubmission, setSelectedPendingSubmission] = useState(null);
  const [showPendingDetails, setShowPendingDetails] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showProposeTimeModal, setShowProposeTimeModal] = useState(false);
  const [proposedDate, setProposedDate] = useState('');
  const [proposedTime, setProposedTime] = useState('');
  const [proposalNote, setProposalNote] = useState('');
  
  // Photographer calendar filter state
  const [photographerFilters, setPhotographerFilters] = useState({});
  const photographerColors = [
    { bg: 'bg-blue-100', text: 'text-blue-300', hover: 'hover:bg-blue-200', dot: 'bg-blue-500' },
    { bg: 'bg-purple-100', text: 'text-purple-800', hover: 'hover:bg-purple-200', dot: 'bg-purple-500' },
    { bg: 'bg-green-100', text: 'text-green-800', hover: 'hover:bg-green-200', dot: 'bg-green-500' },
    { bg: 'bg-orange-100', text: 'text-orange-800', hover: 'hover:bg-orange-200', dot: 'bg-orange-500' },
    { bg: 'bg-pink-100', text: 'text-pink-800', hover: 'hover:bg-pink-200', dot: 'bg-pink-500' },
    { bg: 'bg-cyan-100', text: 'text-cyan-800', hover: 'hover:bg-cyan-200', dot: 'bg-cyan-500' },
    { bg: 'bg-yellow-100', text: 'text-yellow-300', hover: 'hover:bg-yellow-200', dot: 'bg-yellow-500' },
    { bg: 'bg-red-100', text: 'text-red-800', hover: 'hover:bg-red-200', dot: 'bg-red-500' },
  ];
  
  const getPhotographerColor = (photographerId) => {
    const photographers = teamMembers.filter(m => m.role?.toLowerCase() === 'photographer');
    const index = photographers.findIndex(p => p.id === photographerId);
    return photographerColors[index % photographerColors.length] || photographerColors[0];
  };
  
  const togglePhotographerFilter = (photographerId) => {
    setPhotographerFilters(prev => ({
      ...prev,
      [photographerId]: !prev[photographerId]
    }));
  };
  
  // Initialize photographer filters when team members load
  useEffect(() => {
    if (teamMembers.length > 0) {
      const photographers = teamMembers.filter(m => m.role?.toLowerCase() === 'photographer');
      const initialFilters = {};
      photographers.forEach(p => {
        if (photographerFilters[p.id] === undefined) {
          initialFilters[p.id] = true; // Show all by default
        }
      });
      if (Object.keys(initialFilters).length > 0) {
        setPhotographerFilters(prev => ({ ...prev, ...initialFilters }));
      }
    }
  }, [teamMembers]);


  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [showSharingModal, setShowSharingModal] = useState(false);
  // Enhanced media states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [bulkOperationMode, setBulkOperationMode] = useState(false);
  const [approvalFilter, setApprovalFilter] = useState('all'); // all, pending, approved, rejected
  const [showGalleryForm, setShowGalleryForm] = useState(false);
  const [customBranding, setCustomBranding] = useState({ logo: '', primaryColor: '#3b82f6', secondaryColor: '#1f2937' });
  const [editingClient, setEditingClient] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [isReschedulingAppointment, setIsReschedulingAppointment] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedFileForSharing, setSelectedFileForSharing] = useState(null);
  const [companyLogoFile, setCompanyLogoFile] = useState(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState(null);
  const [existingCompanyLogoUrl, setExistingCompanyLogoUrl] = useState(null);
  const [uploadingCompanyLogo, setUploadingCompanyLogo] = useState(false);
  
  // Progress tracking states
  const [uploadProgress, setUploadProgress] = useState({
    isUploading: false,
    currentFile: '',
    completedFiles: 0,
    totalFiles: 0,
    overallProgress: 0,
    fileStatuses: [], // {name, status: 'pending'|'uploading'|'completed'|'failed', size}
    uploadSpeed: 0,
    timeRemaining: 0
  });

  // Appointment thumbnails state
  const [appointmentThumbnails, setAppointmentThumbnails] = useState({});

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Sidebar collapse state (collapsed by default on calendar page)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Universal search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showCommandCenter, setShowCommandCenter] = useState(false);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1); // Track selected result for keyboard navigation
  const [showMediaManager, setShowMediaManager] = useState(false);
  const [mediaManagerAppointment, setMediaManagerAppointment] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isMediaCollapsed, setIsMediaCollapsed] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    onConfirm: null 
  });
  
  // Helper functions for toast and confirm dialog
  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
  }, []);
  
  const hideToast = useCallback(() => {
    setToast({ show: false, message: '', type: 'success' });
  }, []);
  
  const showConfirm = useCallback((title, message, onConfirm) => {
    setConfirmDialog({ show: true, title, message, onConfirm });
  }, []);
  
  const hideConfirm = useCallback(() => {
    setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });
  }, []);

  // Settings state
  const [settingsTab, setSettingsTab] = useState('account');
  const [availableTags, setAvailableTags] = useState([]);
  const [accountSettings, setAccountSettings] = useState({
    business_name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    timezone: 'UTC'
  });
  const [notificationTemplates, setNotificationTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [dropboxSettings, setDropboxSettings] = useState({
    app_key: '',
    app_secret: '',
    is_connected: false
  });
  const [showDropboxBrowser, setShowDropboxBrowser] = useState(false);
  const [dropboxFiles, setDropboxFiles] = useState([]);
  const [dropboxCurrentPath, setDropboxCurrentPath] = useState('');
  const [dropboxSelectedFiles, setDropboxSelectedFiles] = useState([]);
  const [workingHours, setWorkingHours] = useState({
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '09:00', end: '17:00' },
    sunday: { enabled: false, start: '09:00', end: '17:00' }
  });
  const [products, setProducts] = useState([]);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newTeamMember, setNewTeamMember] = useState({
    name: '',
    email: '',
    role: 'Agent',
    phone: '',
    working_hours: {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: false, start: '09:00', end: '17:00' },
      sunday: { enabled: false, start: '09:00', end: '17:00' }
    },
    assigned_products: [],
    assigned_packages: []
  });

  // Google Calendar integration states
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedMemberForCalendar, setSelectedMemberForCalendar] = useState(null);
  const [calendarConnectionData, setCalendarConnectionData] = useState({
    calendar_email: '',
    calendar_name: 'Primary Calendar'
  });
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    pricing_tiers: [],
    duration_minutes: '',
    category: 'Photography',
    active: true,
    image: null,
    image_id: null
  });

  // Automated task rules states
  const [automatedTaskRules, setAutomatedTaskRules] = useState([]);
  const [showAddTaskRule, setShowAddTaskRule] = useState(false);
  const [editingTaskRule, setEditingTaskRule] = useState(null);
  const [newTaskRule, setNewTaskRule] = useState({
    name: '',
    product_ids: [],
    team_member_filter: [],
    task_title: '',
    task_description: '',
    assigned_to: '',
    due_days_after_shoot: 0,
    exclude_weekends: false,
    priority: 'medium'
  });

  // Pricing tiers management
  const [showPricingTiers, setShowPricingTiers] = useState(false);
  const [newPricingTier, setNewPricingTier] = useState({
    min_sqft: '',
    max_sqft: '',
    price: '',
    duration_minutes: ''
  });

  // Photographer availability states
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [loadingAvailableDates, setLoadingAvailableDates] = useState(false);
  const [selectedPhotographerAvailability, setSelectedPhotographerAvailability] = useState(null);
  
  // Product pricing states
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [calculatedPricing, setCalculatedPricing] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [productPricing, setProductPricing] = useState({}); // Individual product pricing for display

  // Appointment form client search states
  const [appointmentClientSearchQuery, setAppointmentClientSearchQuery] = useState('');
  const [appointmentClientSearchResults, setAppointmentClientSearchResults] = useState([]);
  const [showAppointmentClientSearch, setShowAppointmentClientSearch] = useState(false);
  const [showCreateClientInAppointment, setShowCreateClientInAppointment] = useState(false);
  const [newClientInAppointmentForm, setNewClientInAppointmentForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  
  // Address autocomplete states
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressAutocompleteService, setAddressAutocompleteService] = useState(null);

  // Edit states
  const [showEditTeamMember, setShowEditTeamMember] = useState(false);
  const [editingTeamMemberId, setEditingTeamMemberId] = useState(null);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editTeamMemberTab, setEditTeamMemberTab] = useState('general'); // Tab state for edit modal
  const [uploadingProductImage, setUploadingProductImage] = useState(false); // Loading state for product/package/addon image uploads
  
  // Appointment details modal
  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
  const [selectedAppointmentDetails, setSelectedAppointmentDetails] = useState(null);
  
  // Products view state
  const [productsViewType, setProductsViewType] = useState('list'); // 'grid' or 'list'
  
  // Products tab state
  const [productsTab, setProductsTab] = useState('products'); // 'products', 'packages', or 'addons'
  const [compactView, setCompactView] = useState(true); // Toggle for compact list view (default: compact)
  const [packageProductSearch, setPackageProductSearch] = useState(''); // Search for products in package form
  const [addonProducts, setAddonProducts] = useState([]);
  const [showAddAddon, setShowAddAddon] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [newAddon, setNewAddon] = useState({
    name: '',
    description: '',
    base_price: 0,
    duration_minutes: 0,
    linked_product_ids: [],
    image: null,
    image_id: null,
    active: true
  });
  
  // Packages state
  const [packages, setPackages] = useState([]);
  const [showAddPackage, setShowAddPackage] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [newPackage, setNewPackage] = useState({
    name: '',
    description: '',
    product_ids: [],
    discount_percentage: 0,
    discount_amount: 0,
    square_footage: 2000, // Default sqft for price calculation
    image: null,
    image_id: null,
    active: false
  });
  
  // Order Forms state
  const [orderForms, setOrderForms] = useState([]);
  const [orderFormSubmissions, setOrderFormSubmissions] = useState([]);
  const [showOrderFormBuilder, setShowOrderFormBuilder] = useState(false);
  const [editingOrderForm, setEditingOrderForm] = useState(null);
  const [newOrderForm, setNewOrderForm] = useState({
    name: '',
    description: '',
    product_ids: [],
    questions: [],
    is_active: true
  });
  
  // Package product search state
  const [packageSearchIndex, setPackageSearchIndex] = useState(0); // For keyboard navigation
  
  // Drag and drop state for reordering products/packages/addons
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedType, setDraggedType] = useState(null); // 'product', 'addon', 'package'
  
  // Reorder handlers for products/packages/addons
  const handleItemDragStart = (e, item, type) => {
    setDraggedItem(item);
    setDraggedType(type);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };
  
  const handleItemDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
    setDraggedType(null);
  };
  
  const handleItemDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDropProduct = async (e, targetProduct) => {
    e.preventDefault();
    if (!draggedItem || draggedType !== 'product' || draggedItem.id === targetProduct.id) return;
    
    const draggedIndex = products.findIndex(p => p.id === draggedItem.id);
    const targetIndex = products.findIndex(p => p.id === targetProduct.id);
    
    const newProducts = [...products];
    newProducts.splice(draggedIndex, 1);
    newProducts.splice(targetIndex, 0, draggedItem);
    
    // Update sort_order for all products
    const updatedProducts = newProducts.map((p, idx) => ({ ...p, sort_order: idx }));
    setProducts(updatedProducts);
    
    // Save to backend
    try {
      await axios.put(`${API}/settings/products/reorder`, {
        order: updatedProducts.map(p => ({ id: p.id, sort_order: p.sort_order }))
      });
    } catch (err) {
      console.error('Failed to save product order:', err);
    }
  };
  
  const handleDropAddon = async (e, targetAddon) => {
    e.preventDefault();
    if (!draggedItem || draggedType !== 'addon' || draggedItem.id === targetAddon.id) return;
    
    const draggedIndex = addonProducts.findIndex(a => a.id === draggedItem.id);
    const targetIndex = addonProducts.findIndex(a => a.id === targetAddon.id);
    
    const newAddons = [...addonProducts];
    newAddons.splice(draggedIndex, 1);
    newAddons.splice(targetIndex, 0, draggedItem);
    
    const updatedAddons = newAddons.map((a, idx) => ({ ...a, sort_order: idx }));
    setAddonProducts(updatedAddons);
    
    try {
      await axios.put(`${API}/settings/addon-products/reorder`, {
        order: updatedAddons.map(a => ({ id: a.id, sort_order: a.sort_order }))
      });
    } catch (err) {
      console.error('Failed to save addon order:', err);
    }
  };
  
  const handleDropPackage = async (e, targetPackage) => {
    e.preventDefault();
    if (!draggedItem || draggedType !== 'package' || draggedItem.id === targetPackage.id) return;
    
    const draggedIndex = packages.findIndex(p => p.id === draggedItem.id);
    const targetIndex = packages.findIndex(p => p.id === targetPackage.id);
    
    const newPackages = [...packages];
    newPackages.splice(draggedIndex, 1);
    newPackages.splice(targetIndex, 0, draggedItem);
    
    const updatedPackages = newPackages.map((p, idx) => ({ ...p, sort_order: idx }));
    setPackages(updatedPackages);
    
    try {
      await axios.put(`${API}/settings/packages/reorder`, {
        order: updatedPackages.map(p => ({ id: p.id, sort_order: p.sort_order }))
      });
    } catch (err) {
      console.error('Failed to save package order:', err);
    }
  };
  
  // Team view state
  const [teamViewType, setTeamViewType] = useState('list'); // 'grid' or 'list'
  
  // Dark mode state - load from localStorage
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  
  // Save dark mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Fetch admin data when admin tab is active
  useEffect(() => {
    if (user && activeTab === 'admin' && isMasterAdmin) {
      const fetchAdminData = async () => {
        try {
          const token = localStorage.getItem('token');
          const [businessesRes, usersRes] = await Promise.all([
            axios.get(`${API}/admin/businesses`, { headers: { Authorization: `Bearer ${token}` }}),
            axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` }})
          ]);
          setAdminData({
            businesses: businessesRes.data,
            users: usersRes.data
          });
        } catch (err) {
          console.error('Failed to fetch admin data:', err);
        }
      };
      fetchAdminData();
    }
  }, [activeTab, user, isMasterAdmin]);
  
  const [editTeamMemberForm, setEditTeamMemberForm] = useState({
    name: '',
    email: '',
    role: 'Agent',
    phone: '',
    working_hours: {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: false, start: '09:00', end: '17:00' },
      sunday: { enabled: false, start: '09:00', end: '17:00' }
    },
    assigned_products: [],
    assigned_packages: [],
    home_base_address: '',
    territory_radius_miles: 50,
    mileage_tiers: []
  });
  const [editProductForm, setEditProductForm] = useState({
    name: '',
    description: '',
    price: '',
    pricing_tiers: [],
    duration_minutes: '',
    category: 'Photography',
    active: true,
    image: null,
    image_id: null
  });
  const [editShowPricingTiers, setEditShowPricingTiers] = useState(false);
  const [editPricingTier, setEditPricingTier] = useState({
    min_sqft: '',
    max_sqft: '',
    price: '',
    duration_minutes: ''
  });

  // Aryeo Import states
  const [importConnectionStatus, setImportConnectionStatus] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [previewData, setPreviewData] = useState({});
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [aryeoToken, setAryeoToken] = useState('');
  const [aryeoTokenInput, setAryeoTokenInput] = useState('');
  const [hasAryeoToken, setHasAryeoToken] = useState(false);
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  const [importPagination, setImportPagination] = useState({
    clients: { page: 1, limit: 100 },
    listings: { page: 1, limit: 50 },
    products: { page: 1, limit: 50 }
  });
  const [selectedRecords, setSelectedRecords] = useState({
    clients: [],
    listings: [], 
    orders: [],
    products: []
  });
  const [showRecordsList, setShowRecordsList] = useState({
    clients: false,
    listings: false,
    orders: false,
    products: false
  });

  const [clientForm, setClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
    avatar: null, // File object for new avatar upload
    companyLogo: null // File object for company logo upload
  });
  
  // Avatar preview states
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [existingAvatarUrl, setExistingAvatarUrl] = useState(null);

  const [appointmentForm, setAppointmentForm] = useState({
    client_id: '',
    photographer_id: '',
    property_address: '',
    property_sqft: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 120,
    notes: ''
  });

  // Appointment form additional states
  const [timeOverrideEnabled, setTimeOverrideEnabled] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [photographerDayAppointments, setPhotographerDayAppointments] = useState([]);
  const [loadingDayAppointments, setLoadingDayAppointments] = useState(false);
  
  // Quote builder states
  const [showQuoteBuilder, setShowQuoteBuilder] = useState(false);
  const [quoteAvailableSlots, setQuoteAvailableSlots] = useState([]);
  const [loadingQuoteSlots, setLoadingQuoteSlots] = useState(false);
  const [selectedQuoteSlots, setSelectedQuoteSlots] = useState([]);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);
  const [dashboardQuotes, setDashboardQuotes] = useState([]);
  const [expandedQuoteId, setExpandedQuoteId] = useState(null);
  const [quoteActionLoading, setQuoteActionLoading] = useState(null);
  const [selectedDashboardQuote, setSelectedDashboardQuote] = useState(null);
  
  // Close quote popup on Escape key
  useEffect(() => {
    if (!selectedDashboardQuote) return;
    const handleEsc = (e) => { if (e.key === 'Escape') setSelectedDashboardQuote(null); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [selectedDashboardQuote]);
  const [quoteFilterTab, setQuoteFilterTab] = useState('all');

  // Keyboard navigation state for search dropdowns
  const [clientSearchHighlightIndex, setClientSearchHighlightIndex] = useState(-1);
  const [addressSearchHighlightIndex, setAddressSearchHighlightIndex] = useState(-1);
  const [productSearchHighlightIndex, setProductSearchHighlightIndex] = useState(-1);

  const [newAppointment, setNewAppointment] = useState({
    client_id: '',
    photographer_id: '',
    service_type: '',
    scheduled_date: '',
    scheduled_time: '',
    property_address: '',
    notes: '',
    appointment_id: '',
    folder_name: 'general',
    description: '',
    business_name: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    client_company: '',
    assigned_team_member: ''
  });
  
  // Availability checking state
  const [availabilityData, setAvailabilityData] = useState({});

  const [mediaUploadForm, setMediaUploadForm] = useState({
    appointment_id: '',
    folder_name: 'general',
    description: '',
    tags: '',
    is_public: false
  });

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  // Load listing from URL when navigating directly to /app/listings/edit/:id
  useEffect(() => {
    const loadListingFromUrl = async () => {
      const listingId = getListingIdFromPath(location.pathname);
      if (listingId && user && !selectedListingForEdit) {
        try {
          // Fetch the appointment/listing data
          const response = await axios.get(`${API}/appointments/${listingId}`);
          const listing = response.data;
          if (listing) {
            setSelectedListingForEdit(listing);
            setPropertyClients(listing.client_ids || []);
            // Also fetch the media for the listing
            await fetchAppointmentMedia(listing.id);
          }
        } catch (error) {
          console.error('Failed to load listing from URL:', error);
          // Navigate back to listings if listing not found
          navigate('/app/listings');
        }
      }
    };
    
    if (activeTab === 'edit-listing' && user) {
      loadListingFromUrl();
    }
  }, [location.pathname, user, activeTab]);

  // Auto-collapse tasks section on listing page if all tasks are complete
  useEffect(() => {
    if (selectedListingForEdit) {
      const listingTasks = safeTasks.filter(t => t.appointment_id === selectedListingForEdit.id);
      const allComplete = listingTasks.length > 0 && listingTasks.every(t => t.status === 'completed');
      setListingTasksExpanded(!allComplete);
    }
  }, [selectedListingForEdit, safeTasks]);

  useEffect(() => {
    if (user && (activeTab === 'settings' || activeTab === 'team' || activeTab === 'products' || activeTab === 'orderform' || activeTab === 'notifications' || activeTab === 'appointments')) {
      fetchSettings();
    }
  }, [activeTab, user]);

  // Fetch notification templates when notifications tab is active
  useEffect(() => {
    if (user && activeTab === 'notifications') {
      const fetchNotificationTemplates = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/notifications/templates`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setNotificationTemplates(response.data);
        } catch (err) {
          console.error('Failed to fetch notification templates:', err);
        }
      };
      fetchNotificationTemplates();
    }
  }, [activeTab, user]);

  // Fetch Google Calendar events when calendar tab is active or on dashboard
  useEffect(() => {
    if (user && (activeTab === 'calendar' || activeTab === 'dashboard')) {
      const loadGoogleEvents = async () => {
        const googleEvents = await fetchGoogleCalendarEvents();
        setCalendarEvents(googleEvents);
        setGoogleCalendarEvents(googleEvents); // Also store for today's appointments
      };
      loadGoogleEvents();
    }
  }, [activeTab, user]);

  // Close appointment menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.appointment-menu') && !event.target.closest('button')) {
        document.querySelectorAll('.appointment-menu').forEach(menu => {
          menu.classList.add('hidden');
        });
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Global Escape key handler for all modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showProposeTimeModal) setShowProposeTimeModal(false);
        else if (showRejectModal) setShowRejectModal(false);
        else if (showPendingDetails) setShowPendingDetails(false);
        else if (showClientForm) setShowClientForm(false);
        else if (showAppointmentForm) setShowAppointmentForm(false);
        else if (showEditAddressModal) setShowEditAddressModal(false);
        else if (showDeleteConfirm) setShowDeleteConfirm(false);
        else if (showAddClientModal) setShowAddClientModal(false);
        else if (showAddPackage) setShowAddPackage(false);
        else if (showAddTaskRule) setShowAddTaskRule(false);
        else if (showTaskForm) setShowTaskForm(false);
        else if (showAppointmentDetails) setShowAppointmentDetails(false);
        else if (showEventDetails) setShowEventDetails(false);
        else if (showMediaUpload) setShowMediaUpload(false);
        else if (showMediaManager) setShowMediaManager(false);
        else if (showAddTeamMember) setShowAddTeamMember(false);
        else if (showAddProduct) setShowAddProduct(false);
        else if (showEditTeamMember) setShowEditTeamMember(false);
        else if (showEditProduct) setShowEditProduct(false);
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showClientForm, showAppointmentForm, showEditAddressModal, showDeleteConfirm, 
      showAddClientModal, showAddPackage, showAddTaskRule, showTaskForm, 
      showAppointmentDetails, showEventDetails, showMediaUpload, showMediaManager,
      showAddTeamMember, showAddProduct, showEditTeamMember, showEditProduct,
      showProposeTimeModal, showRejectModal, showPendingDetails]);

  // Pre-check availability for visible month when photographer/products change
  useEffect(() => {
    const checkMonthAvailability = async () => {
      if (!appointmentForm.photographer_id || selectedProducts.length === 0 || !showAppointmentForm) {
        setLoadingAvailableDates(false);
        return;
      }

      setLoadingAvailableDates(true); // Start loading

      const year = calendarMonth.getFullYear();
      const month = calendarMonth.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const today = new Date();
      const appointmentDuration = calculatedPricing?.total_duration || 120;

      // Handle "Any Available" option
      if (appointmentForm.photographer_id === 'any') {
        // Get all eligible photographers
        const eligiblePhotographers = teamMembers.filter(member => {
          if (selectedProducts.length === 0) return true;
          const assignedProducts = member.assigned_products || [];
          return selectedProducts.every(productId => assignedProducts.includes(productId));
        });

        // Check each date
        const datePromises = [];
        for (let day = 1; day <= daysInMonth; day++) {
          const dateObj = new Date(year, month, day);
          // Format date without timezone conversion
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          
          if (dateObj >= today) {
            // For each date, check all eligible photographers
            const photographerPromises = eligiblePhotographers.map(photographer =>
              axios.get(`${API}/availability/photographer/${photographer.id}?date=${dateStr}&duration=${appointmentDuration}`)
                .then(response => (response.data.time_slots || []).length > 0)
                .catch(() => false)
            );

            datePromises.push(
              Promise.all(photographerPromises).then(results => ({
                date: dateStr,
                hasSlots: results.some(hasSlots => hasSlots) // True if ANY photographer has slots
              }))
            );
          }
        }

        const results = await Promise.all(datePromises);
        const availabilityMap = {};
        results.forEach(result => {
          availabilityMap[result.date] = result.hasSlots;
        });
        setAvailableDates(availabilityMap);
        setLoadingAvailableDates(false); // End loading
      } else {
        // Single photographer - existing logic
        const checkPromises = [];
        for (let day = 1; day <= daysInMonth; day++) {
          const dateObj = new Date(year, month, day);
          // Format date without timezone conversion
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          
          if (dateObj >= today) {
            checkPromises.push(
              axios.get(`${API}/availability/photographer/${appointmentForm.photographer_id}?date=${dateStr}&duration=${appointmentDuration}`)
                .then(response => ({
                  date: dateStr,
                  hasSlots: (response.data.time_slots || []).length > 0
                }))
                .catch(() => ({
                  date: dateStr,
                  hasSlots: false
                }))
            );
          }
        }

        const results = await Promise.all(checkPromises);
        const availabilityMap = {};
        results.forEach(result => {
          availabilityMap[result.date] = result.hasSlots;
        });
        setAvailableDates(availabilityMap);
        setLoadingAvailableDates(false); // End loading
      }
    };

    checkMonthAvailability();
  }, [appointmentForm.photographer_id, selectedProducts, calendarMonth, showAppointmentForm, calculatedPricing, teamMembers]);

  // Auto-fetch quote availability when pricing is calculated and quote builder is open
  useEffect(() => {
    if (showQuoteBuilder && calculatedPricing?.total_duration && selectedProducts.length > 0 && appointmentForm.client_id) {
      // Quote prerequisites met - no auto-fetch needed since calendar view handles it
    }
  }, [showQuoteBuilder, calculatedPricing?.total_duration, appointmentForm.client_id]);

  // Load photographer's own data when opening settings OR on initial login
  useEffect(() => {
    if (user && permissions) {
      const isPhotographer = !hasPermission('settings', 'manage_business');
      if (isPhotographer && !editingTeamMember) {
        // Load photographer data on first login
        loadMyTeamMemberData();
      }
    }
  }, [user, permissions]);

  // Also reload when opening settings
  useEffect(() => {
    if (activeTab === 'settings' && user && permissions) {
      const isPhotographer = !hasPermission('settings', 'manage_business');
      if (isPhotographer) {
        loadMyTeamMemberData();
      }
    }
  }, [activeTab]);

  // Keep sidebar visible on calendar page (removed auto-collapse)
  // Sidebar state is now controlled manually by user

  // Fetch drive times when day view is selected
  useEffect(() => {
    const fetchDriveTimes = async () => {
      if (calendarView === 'day' && activeTab === 'calendar' && user) {
        setLoadingDriveTimes(true);
        try {
          const dateStr = moment(calendarMonth).format('YYYY-MM-DD');
          const response = await axios.get(`${API}/calendar/day-drive-times/${dateStr}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          setDayDriveTimes(response.data.team_drive_times || {});
        } catch (error) {
          console.error('Error fetching drive times:', error);
          setDayDriveTimes({});
        } finally {
          setLoadingDriveTimes(false);
        }
      }
    };
    
    fetchDriveTimes();
  }, [calendarView, calendarMonth, activeTab, user]);

  // Close mobile menu on screen resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch Aryeo token when Import tab is opened
  useEffect(() => {
    if (activeTab === 'settings' && settingsTab === 'import') {
      fetchAryeoToken();
    }
  }, [activeTab, settingsTab]);

  // Command Center keyboard shortcut (Cmd+K / Ctrl+K / /) - Only for users with manage_business permission
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only allow command center for users with manage_business permission
      if (!hasPermission || !hasPermission('settings', 'manage_business')) {
        return;
      }
      
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux) or '/' key
      if (((e.metaKey || e.ctrlKey) && e.key === 'k') || e.key === '/') {
        // Only trigger on '/' if not typing in an input field
        if (e.key === '/' && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
          return;
        }
        e.preventDefault();
        setShowCommandCenter(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasPermission]);

  // Slideshow keyboard navigation
  useEffect(() => {
    if (!slideshowOpen) return;

    const handleKeyDown = (e) => {
      const images = mediaFiles.filter(m => m.mime_type?.startsWith('image/'));
      
      if (e.key === 'Escape') {
        setSlideshowOpen(false);
      } else if (e.key === 'ArrowLeft' && slideshowIndex > 0) {
        setSlideshowIndex(slideshowIndex - 1);
      } else if (e.key === 'ArrowRight' && slideshowIndex < images.length - 1) {
        setSlideshowIndex(slideshowIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slideshowOpen, slideshowIndex, mediaFiles]);

  // Load Dropbox connection status when integrations tab opens
  useEffect(() => {
    if (activeTab === 'settings' && settingsTab === 'integrations') {
      console.log('Loading Dropbox status...');
      axios.get(`${API}/integrations/dropbox/status`)
        .then(response => {
          console.log('Dropbox status:', response.data);
          setDropboxSettings(prev => ({
            ...prev,
            is_connected: response.data.is_connected
          }));
        })
        .catch(error => {
          console.error('Failed to load Dropbox status:', error);
        });
    }
  }, [activeTab, settingsTab]);

  // Check for OAuth callback on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('dropbox_connected') === 'true') {
      setDropboxSettings(prev => ({...prev, is_connected: true}));
      setActiveTab('settings');
      setSettingsTab('integrations');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('calendar_connected') === 'true') {
      alert('Google Calendar connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      // Force a refresh of team members so the UI reflects the new connection state
      if (typeof fetchTeamMembers === 'function') {
        try { fetchTeamMembers(); } catch (_e) { /* best-effort */ }
      }
    }
    const calErr = urlParams.get('calendar_error');
    if (calErr) {
      alert(`Couldn't connect Google Calendar: ${calErr}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearchResults) {
        const searchContainers = document.querySelectorAll('.search-container');
        let clickedInside = false;
        
        searchContainers.forEach(container => {
          if (container.contains(event.target)) {
            clickedInside = true;
          }
        });
        
        if (!clickedInside) {
          setShowSearchResults(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchResults]);

  // Close appointment client search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAppointmentClientSearch || showAddressSuggestions) {
        // Check if click is outside the dropdown containers
        const isOutsideClientSearch = !event.target.closest('.client-search-container');
        const isOutsideAddressSearch = !event.target.closest('.address-search-container');
        
        if (isOutsideClientSearch && showAppointmentClientSearch) {
          setShowAppointmentClientSearch(false);
        }
        if (isOutsideAddressSearch && showAddressSuggestions) {
          setShowAddressSuggestions(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAppointmentClientSearch, showAddressSuggestions]);

  // Search bar activation with "/" key
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if "/" was pressed and no input/textarea is focused
      if (event.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        event.preventDefault(); // Prevent "/" from appearing in the search box
        
        // Clear the search query first
        setSearchQuery('');
        setShowSearchResults(false);
        
        // Focus the appropriate search input based on screen size
        const desktopSearch = document.getElementById('desktop-search');
        const mobileSearch = document.getElementById('mobile-search');
        
        if (window.innerWidth >= 768 && desktopSearch) {
          desktopSearch.focus();
        } else if (mobileSearch) {
          mobileSearch.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Avatar management state
  const [clientAvatarUrls, setClientAvatarUrls] = useState({});
  const [teamMemberAvatarUrls, setTeamMemberAvatarUrls] = useState({});
  const [avatarLoadingStates, setAvatarLoadingStates] = useState({});
  const [teamMemberAvatarFile, setTeamMemberAvatarFile] = useState(null);
  const [teamMemberAvatarPreview, setTeamMemberAvatarPreview] = useState(null);
  const [existingTeamMemberAvatarUrl, setExistingTeamMemberAvatarUrl] = useState(null);

  // Get avatar URL - fetches presigned S3 URL from backend
  const getAvatarUrl = async (avatarId) => {
    if (!avatarId) return null;
    try {
      const response = await axios.get(`${API}/media/${avatarId}?url_only=true`);
      return response.data?.url || null;
    } catch (error) {
      console.log('Could not get avatar URL:', avatarId);
      return null;
    }
  };

  // For backward compatibility
  const fetchAvatarBlob = async (avatarId) => {
    return getAvatarUrl(avatarId);
  };

  // Load avatar URLs for clients - fetches S3 presigned URLs
  useEffect(() => {
    const loadAvatarUrls = async () => {
      if (!clients || clients.length === 0) return;
      
      const newAvatarUrls = {};
      const promises = [];
      
      for (const client of clients) {
        if (client.avatar && !clientAvatarUrls[client.avatar]) {
          promises.push(
            getAvatarUrl(client.avatar).then(url => {
              if (url) {
                newAvatarUrls[client.avatar] = url;
              }
            })
          );
        }
      }
      
      if (promises.length > 0) {
        await Promise.all(promises);
        if (Object.keys(newAvatarUrls).length > 0) {
          setClientAvatarUrls(prev => ({...prev, ...newAvatarUrls}));
        }
      }
    };
    
    loadAvatarUrls();
  }, [clients]);

  // Drag and drop handlers for media reordering
  const handleDragStart = (e, index) => {
    console.log('Drag started:', index);
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Only clear drag over if we're leaving the entire drag area
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    console.log('Drop triggered:', dropIndex);
    
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    console.log('Drag from:', dragIndex, 'to:', dropIndex);
    
    if (dragIndex !== dropIndex && !isNaN(dragIndex) && !isNaN(dropIndex)) {
      // Work with current mediaFiles directly (no filtering here as we already filtered in render)
      const currentFiles = [...mediaFiles];
      
      // Check if indices are valid
      if (dragIndex >= 0 && dragIndex < currentFiles.length && dropIndex >= 0 && dropIndex < currentFiles.length) {
        const draggedFile = currentFiles[dragIndex];
        console.log('Moving file:', draggedFile?.original_filename);
        
        // Only proceed if the dragged file exists
        if (draggedFile) {
          // Remove the dragged item
          currentFiles.splice(dragIndex, 1);
          
          // Insert it at the new position
          currentFiles.splice(dropIndex, 0, draggedFile);
          
          setMediaFiles(currentFiles);
          console.log('Files reordered successfully');
        }
      }
    }
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const saveMediaOrder = async () => {
    try {
      // Get the appointment ID from either selectedAppointment or selectedListingForEdit
      const appointmentId = selectedAppointment?.id || selectedListingForEdit?.id;
      
      if (!appointmentId) {
        alert('No appointment selected.');
        return;
      }

      // Create an array of file IDs in the new order, filtering out invalid files
      const fileOrder = mediaFiles
        .filter(file => file && file.id) // Only include valid files with IDs
        .map((file, index) => ({
          file_id: file.id,
          display_order: index + 1
        }));

      if (fileOrder.length === 0) {
        alert('No valid files to reorder.');
        return;
      }

      await axios.put(`${API}/media/appointment/${appointmentId}/reorder`, {
        file_order: fileOrder
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      showToast('Media order saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving media order:', error);
      showToast('Failed to save media order. Please try again.', 'error');
    }
  };

  // Get today's appointments
  const getTodaysAppointments = () => {
    // Get today's date in the user's timezone
    const userTimezone = accountSettings.timezone || 'UTC';
    const today = moment().tz(userTimezone).format('YYYY-MM-DD');
    
    // Get local appointments for today
    const localAppointments = safeAppointments.filter(appointment => appointment.scheduled_date === today);
    
    // Get Google Calendar events for today
    const googleEventsToday = (googleCalendarEvents || [])
      .filter(event => {
        if (!event.start) return false;
        // Convert event date to user's timezone
        const eventDate = moment(event.start).tz(userTimezone).format('YYYY-MM-DD');
        return eventDate === today;
      })
      .map(event => ({
        id: `google-${event.id}`,
        client_name: event.title || 'Google Calendar Event',
        service_type: 'google_calendar',
        property_address: event.description || 'N/A',
        scheduled_date: moment(event.start).tz(userTimezone).format('YYYY-MM-DD'),
        scheduled_time: moment(event.start).tz(userTimezone).format('HH:mm'),
        duration_minutes: 60,
        status: 'scheduled',
        notes: '',
        photographer_name: event.team_member_name || '',
        isGoogleEvent: true
      }));
    
    // Combine both local and Google events
    return [...localAppointments, ...googleEventsToday];
  };

  // Format date for table display (October 3, 2025)
  const formatTableDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateStr;
    }
  };

  // Format time for table display (2:00pm)
  const formatTableTime = (timeStr) => {
    try {
      const [hours, minutes] = timeStr.split(':');
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes));
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      }).toLowerCase();
    } catch (error) {
      return timeStr;
    }
  };

  // Date formatting helper function
  const formatAppointmentDate = (dateStr, timeStr) => {
    try {
      const date = new Date(dateStr + 'T' + timeStr);
      
      // Format day with ordinal suffix (1st, 2nd, 3rd, etc.)
      const day = date.getDate();
      const suffix = day % 10 === 1 && day !== 11 ? 'st' : 
                   day % 10 === 2 && day !== 12 ? 'nd' : 
                   day % 10 === 3 && day !== 13 ? 'rd' : 'th';
      
      const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const time = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      }).toLowerCase();
      
      return `${weekday}, ${month} ${day}${suffix} ${time} CDT`;
    } catch (error) {
      // Fallback to original format if parsing fails
      return `${dateStr} at ${timeStr}`;
    }
  };

  // Universal search functionality
  // Debounce timer ref for search
  const searchDebounceRef = useRef(null);
  
  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      setSelectedSearchIndex(-1); // Reset selected index when clearing
      return;
    }

    // Define navigation shortcuts
    const navigationPages = [
      { name: 'Dashboard', tab: 'dashboard', icon: '📊', keywords: ['dashboard', 'home', 'overview'] },
      { name: 'Calendar', tab: 'calendar', icon: '📅', keywords: ['calendar', 'schedule'] },
      { name: 'Clients', tab: 'clients', icon: '👥', keywords: ['clients', 'customers', 'contacts'] },
      { name: 'Appointments', tab: 'appointments', icon: '📋', keywords: ['appointments', 'bookings', 'scheduled'] },
      { name: 'Listings', tab: 'media', icon: '🏠', keywords: ['listings', 'properties', 'media'] },
      { name: 'Tasks', tab: 'tasks', icon: '✓', keywords: ['tasks', 'todo', 'checklist'] },
      { name: 'Team', tab: 'team', icon: '👨‍💼', keywords: ['team', 'members', 'staff', 'photographers'] },
      { name: 'Main Products', tab: 'products', icon: '📦', keywords: ['products', 'services', 'pricing', 'main products'] },
      { name: 'Packages', tab: 'products', productsTab: 'packages', icon: '📦', keywords: ['packages', 'bundles', 'product packages'] },
      { name: 'Add-on Products', tab: 'products', productsTab: 'addons', icon: '➕', keywords: ['add-on', 'addon', 'extras', 'upsell', 'add-on products'] },
      { name: 'Order Form', tab: 'orderform', icon: '📋', keywords: ['order form', 'booking link', 'public scheduling', 'client booking'] },
      { name: 'Payroll', tab: 'payroll', icon: '💵', keywords: ['payroll', 'pay', 'pay stub', 'paystub', 'payouts', 'team pay', 'wages', 'compensation', 'salary'] },
      { name: 'Settings', tab: 'settings', icon: '⚙️', keywords: ['settings', 'configuration', 'preferences'] },
      // Settings sub-tabs
      { name: 'Account Settings', tab: 'settings', settingsTab: 'account', icon: '👤', keywords: ['account', 'profile', 'business'] },
      { name: 'Working Hours', tab: 'settings', settingsTab: 'hours', icon: '🕐', keywords: ['hours', 'working', 'availability', 'schedule'] },
      { name: 'Scheduling Settings', tab: 'settings', settingsTab: 'scheduling', icon: '📆', keywords: ['scheduling', 'booking', 'buffer'] },
      { name: 'Task Settings', tab: 'settings', settingsTab: 'tasks', icon: '✅', keywords: ['task settings', 'task templates'] },
      { name: 'Coupons', tab: 'settings', settingsTab: 'coupons', icon: '🎟️', keywords: ['coupons', 'discounts', 'promo', 'codes'] },
      { name: 'Tags', tab: 'settings', settingsTab: 'tags', icon: '🏷️', keywords: ['tags', 'labels', 'categories', 'organize'] },
      { name: 'Integrations', tab: 'settings', settingsTab: 'integrations', icon: '🔗', keywords: ['integrations', 'connect', 'api', 'google', 'calendar'] },
      { name: 'Import Data', tab: 'settings', settingsTab: 'import', icon: '📥', keywords: ['import', 'upload', 'csv', 'data'] }
    ];

    // Define action shortcuts for creating new items
    const actionShortcuts = [
      { name: 'New Client', action: 'newClient', icon: '➕', keywords: ['new client', 'add client', 'create client'] },
      { name: 'New Appointment', action: 'newAppointment', icon: '➕', keywords: ['new appointment', 'add appointment', 'create appointment', 'schedule', 'book'] },
      { name: 'New Task', action: 'newTask', icon: '➕', keywords: ['new task', 'add task', 'create task'] },
      { name: 'New Team Member', action: 'newTeamMember', icon: '➕', keywords: ['new team', 'add team', 'add member', 'invite', 'create team'] },
      { name: 'New Product', action: 'newProduct', icon: '➕', keywords: ['new product', 'add product', 'create product', 'new service', 'add service'] }
    ];

    // Check for navigation shortcuts (instant - no API call)
    const matchingPages = navigationPages.filter(page => 
      page.keywords.some(keyword => keyword.includes(query.toLowerCase()))
    ).map(page => ({
      type: 'navigation',
      id: page.settingsTab ? `${page.tab}-${page.settingsTab}` : (page.productsTab ? `${page.tab}-${page.productsTab}` : page.tab),
      title: page.name,
      subtitle: page.settingsTab ? 'Settings tab' : (page.productsTab ? 'Products tab' : 'Go to page'),
      icon: page.icon,
      data: { tab: page.tab, settingsTab: page.settingsTab, productsTab: page.productsTab }
    }));

    // Check for action shortcuts (instant - no API call)
    const matchingActions = actionShortcuts.filter(action => 
      action.keywords.some(keyword => keyword.includes(query.toLowerCase()))
    ).map(action => ({
      type: 'action',
      id: action.action,
      title: action.name,
      subtitle: 'Quick action',
      icon: action.icon,
      data: { action: action.action }
    }));

    // Show instant results immediately (navigation + actions)
    const instantResults = [...matchingPages, ...matchingActions];
    if (instantResults.length > 0) {
      setSearchResults(instantResults);
      setShowSearchResults(true);
    }

    // Debounce API search for clients/appointments (300ms delay)
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    searchDebounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        // Search clients and appointments
        const [clientsResponse, appointmentsResponse] = await Promise.all([
          axios.get(`${API}/clients`),
          axios.get(`${API}/appointments`)
        ]);

        const allClients = clientsResponse.data;
        const allAppointments = appointmentsResponse.data;

        // Filter clients by name, email, or company (NO avatar fetching during search)
        const matchingClientsRaw = allClients.filter(client => 
          (client.name && client.name.toLowerCase().includes(query.toLowerCase())) ||
          (client.email && client.email.toLowerCase().includes(query.toLowerCase())) ||
          (client.company && client.company.toLowerCase().includes(query.toLowerCase()))
        ).slice(0, 5);
        
        // Build client results with their recent properties as separate clickable items
        const clientResultsWithProperties = [];
        matchingClientsRaw.forEach(client => {
          // Add the client result first
          clientResultsWithProperties.push({
            type: 'client',
            id: client.id,
            title: client.name,
            subtitle: client.email,
            company: client.company,
            avatar_url: client.avatar,
            data: client
          });
          
          // Find the 5 most recent properties linked to this client and add as separate results
          const clientProperties = allAppointments
            .filter(apt => apt.client_id === client.id || (apt.client_ids || []).includes(client.id))
            .sort((a, b) => new Date(b.scheduled_date || b.created_at) - new Date(a.scheduled_date || a.created_at))
            .slice(0, 5);
          
          clientProperties.forEach(apt => {
            clientResultsWithProperties.push({
              type: 'client-property',
              id: `${client.id}-${apt.id}`,
              title: apt.property_address || 'No address',
              subtitle: apt.scheduled_date ? new Date(apt.scheduled_date).toLocaleDateString() : '',
              clientName: client.name,
              icon: '🏠',
              isSubResult: true,
              data: apt
            });
          });
        });

        // Filter appointments by property address (direct property search)
        const matchingProperties = allAppointments
          .filter(appointment => {
            if (appointment.property_address && appointment.property_address.toLowerCase().includes(query.toLowerCase())) {
              return true;
            }
            return false;
          })
          .slice(0, 5)
          .map(appointment => {
            // Get client name for this property
            const client = allClients.find(c => c.id === appointment.client_id);
            const clientName = client?.name || appointment.client_name || 'Unknown Client';
            
            return {
              type: 'property',
              id: appointment.id,
              title: appointment.property_address || 'No address',
              subtitle: clientName,
              date: appointment.scheduled_date,
              icon: '🏠',
              data: appointment
            };
          });

        // Combine all results
        const allResults = [
          ...matchingPages,
          ...matchingActions,
          ...clientResultsWithProperties,
          ...matchingProperties
        ];

        setSearchResults(allResults);
        setShowSearchResults(allResults.length > 0);
        setSelectedSearchIndex(-1);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearchLoading(false);
      }
    }, 300); // 300ms debounce
  };

  const handleCommandCenterSearch = (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      performSearch(query);
    } else {
      setSearchResults([]);
    }
  };

  const handleCommandCenterResultClick = (result) => {
    if (result.type === 'navigation') {
      // Navigate to the selected page
      setActiveTab(result.data.tab);
      // If it's a settings sub-tab, also set the settings tab
      if (result.data.settingsTab) {
        setSettingsTab(result.data.settingsTab);
      }
      // If it's a products sub-tab, also set the products tab
      if (result.data.productsTab) {
        setProductsTab(result.data.productsTab);
      }
    } else if (result.type === 'action') {
      // Handle quick actions
      switch (result.data.action) {
        case 'newClient':
          setClientForm({ name: '', email: '', phone: '', company: '', notes: '', avatar: null });
          setEditingClient(null);
          setExistingAvatarUrl(null);
          setAvatarPreview(null);
          setShowClientForm(true);
          setActiveTab('clients');
          break;
        case 'newAppointment':
          setShowAppointmentForm(true);
          break;
        case 'newTask':
          setTaskForm({ appointment_id: '', title: '', description: '', due_date: '', assigned_to: '', priority: 'medium', status: 'pending' });
          setEditingTask(null);
          setShowTaskForm(true);
          setActiveTab('tasks');
          break;
        case 'newTeamMember':
          setShowAddTeamMember(true);
          setActiveTab('team');
          break;
        case 'newProduct':
          setEditingProduct(null);
          setShowAddProduct(true);
          setActiveTab('products');
          break;
        default:
          break;
      }
    } else if (result.type === 'client') {
      // Navigate to client edit page and populate form
      const client = result.data;
      setClientForm({
        name: client.name,
        email: client.email || '',
        phone: client.phone || '',
        company: client.company || '',
        notes: client.notes || '',
        avatar: null
      });
      setEditingClient(client);
      // Set existing avatar URL if client has one
      if (client.avatar && clientAvatarUrls[client.avatar]) {
        setExistingAvatarUrl(clientAvatarUrls[client.avatar]);
      } else if (client.avatar) {
        // Load avatar blob URL if not already cached
        fetchAvatarBlob(client.avatar).then(blobUrl => {
          if (blobUrl) {
            setExistingAvatarUrl(blobUrl);
            setClientAvatarUrls(prev => ({...prev, [client.avatar]: blobUrl}));
          }
        });
      } else {
        setExistingAvatarUrl(null);
      }
      setAvatarPreview(null);
      setClientFormActiveTab('profile');
      setShowClientForm(true);
      setActiveTab('clients');
    } else if (result.type === 'property' || result.type === 'client-property') {
      // Navigate to Edit Listings page for that property
      const appointment = result.data;
      setSelectedListingForEdit(appointment);
      setPropertyClients(appointment.client_ids || []);
      fetchAppointmentMedia(appointment.id);
      setActiveTab('edit-listing', { listingId: appointment.id });
    }
    
    // Close command center and clear search
    setShowCommandCenter(false);
    setSearchQuery('');
    setShowSearchResults(false);
    setSearchResults([]);
  };

  const handleCommandCenterClose = () => {
    setShowCommandCenter(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleCommandCenterClear = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  // Debounced search
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchQuery]);

  // Auto-scroll selected search result into view
  useEffect(() => {
    if (selectedSearchIndex >= 0 && showSearchResults) {
      const searchResults = document.querySelectorAll('.search-result-item');
      if (searchResults[selectedSearchIndex]) {
        searchResults[selectedSearchIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [selectedSearchIndex]);

  // Search clients with debounce for Add Client Modal
  useEffect(() => {
    if (showAddClientModal && !showCreateNewClient) {
      const delayedSearch = setTimeout(() => {
        searchClients(clientSearchQuery);
      }, 300);

      return () => clearTimeout(delayedSearch);
    }
  }, [clientSearchQuery, showAddClientModal, showCreateNewClient]);

  const handleSearchSelect = (result) => {
    if (result.type === 'navigation') {
      // Navigate to the selected page
      setActiveTab(result.data.tab);
    } else if (result.type === 'client') {
      // Navigate to client edit page and populate form
      const client = result.data;
      setClientForm({
        name: client.name,
        email: client.email || '',
        phone: client.phone || '',
        company: client.company || '',
        notes: client.notes || '',
        avatar: null // Reset avatar for editing
      });
      setEditingClient(client);
      // Set existing avatar URL if client has one
      if (client.avatar && clientAvatarUrls[client.avatar]) {
        setExistingAvatarUrl(clientAvatarUrls[client.avatar]);
      } else if (client.avatar) {
        // Load avatar blob URL if not already cached
        fetchAvatarBlob(client.avatar).then(blobUrl => {
          if (blobUrl) {
            setExistingAvatarUrl(blobUrl);
            setClientAvatarUrls(prev => ({...prev, [client.avatar]: blobUrl}));
          }
        });
      } else {
        setExistingAvatarUrl(null);
      }
      setAvatarPreview(null);
      setClientFormActiveTab('profile'); // Reset to profile tab
      setShowClientForm(true);
      setActiveTab('clients');
    } else if (result.type === 'property') {
      // Navigate to Edit Listings page for that property
      const appointment = result.data;
      setSelectedListingForEdit(appointment);
      setPropertyClients(appointment.client_ids || []);
      fetchAppointmentMedia(appointment.id);
      setActiveTab('edit-listing', { listingId: appointment.id });
    }
    
    // Clear search
    setSearchQuery('');
    setShowSearchResults(false);
    setSelectedSearchIndex(-1);
    setIsMobileMenuOpen(false);
  };

  // Handle keyboard navigation in search results
  const handleSearchKeyDown = (e) => {
    if (!showSearchResults || searchResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSearchIndex(prev => 
        prev < searchResults.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSearchIndex(prev => 
        prev > 0 ? prev - 1 : searchResults.length - 1
      );
    } else if (e.key === 'Enter' && selectedSearchIndex >= 0) {
      e.preventDefault();
      handleSearchSelect(searchResults[selectedSearchIndex]);
    } else if (e.key === 'Escape') {
      setShowSearchResults(false);
      setSelectedSearchIndex(-1);
    }
  };

  // Search clients for Add Client Modal
  const searchClients = async (query) => {
    if (!query || query.trim().length < 1) {
      setClientSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/clients/search`, {
        params: { query: query.trim() }
      });
      setClientSearchResults(response.data);
    } catch (error) {
      console.error('Error searching clients:', error);
      setClientSearchResults([]);
    }
  };

  // Add client to property/appointment
  const addClientToProperty = async (clientId) => {
    if (!selectedListingForEdit || !clientId) return;
    
    try {
      // Get current client_ids or initialize empty array
      const currentClientIds = propertyClients || [];
      
      // Check if client already added
      if (currentClientIds.includes(clientId)) {
        alert('This client is already added to this property');
        return;
      }
      
      if (clientId === selectedListingForEdit.client_id) {
        alert('This client is already the primary client');
        return;
      }
      
      // Add new client to the list
      const updatedClientIds = [...currentClientIds, clientId];
      
      // Update backend
      await axios.patch(`${API}/appointments/${selectedListingForEdit.id}/clients`, {
        client_ids: updatedClientIds
      });
      
      // Update local state
      setPropertyClients(updatedClientIds);
      setShowAddClientModal(false);
      setClientSearchQuery('');
      setClientSearchResults([]);
      setSelectedClientToAdd('');
      
      showToast('Client added successfully!', 'success');
    } catch (error) {
      console.error('Error adding client to property:', error);
      alert('Failed to add client. Please try again.');
    }
  };

  // Create new client from Add Client Modal
  const createNewClient = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData();
      formData.append('name', newClientForm.name);
      formData.append('email', newClientForm.email || '');
      formData.append('phone', newClientForm.phone || '');
      formData.append('company', newClientForm.company || '');
      
      const response = await axios.post(`${API}/clients`, formData);
      
      // Add newly created client to property
      await addClientToProperty(response.data.id);
      
      // Refresh clients list
      const clientsRes = await axios.get(`${API}/clients`);
      setClients(clientsRes.data);
      
      // Reset form
      setNewClientForm({ name: '', email: '', phone: '', company: '' });
      setShowCreateNewClient(false);
      
    } catch (error) {
      console.error('Error creating client:', error);
      alert('Failed to create client. Please try again.');
    }
  };

  // Search clients for Appointment Form
  const searchAppointmentClients = async (query) => {
    if (!query || query.trim().length < 1) {
      setAppointmentClientSearchResults([]);
      setShowAppointmentClientSearch(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/clients/search`, {
        params: { query: query.trim() }
      });
      setAppointmentClientSearchResults(response.data);
      setShowAppointmentClientSearch(true);
    } catch (error) {
      console.error('Error searching clients:', error);
      setAppointmentClientSearchResults([]);
    }
  };

  // Select client in appointment form
  const selectAppointmentClient = (client) => {
    setAppointmentForm({ ...appointmentForm, client_id: client.id });
    setAppointmentClientSearchQuery(client.name);
    setShowAppointmentClientSearch(false);
    setAppointmentClientSearchResults([]);
  };

  // Create new client from Appointment Form
  const createNewClientInAppointment = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData();
      formData.append('name', newClientInAppointmentForm.name);
      formData.append('email', newClientInAppointmentForm.email || '');
      formData.append('phone', newClientInAppointmentForm.phone || '');
      formData.append('company', newClientInAppointmentForm.company || '');
      
      const response = await axios.post(`${API}/clients`, formData);
      
      // Set the newly created client in the appointment form
      setAppointmentForm({ ...appointmentForm, client_id: response.data.id });
      setAppointmentClientSearchQuery(response.data.name);
      
      // Refresh clients list
      const clientsRes = await axios.get(`${API}/clients`);
      setClients(clientsRes.data);
      
      // Reset and hide form
      setNewClientInAppointmentForm({ name: '', email: '', phone: '', company: '' });
      setShowCreateClientInAppointment(false);
      setShowAppointmentClientSearch(false);
      
    } catch (error) {
      console.error('Error creating client:', error);
      alert('Failed to create client. Please try again.');
    }
  };

  // Initialize Google Places Autocomplete Service
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setAddressAutocompleteService(
        new window.google.maps.places.AutocompleteService()
      );
    }
  }, []);

  // Initialize Today's Appointments Map
  useEffect(() => {
    if (todaysAppointmentsView === 'map' && window.google && window.google.maps) {
      const mapContainer = document.getElementById('todays-appointments-map');
      if (!mapContainer) return;

      const todaysAppts = getTodaysAppointments().filter(a => a.property_address && !a.isGoogleEvent);
      if (todaysAppts.length === 0) return;

      // Initialize map with very zoomed out view
      const map = new window.google.maps.Map(mapContainer, {
        zoom: 3,
        center: { lat: 37.7749, lng: -122.4194 }, // Default to San Francisco
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER,
        },
      });

      const geocoder = new window.google.maps.Geocoder();
      const bounds = new window.google.maps.LatLngBounds();
      let geocodedCount = 0;

      // Geocode and add markers for each appointment
      todaysAppts.forEach((appointment, index) => {
        geocoder.geocode({ address: appointment.property_address }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const position = results[0].geometry.location;
            bounds.extend(position);

            // Create custom marker with number
            const marker = new window.google.maps.Marker({
              position: position,
              map: map,
              title: appointment.property_address,
              label: {
                text: String(index + 1),
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold',
              },
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                fillColor: '#2563eb',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
                scale: 15,
              },
            });

            // Create info window
            const infoWindow = new window.google.maps.InfoWindow({
              content: `
                <div style="padding: 10px; max-width: 250px;">
                  <h3 style="font-weight: bold; margin-bottom: 8px; color: #111;">${appointment.client_name}</h3>
                  <p style="margin-bottom: 4px; color: #666; font-size: 13px;"><strong>Time:</strong> ${formatTableTime(appointment.scheduled_time)}</p>
                  <p style="margin-bottom: 4px; color: #666; font-size: 13px;"><strong>Address:</strong> ${appointment.property_address}</p>
                  <p style="margin-bottom: 8px; color: #666; font-size: 13px;"><strong>Photographer:</strong> ${appointment.photographer_name || 'Unassigned'}</p>
                  <button 
                    onclick="window.open('https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(appointment.property_address)}', '_blank')"
                    style="background-color: #2563eb; color: white; padding: 6px 12px; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; width: 100%;"
                  >
                    Get Directions
                  </button>
                </div>
              `,
            });

            marker.addListener('click', () => {
              infoWindow.open(map, marker);
            });
          }
          
          // Track geocoding completion
          geocodedCount++;
          if (geocodedCount === todaysAppts.length) {
            // All markers geocoded, now fit bounds to show all markers
            map.fitBounds(bounds);
            
            // Add some padding around the bounds
            const currentZoom = map.getZoom();
            if (currentZoom > 12) {
              map.setZoom(12); // Max zoom in case all appointments are at same location
            }
          }
        });
      });
    }
  }, [todaysAppointmentsView, appointments]);

  // Auto-calculate pricing when editing appointment with products
  useEffect(() => {
    if (showAppointmentForm && editingAppointment && selectedProducts.length > 0 && appointmentForm.property_sqft && products.length > 0) {
      // Only run if we don't already have calculated pricing
      if (!calculatedPricing) {
        calculateAppointmentPricing(selectedProducts, appointmentForm.property_sqft);
      }
    }
  }, [showAppointmentForm, editingAppointment, products.length, selectedProducts.length, appointmentForm.property_sqft]);

  // Search for address suggestions
  const searchAddressSuggestions = (input) => {
    if (!input || input.trim().length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    if (!addressAutocompleteService) {
      console.error('Google Places Autocomplete service not initialized');
      return;
    }

    addressAutocompleteService.getPlacePredictions(
      {
        input: input,
        types: ['address'],
        componentRestrictions: { country: 'us' }
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setAddressSuggestions(predictions);
          setShowAddressSuggestions(true);
        } else {
          setAddressSuggestions([]);
          setShowAddressSuggestions(false);
        }
      }
    );
  };

  // Select address suggestion
  const selectAddressSuggestion = (suggestion) => {
    setAppointmentForm({ ...appointmentForm, property_address: suggestion.description });
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
  };

  // Load photographer's own team member data for settings
  const loadMyTeamMemberData = async () => {
    try {
      const response = await axios.get(`${API}/me/team-member`);
      setEditingTeamMember(response.data);
      setEditTeamMemberForm({
        name: response.data.name || '',
        email: response.data.email || '',
        role: response.data.role || '',
        phone: response.data.phone || '',
        working_hours: response.data.working_hours || {
          monday: { enabled: true, start: '09:00', end: '17:00' },
          tuesday: { enabled: true, start: '09:00', end: '17:00' },
          wednesday: { enabled: true, start: '09:00', end: '17:00' },
          thursday: { enabled: true, start: '09:00', end: '17:00' },
          friday: { enabled: true, start: '09:00', end: '17:00' },
          saturday: { enabled: false, start: '09:00', end: '17:00' },
          sunday: { enabled: false, start: '09:00', end: '17:00' }
        }
      });
    } catch (error) {
      console.error('Failed to load team member data:', error);
    }
  };

  // Helper to get assignee info - works for both admins and photographers
  const getTaskAssignee = (assignedToId) => {
    // First try to find in teamMembers array (for admins)
    let assignee = (teamMembers ?? []).find(tm => tm.id === assignedToId);
    
    // If not found and user is a photographer viewing their own tasks, use editingTeamMember
    if (!assignee && editingTeamMember && editingTeamMember.id === assignedToId) {
      assignee = editingTeamMember;
    }
    
    return assignee;
  };

  const fetchData = async () => {
    // Fetch all resources in PARALLEL for faster load times
    // Each request handles its own errors independently
    
    const safeRequest = async (url, fallback = [], maxRetries = 3) => {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const res = await axios.get(`${API}${url}`, { timeout: 30000 });
          return { success: true, data: res.data };
        } catch (error) {
          const status = error.response?.status;
          const isRetryable = !status || status >= 500 || error.code === 'ECONNABORTED' || error.message?.includes('timeout');
          
          if (attempt < maxRetries && isRetryable) {
            console.log(`Retry ${attempt}/${maxRetries} for ${url} (${error.message})`);
            await new Promise(r => setTimeout(r, 500 * attempt)); // Exponential backoff
            continue;
          }
          
          console.log(`Failed to fetch ${url} after ${attempt} attempts:`, status || error.message);
          return { success: false, data: fallback };
        }
      }
      return { success: false, data: fallback };
    };

    // Run all independent requests in parallel
    const [
      clientsResult,
      appointmentsResult,
      blocksResult,
      statsResult,
      serviceTypesResult,
      membersResult,
      tasksResult,
      tasksSummaryResult,
      submissionsResult,
      accountResult,
      tagsResult,
      calendarResult,
      businessHoursResult
    ] = await Promise.all([
      safeRequest('/clients', []),
      safeRequest('/appointments', []),
      safeRequest('/schedule-blocks', []),
      safeRequest('/dashboard/stats', {}),
      safeRequest('/service-types', []),
      safeRequest('/settings/team-members', []),
      safeRequest('/tasks', { tasks: [] }),
      safeRequest('/tasks/summary', {}),
      safeRequest('/order-form-submissions', []),
      safeRequest('/settings/account', {}),
      safeRequest('/settings/tags', []),
      (async () => {
        try {
          const start = moment().startOf('month').format('YYYY-MM-DD');
          const end = moment().endOf('month').format('YYYY-MM-DD');
          const res = await axios.get(`${API}/calendar/appointments`, { params: { start_date: start, end_date: end } });
          const events = (res.data?.events || []).map(event => ({
            ...event,
            start: new Date(event.start),
            end: new Date(event.end)
          }));
          return { success: true, data: events };
        } catch (error) {
          console.log('Could not fetch calendar data:', error.response?.status);
          return { success: false, data: [] };
        }
      })(),
      safeRequest('/business-hours', {})
    ]);

    // Apply all results
    setClients(clientsResult.data);
    setAppointments(appointmentsResult.data);
    setScheduleBlocks(blocksResult.data);
    setStats(statsResult.data);
    setServiceTypes(serviceTypesResult.data);
    setTeamMembers(membersResult.data);
    setTasks(tasksResult.data?.tasks || []);
    setTasksSummary(tasksSummaryResult.data);
    setOrderFormSubmissions(submissionsResult.data || []);
    setAccountSettings(accountResult.data);
    setAvailableTags(tagsResult.data || []);
    setCalendarEvents(calendarResult.data);
    if (businessHoursResult.data) {
      setBusinessHours(businessHoursResult.data);
    }

    // Fetch thumbnails after appointments are set (depends on appointments data)
    if (appointmentsResult.success && appointmentsResult.data.length > 0) {
      fetchAppointmentThumbnails(appointmentsResult.data);
    }

    // Fetch quotes for dashboard
    try {
      const quotesRes = await axios.get(`${API}/quotes`);
      setDashboardQuotes(quotesRes.data.quotes || []);
    } catch (e) {
      console.log('Quotes fetch skipped');
    }

    // Fetch settings if on settings tab (conditional, runs after parallel batch)
    if (activeTab === 'settings') {
      try {
        await fetchSettings();
      } catch (error) {
        console.log('Could not fetch settings:', error.response?.status);
      }
    }
    
    // Fetch Google Calendar events if on calendar tab
    if (activeTab === 'calendar') {
      try {
        const googleEvents = await fetchGoogleCalendarEvents();
        setCalendarEvents(googleEvents);
      } catch (error) {
        console.log('Could not fetch Google Calendar events:', error.response?.status);
      }
    }
    
    setLoading(false);
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`);
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchTasksSummary = async () => {
    try {
      const response = await axios.get(`${API}/tasks/summary`);
      setTasksSummary(response.data);
    } catch (error) {
      console.error('Error fetching tasks summary:', error);
    }
  };

  const fetchCalendarData = async (startDate, endDate) => {
    try {
      // Default to current month if no dates provided
      const start = startDate || moment().startOf('month').format('YYYY-MM-DD');
      const end = endDate || moment().endOf('month').format('YYYY-MM-DD');
      
      const response = await axios.get(`${API}/calendar/appointments`, {
        params: { start_date: start, end_date: end }
      });
      
      const events = (response.data?.events || []).map(event => ({
        ...event,
        start: new Date(event.start),
        end: new Date(event.end)
      }));
      
      setCalendarEvents(events);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setCalendarEvents([]);
    }
  };


  const fetchGoogleCalendarEvents = async (startDate, endDate) => {
    try {
      // Default to current month if no dates provided
      const start = startDate || moment().startOf('month').toISOString();
      const end = endDate || moment().endOf('month').toISOString();
      
      const response = await axios.get(`${API}/calendar/events`, {
        params: { start_date: start, end_date: end }
      });
      
      const googleEvents = (response.data?.events || []).map(event => ({
        id: event.id,
        title: event.summary,
        start: event.start?.dateTime ? new Date(event.start.dateTime) : new Date(event.start?.date),
        end: event.end?.dateTime ? new Date(event.end.dateTime) : new Date(event.end?.date),
        description: event.description || '',
        team_member_id: event.team_member_id,
        team_member_name: event.team_member_name,
        source: 'google_calendar',
        allDay: !event.start?.dateTime // If no dateTime, it's an all-day event
      }));
      
      return googleEvents;
    } catch (error) {
      console.error('Error fetching Google Calendar events:', error);
      return [];
    }
  };


  const fetchBusinessHours = async () => {
    try {
      const response = await axios.get(`${API}/business-hours`);
      setBusinessHours(response.data);
    } catch (error) {
      console.error('Error fetching business hours:', error);
      setBusinessHours([]);
    }
  };

  // Task Management Functions
  //
  // ⚠️  DO NOT MODIFY THE WIRING BELOW WITHOUT EXPLICIT USER REQUEST  ⚠️
  // ----------------------------------------------------------------------
  // `handleTaskSubmit` MUST stay exported in the DashboardContext value
  // (search for `handleTaskSubmit, deleteTask` near the bottom of this file).
  // It was previously omitted by accident, which caused `<form onSubmit>` in
  // `TaskModals.js` to receive `undefined` → the Create/Edit Task button did
  // a default-browser form submit instead of POSTing to `/api/tasks`, and
  // every task creation silently failed in the UI. If you change this
  // function's name or remove its handler, also re-check the context value
  // object. Same goes for `deleteTask`, `updateTaskStatus`, and `editTask`.
  // Tested fix: Mar 1, 2026.
  // ----------------------------------------------------------------------
  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, taskForm);
      } else {
        await axios.post(`${API}/tasks`, taskForm);
      }
      setShowTaskForm(false);
      setEditingTask(null);
      setTaskForm({ appointment_id: '', title: '', description: '', due_date: '', assigned_to: '', priority: 'medium', status: 'pending' });
      await fetchTasks();
      await fetchTasksSummary();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Failed to save task');
    }
  };

  const editTask = (task) => {
    setEditingTask(task);
    // Find the associated appointment to get property and client info
    const appointment = appointments.find(apt => apt.id === task.appointment_id);
    const propertySearch = appointment 
      ? `${appointment.property_address} - ${appointment.client_name}`
      : (task.appointment_id === 'general' ? 'General Task' : '');
    
    setTaskForm({
      appointment_id: task.appointment_id,
      title: task.title,
      description: task.description || '',
      due_date: task.due_date,
      assigned_to: task.assigned_to || '',
      priority: task.priority,
      status: task.status || 'pending',
      propertySearch: propertySearch
    });
    setShowTaskForm(true);
  };

  const deleteTask = async (taskId) => {
    showConfirm(
      'Delete Task',
      'Are you sure you want to delete this task?',
      async () => {
        try {
          await axios.delete(`${API}/tasks/${taskId}`);
          await fetchTasks();
          await fetchTasksSummary();
          showToast('Task deleted successfully', 'success');
        } catch (error) {
          console.error('Error deleting task:', error);
          showToast('Failed to delete task', 'error');
        }
      }
    );
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus });
      await fetchTasks();
      await fetchTasksSummary();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const fetchAvailableTimeSlots = async (date, duration = 120) => {
    try {
      const response = await axios.get(`${API}/time-slots`, {
        params: { 
          date: moment(date).format('YYYY-MM-DD'),
          service_duration: duration
        }
      });
      setAvailableTimeSlots(response.data.available_slots || []);
    } catch (error) {
      console.error('Error fetching time slots:', error);
      setAvailableTimeSlots([]);
    }
  };

  const fetchAppointmentMedia = async (appointmentId) => {
    try {
      // Backend now returns thumbnail_url for each image file
      // - S3 files: pre-signed URL (ready to use)
      // - Local files: "__LOCAL_THUMB__:{file_id}" marker (construct URL with auth token)
      const response = await axios.get(`${API}/media/appointment/${appointmentId}?include_thumbnails=true`);
      const files = response.data || [];
      const token = localStorage.getItem('token');
      
      // Filter and map thumbnail URLs
      const validFiles = files
        .filter(file => file && typeof file === 'object' && file.id && file.mime_type)
        .map(file => {
          let thumbnailUrl = null;
          
          if (file.thumbnail_url) {
            if (file.thumbnail_url.startsWith('__LOCAL_THUMB__:')) {
              // Local file: construct URL with auth token for direct img src usage
              const fileId = file.thumbnail_url.replace('__LOCAL_THUMB__:', '');
              thumbnailUrl = `${API}/media/${fileId}/thumbnail?size=400&token=${token}`;
            } else {
              // S3 pre-signed URL: use directly
              thumbnailUrl = file.thumbnail_url;
            }
          }
          
          return { ...file, thumbnailUrl };
        });
      
      setMediaFiles(validFiles);
    } catch (error) {
      console.error('Error fetching media:', error);
      setMediaFiles([]);
    }
  };

  const handleEditAddress = async (e) => {
    e.preventDefault();
    
    if (!editAddressForm.trim()) {
      alert('Please enter a valid address');
      return;
    }

    try {
      await axios.put(`${API}/appointments/${selectedListingForEdit.id}`, {
        ...selectedListingForEdit,
        property_address: editAddressForm
      });
      
      // Update local state
      setSelectedListingForEdit({
        ...selectedListingForEdit,
        property_address: editAddressForm
      });
      
      // Refresh appointments list
      fetchData();
      
      setShowEditAddressModal(false);
      showToast('Address updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating address:', error);
      alert('Failed to update address');
    }
  };


  const handleClientSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData();
      formData.append('name', clientForm.name);
      formData.append('email', clientForm.email || '');
      formData.append('phone', clientForm.phone || '');
      formData.append('company', clientForm.company || '');
      formData.append('notes', clientForm.notes || '');
      
      // Handle avatar
      if (clientForm.avatar) {
        formData.append('avatar', clientForm.avatar);
      }
      if (clientForm.removeAvatar) {
        formData.append('remove_avatar', 'true');
      }
      
      let response;
      if (editingClient) {
        // Use uploadWithRetry for better reliability
        response = await uploadWithRetry(
          `${API}/clients/${editingClient.id}`,
          formData,
          { method: 'PUT', timeout: 120000 }
        );
        
        // Upload company logo separately if provided
        if (clientForm.companyLogo) {
          setUploadingCompanyLogo(true);
          try {
            const logoFormData = new FormData();
            logoFormData.append('file', clientForm.companyLogo);
            await uploadWithRetry(
              `${API}/clients/${editingClient.id}/company-logo`,
              logoFormData,
              { timeout: 120000 }
            );
          } finally {
            setUploadingCompanyLogo(false);
          }
        } else if (clientForm.removeCompanyLogo) {
          await axios.delete(`${API}/clients/${editingClient.id}/company-logo`);
        }
      } else {
        // Use uploadWithRetry for better reliability
        response = await uploadWithRetry(
          `${API}/clients`,
          formData,
          { timeout: 120000 }
        );
        
        // Upload company logo for new client if provided
        if (clientForm.companyLogo && response.data?.id) {
          setUploadingCompanyLogo(true);
          try {
            const logoFormData = new FormData();
            logoFormData.append('file', clientForm.companyLogo);
            await uploadWithRetry(
              `${API}/clients/${response.data.id}/company-logo`,
              logoFormData,
              { timeout: 120000 }
            );
          } finally {
            setUploadingCompanyLogo(false);
          }
        }
      }
      
      // Refresh client list
      const clientsRes = await axios.get(`${API}/clients`);
      setClients(clientsRes.data);
      
      // If avatar was uploaded, immediately fetch the new avatar
      const updatedClient = response.data;
      if (updatedClient?.avatar) {
        const blobUrl = await fetchAvatarBlob(updatedClient.avatar);
        if (blobUrl) {
          setClientAvatarUrls(prev => ({...prev, [updatedClient.avatar]: blobUrl}));
        }
      }
      
      resetClientForm();
      showToast('Client saved successfully!', 'success');
      
    } catch (error) {
      console.error('Error saving client:', error);
      setUploadingCompanyLogo(false);
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to save client';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Upload timed out. Please try again with a smaller image or check your connection.';
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, 'error');
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        alert('Avatar must be smaller than 5MB');
        return;
      }
      
      setClientForm({...clientForm, avatar: file, removeAvatar: false});
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAvatar = () => {
    setClientForm({...clientForm, avatar: null, removeAvatar: true});
    setAvatarPreview(null);
    setExistingAvatarUrl(null);
    
    // Clear file input
    const fileInput = document.getElementById('avatar-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Company Logo handlers
  const handleCompanyLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        alert('Company logo must be smaller than 5MB');
        return;
      }
      
      setClientForm({...clientForm, companyLogo: file, removeCompanyLogo: false});
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setCompanyLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCompanyLogo = () => {
    setClientForm({...clientForm, companyLogo: null, removeCompanyLogo: true});
    setCompanyLogoPreview(null);
    setExistingCompanyLogoUrl(null);
    
    // Clear file input
    const fileInput = document.getElementById('company-logo-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const refreshAvatarForClient = async (clientId, avatarId) => {
    if (!avatarId) {
      // Remove from cache if no avatar
      setClientAvatarUrls(prev => {
        const newUrls = {...prev};
        delete newUrls[clientId];
        return newUrls;
      });
      return;
    }
    
    // Fetch new avatar
    const blobUrl = await fetchAvatarBlob(avatarId);
    if (blobUrl) {
      setClientAvatarUrls(prev => ({...prev, [avatarId]: blobUrl}));
    }
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create service_type from selected products
      const selectedProductNames = selectedProducts.map(productId => 
        products.find(p => p.id === productId)?.name || 'Unknown'
      );
      const service_type = selectedProductNames.length > 0 ? selectedProductNames.join(' + ') : 'consultation';

      // Build product_pricing array with details for each product
      const product_pricing = calculatedPricing?.products?.map(p => ({
        product_id: p.product_id,
        product_name: p.product_name,
        price: p.calculated_price,
        duration: p.calculated_duration
      })) || selectedProducts.map(productId => {
        const product = products.find(p => p.id === productId);
        return product ? {
          product_id: product.id,
          product_name: product.name,
          price: product.price,
          duration: product.duration_minutes || 60
        } : null;
      }).filter(Boolean);

      // Use calculated total duration if available, otherwise use default
      const appointmentData = {
        ...appointmentForm,
        service_type,
        selected_products: selectedProducts,
        product_pricing,
        total_price: calculatedPricing?.total_price || product_pricing.reduce((sum, p) => sum + (p.price || 0), 0),
        total_duration: calculatedPricing?.total_duration || product_pricing.reduce((sum, p) => sum + (p.duration || 60), 0),
        duration_minutes: calculatedPricing?.total_duration || 120,
        property_sqft: appointmentForm.property_sqft ? parseInt(appointmentForm.property_sqft) : null,
        payment_status: appointmentForm.payment_status || 'unpaid',
        payment_amount: appointmentForm.payment_amount || null,
        payment_date: appointmentForm.payment_date || null
      };

      if (editingAppointment) {
        await axios.put(`${API}/appointments/${editingAppointment.id}`, appointmentData);
      } else {
        await axios.post(`${API}/appointments`, appointmentData);
      }
      fetchData();
      resetAppointmentForm();
      showToast('Appointment saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving appointment:', error);
      alert('Failed to save appointment');
    }
  };

  const handleMediaUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    const fileInput = e.target.file;
    
    if (!fileInput.files[0]) {
      alert('Please select a file');
      return;
    }

    formData.append('file', fileInput.files[0]);
    formData.append('appointment_id', mediaUploadForm.appointment_id);
    formData.append('folder_name', mediaUploadForm.folder_name);
    formData.append('description', mediaUploadForm.description);
    formData.append('tags', mediaUploadForm.tags);
    formData.append('is_public', mediaUploadForm.is_public);

    try {
      await axios.post(`${API}/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      showToast('File uploaded successfully!', 'success');
      resetMediaUploadForm();
      if (selectedAppointment) {
        fetchAppointmentMedia(selectedAppointment.id);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Upload failed: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const resetClientForm = () => {
    setClientForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      notes: '',
      avatar: null,
      removeAvatar: false,
      companyLogo: null,
      removeCompanyLogo: false
    });
    setEditingClient(null);
    setAvatarPreview(null);
    setExistingAvatarUrl(null);
    setCompanyLogoPreview(null);
    setExistingCompanyLogoUrl(null);
    setClientFormActiveTab('profile'); // Reset to profile tab
    setShowClientForm(false);
  };

  const resetAppointmentForm = () => {
    setAppointmentForm({
      client_id: '',
      photographer_id: '',
      property_address: '',
      property_sqft: '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 120,
      notes: ''
    });
    // Reset availability data
    setAvailableTimeSlots([]);
    setSelectedPhotographerAvailability(null);
    // Reset pricing data
    setSelectedProducts([]);
    setCalculatedPricing(null);
    setCalculatingPrice(false);
    setProductPricing({});
    setEditingAppointment(null);
    // Reset client search states
    setAppointmentClientSearchQuery('');
    setAppointmentClientSearchResults([]);
    setShowAppointmentClientSearch(false);
    setShowCreateClientInAppointment(false);
    setNewClientInAppointmentForm({ name: '', email: '', phone: '', company: '' });
    // Reset address search states
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    // Reset new appointment form states
    setTimeOverrideEnabled(false);
    setProductSearchQuery('');
    setShowProductDropdown(false);
    setPhotographerDayAppointments([]);
    // Reset keyboard navigation indexes
    setClientSearchHighlightIndex(-1);
    setAddressSearchHighlightIndex(-1);
    setProductSearchHighlightIndex(-1);
    // Reset reschedule mode
    setIsReschedulingAppointment(false);
    // Reset quote builder
    setShowQuoteBuilder(false);
    setQuoteAvailableSlots([]);
    setSelectedQuoteSlots([]);
    setSendingQuote(false);
    setQuoteSent(false);
    setShowAppointmentForm(false);
  };

  // Function to open a NEW appointment form (resets all state first)
  const openNewAppointmentForm = async () => {
    // Reset all form state
    setAppointmentForm({
      client_id: '',
      photographer_id: '',
      property_address: '',
      property_sqft: '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 120,
      notes: ''
    });
    setAvailableTimeSlots([]);
    setSelectedPhotographerAvailability(null);
    setSelectedProducts([]);
    setCalculatedPricing(null);
    setCalculatingPrice(false);
    setProductPricing({});
    setEditingAppointment(null);
    setAppointmentClientSearchQuery('');
    setAppointmentClientSearchResults([]);
    setShowAppointmentClientSearch(false);
    setShowCreateClientInAppointment(false);
    setNewClientInAppointmentForm({ name: '', email: '', phone: '', company: '' });
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setTimeOverrideEnabled(false);
    setProductSearchQuery('');
    setShowProductDropdown(false);
    setPhotographerDayAppointments([]);
    setClientSearchHighlightIndex(-1);
    setAddressSearchHighlightIndex(-1);
    setProductSearchHighlightIndex(-1);
    setIsReschedulingAppointment(false);
    // Reset quote builder
    setShowQuoteBuilder(false);
    setQuoteAvailableSlots([]);
    setSelectedQuoteSlots([]);
    setSendingQuote(false);
    setQuoteSent(false);
    // Now show the form
    setShowAppointmentForm(true);
  };

  // Quote builder functions
  const fetchQuoteAvailability = async () => {
    setLoadingQuoteSlots(true);
    try {
      const duration = calculatedPricing?.total_duration || 120;
      const response = await axios.get(`${API}/quotes/team-availability?duration=${duration}&days=14`);
      setQuoteAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching quote availability:', error);
      showToast('Failed to load availability', 'error');
    } finally {
      setLoadingQuoteSlots(false);
    }
  };

  const toggleQuoteSlot = (slot) => {
    const slotKey = `${slot.date}-${slot.time}-${slot.photographer_id}`;
    const isSelected = selectedQuoteSlots.some(s => `${s.date}-${s.time}-${s.photographer_id}` === slotKey);
    if (isSelected) {
      setSelectedQuoteSlots(selectedQuoteSlots.filter(s => `${s.date}-${s.time}-${s.photographer_id}` !== slotKey));
    } else if (selectedQuoteSlots.length < 3) {
      setSelectedQuoteSlots([...selectedQuoteSlots, slot]);
    } else {
      showToast('You can select up to 3 time slots', 'warning');
    }
  };

  const sendAppointmentQuote = async () => {
    if (selectedQuoteSlots.length === 0) {
      showToast('Please select at least one time slot', 'warning');
      return;
    }

    // Find the client info
    const client = clients.find(c => c.id === appointmentForm.client_id);
    if (!client?.email) {
      showToast('Client must have an email address to send a quote', 'error');
      return;
    }

    setSendingQuote(true);
    try {
      // Build product pricing
      const product_pricing = calculatedPricing?.products?.map(p => ({
        product_id: p.product_id,
        product_name: p.product_name,
        price: p.calculated_price,
        duration: p.calculated_duration
      })) || selectedProducts.map(productId => {
        const product = products.find(p => p.id === productId);
        return product ? { product_id: product.id, product_name: product.name, price: product.price, duration: product.duration_minutes || 60 } : null;
      }).filter(Boolean);

      // Create the quote
      const quoteData = {
        client_id: appointmentForm.client_id,
        client_name: client.name,
        client_email: client.email,
        client_phone: client.phone || '',
        property_address: appointmentForm.property_address,
        property_sqft: appointmentForm.property_sqft,
        selected_products: selectedProducts,
        product_pricing,
        total_price: calculatedPricing?.total_price || product_pricing.reduce((sum, p) => sum + (p.price || 0), 0),
        notes: appointmentForm.notes,
        time_slots: selectedQuoteSlots
      };

      const createRes = await axios.post(`${API}/quotes`, quoteData);
      const quoteId = createRes.data.id;

      // Send the email
      await axios.post(`${API}/quotes/${quoteId}/send-email`);

      setQuoteSent(true);
      showToast('Quote sent to ' + client.email, 'success');
      
      // Refresh dashboard quotes
      try {
        const quotesRes = await axios.get(`${API}/quotes`);
        setDashboardQuotes(quotesRes.data.quotes || []);
      } catch(e) {}
    } catch (error) {
      console.error('Error sending quote:', error);
      const detail = error.response?.data?.detail || 'Failed to send quote email';
      showToast(detail, 'error');
    } finally {
      setSendingQuote(false);
    }
  };

  const handleQuoteReminder = async (quoteId) => {
    setQuoteActionLoading(quoteId);
    try {
      await axios.post(`${API}/quotes/${quoteId}/remind`);
      showToast('Reminder sent', 'success');
      const quotesRes = await axios.get(`${API}/quotes`);
      setDashboardQuotes(quotesRes.data.quotes || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to send reminder', 'error');
    } finally {
      setQuoteActionLoading(null);
    }
  };

  const handleQuoteCancel = async (quoteId) => {
    setQuoteActionLoading(quoteId);
    try {
      await axios.put(`${API}/quotes/${quoteId}/cancel`);
      showToast('Quote cancelled', 'success');
      setSelectedDashboardQuote(null);
      const quotesRes = await axios.get(`${API}/quotes`);
      setDashboardQuotes(quotesRes.data.quotes || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to cancel quote', 'error');
    } finally {
      setQuoteActionLoading(null);
    }
  };

  const handleQuoteDelete = async (quoteId) => {
    setQuoteActionLoading(quoteId);
    try {
      await axios.delete(`${API}/quotes/${quoteId}`);
      showToast('Quote deleted', 'success');
      setSelectedDashboardQuote(null);
      const quotesRes = await axios.get(`${API}/quotes`);
      setDashboardQuotes(quotesRes.data.quotes || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete quote', 'error');
    } finally {
      setQuoteActionLoading(null);
    }
  };

  const resetMediaUploadForm = () => {
    setMediaUploadForm({
      appointment_id: '',
      folder_name: 'general',
      description: '',
      tags: '',
      is_public: false
    });
    setShowMediaUpload(false);
  };

  const editClient = (client) => {
    setClientForm({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      notes: client.notes || '',
      avatar: null,
      removeAvatar: false,
      companyLogo: null,
      removeCompanyLogo: false
    });
    setEditingClient(client);
    setAvatarPreview(null);
    setCompanyLogoPreview(null);
    setClientFormActiveTab('profile'); // Reset to profile tab when editing
    
    // Load existing avatar
    if (client.avatar && clientAvatarUrls[client.avatar]) {
      setExistingAvatarUrl(clientAvatarUrls[client.avatar]);
    } else if (client.avatar) {
      fetchAvatarBlob(client.avatar).then(blobUrl => {
        if (blobUrl) {
          setExistingAvatarUrl(blobUrl);
        }
      });
    } else {
      setExistingAvatarUrl(null);
    }
    
    // Load existing company logo
    if (client.company_logo) {
      // Fetch company logo URL from the backend
      axios.get(`${API}/clients/${client.id}/company-logo?url_only=true`)
        .then(response => {
          if (response.data?.url) {
            setExistingCompanyLogoUrl(response.data.url);
          } else {
            setExistingCompanyLogoUrl(null);
          }
        })
        .catch(() => {
          setExistingCompanyLogoUrl(null);
        });
    } else {
      setExistingCompanyLogoUrl(null);
    }
    
    setShowClientForm(true);
  };

  const editAppointment = (appointment) => {
    setAppointmentForm({
      client_id: appointment.client_id,
      photographer_id: appointment.photographer_id || '',
      service_type: appointment.service_type,
      property_address: appointment.property_address,
      property_sqft: appointment.property_sqft || '',
      scheduled_date: appointment.scheduled_date,
      scheduled_time: appointment.scheduled_time,
      duration_minutes: appointment.duration_minutes,
      notes: appointment.notes || ''
    });
    
    // Set client search query with the client name
    const client = clients.find(c => c.id === appointment.client_id);
    if (client) {
      setAppointmentClientSearchQuery(client.name);
    }
    
    // Load selected products
    if (appointment.selected_products && appointment.selected_products.length > 0) {
      setSelectedProducts(appointment.selected_products);
      
      // Recalculate pricing if we have products and square footage
      if (appointment.property_sqft) {
        calculateAppointmentPricing(appointment.selected_products, appointment.property_sqft);
      }
    } else {
      setSelectedProducts([]);
      setCalculatedPricing(null);
    }
    
    // Fetch photographer availability if photographer and date are set
    if (appointment.photographer_id && appointment.scheduled_date) {
      fetchPhotographerAvailability(appointment.photographer_id, appointment.scheduled_date, appointment.duration_minutes);
    }
    
    setEditingAppointment(appointment);
    setShowAppointmentForm(true);
  };

  const deleteClient = async (clientId) => {
    showConfirm(
      'Delete Client',
      'Are you sure you want to delete this client? This action cannot be undone.',
      async () => {
        try {
          await axios.delete(`${API}/clients/${clientId}`);
          fetchData();
          showToast('Client deleted successfully', 'success');
        } catch (error) {
          console.error('Error deleting client:', error);
          showToast('Failed to delete client', 'error');
        }
      }
    );
  };

  // Called from the inline detailed-warning confirm modal inside ClientsPage —
  // it already shows its own warning UI, so we skip the showConfirm prompt
  // and execute the delete directly.
  const deleteClientDirect = async (clientId) => {
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      fetchData();
      showToast('Client deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting client:', error);
      showToast('Failed to delete client', 'error');
    }
  };

  const deleteAppointment = async (appointmentId) => {
    showConfirm(
      'Delete Appointment',
      'Are you sure you want to delete this appointment? This action cannot be undone.',
      async () => {
        try {
          await axios.delete(`${API}/appointments/${appointmentId}`);
          fetchData();
          showToast('Appointment deleted successfully', 'success');
        } catch (error) {
          console.error('Error deleting appointment:', error);
          showToast('Failed to delete appointment', 'error');
        }
      }
    );
  };

  const deleteMediaFile = async (fileId) => {
    showConfirm(
      'Delete File',
      'Are you sure you want to delete this file? This action cannot be undone.',
      async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API}/media/${fileId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          // Refresh media list - check both possible sources
          if (selectedListingForEdit) {
            await fetchAppointmentMedia(selectedListingForEdit.id);
          } else if (selectedAppointment) {
            await fetchAppointmentMedia(selectedAppointment.id);
          }
          showToast('File deleted successfully', 'success');
        } catch (error) {
          console.error('Error deleting file:', error);
          showToast('Failed to delete file', 'error');
        }
      }
    );
  };

  const fetchAppointmentThumbnails = async (appointmentsData = appointments) => {
    const thumbnails = {};
    
    for (const appointment of appointmentsData) {
      try {
        // Get media files for this appointment
        const response = await axios.get(`${API}/media/appointment/${appointment.id}`);
        const mediaFiles = response.data || [];
        
        // Find the first image file
        const firstImage = mediaFiles.find(file => file && file.mime_type && file.mime_type.startsWith('image/'));
        
        if (firstImage) {
          // Fetch the thumbnail with authentication
          const imageResponse = await axios.get(`${API}/media/${firstImage.id}/thumbnail?size=600`, {
            responseType: 'blob',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          const imageUrl = URL.createObjectURL(imageResponse.data);
          thumbnails[appointment.id] = imageUrl;
        }
      } catch (error) {
        console.error(`Failed to fetch thumbnail for appointment ${appointment.id}:`, error);
      }
    }
    
    setAppointmentThumbnails(thumbnails);
  };

  // Settings functions
  const fetchSettings = async () => {
    try {
      const [accountRes, hoursRes, membersRes, productsRes, rulesRes, packagesRes, addonsRes, orderFormsRes, submissionsRes] = await Promise.all([
        axios.get(`${API}/settings/account`),
        axios.get(`${API}/settings/working-hours`),
        axios.get(`${API}/settings/team-members`),
        axios.get(`${API}/settings/products`),
        axios.get(`${API}/automated-task-rules`),
        axios.get(`${API}/settings/packages`),
        axios.get(`${API}/settings/addon-products`).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/order-forms`).catch(() => ({ data: [] })),
        axios.get(`${API}/order-form-submissions`).catch(() => ({ data: [] }))
      ]);
      
      setAccountSettings(accountRes.data);
      setWorkingHours(hoursRes.data);
      setTeamMembers(membersRes.data);
      setProducts(productsRes.data);
      setAutomatedTaskRules(rulesRes.data.rules || []);
      setPackages(packagesRes.data || []);
      setAddonProducts(addonsRes.data || []);
      setOrderForms(orderFormsRes.data || []);
      setOrderFormSubmissions(submissionsRes.data || []);
      
      // Fetch team member avatars
      fetchTeamMemberAvatars(membersRes.data);
      
      // Return products so they can be used immediately
      return productsRes.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      return [];
    }
  };

  // Fetch team member avatars
  const fetchTeamMemberAvatars = async (members = teamMembers) => {
    const avatarUrls = {};
    const token = localStorage.getItem('token');
    
    for (const member of members) {
      if (member.avatar && !teamMemberAvatarUrls[member.id]) {
        // Use the avatar endpoint directly as image src - backend will proxy the image
        avatarUrls[member.id] = `${API}/settings/team-members/${member.id}/avatar?token=${token}`;
      }
    }
    
    if (Object.keys(avatarUrls).length > 0) {
      setTeamMemberAvatarUrls(prev => ({...prev, ...avatarUrls}));
    }
  };

  const updateAccountSettings = async () => {
    try {
      await axios.put(`${API}/settings/account`, accountSettings);
      showToast('Account settings updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating account settings:', error);
      alert('Failed to update account settings');
    }
  };

  const updateWorkingHours = async () => {
    try {
      await axios.put(`${API}/settings/working-hours`, workingHours);
      alert('Working hours updated successfully!');
    } catch (error) {
      console.error('Error updating working hours:', error);
      alert('Failed to update working hours');
    }
  };

  const addTeamMember = async () => {
    try {
      // Create FormData for invitation (no avatar at this stage)
      const formData = new FormData();
      formData.append('name', newTeamMember.name);
      formData.append('email', newTeamMember.email);
      formData.append('role', newTeamMember.role);
      formData.append('phone', newTeamMember.phone || '');
      formData.append('working_hours', JSON.stringify(newTeamMember.working_hours));
      formData.append('assigned_products', JSON.stringify(newTeamMember.assigned_products));
      formData.append('assigned_packages', JSON.stringify(newTeamMember.assigned_packages || []));
      
      // Send invitation instead of directly adding
      const response = await axios.post(`${API}/team/invite`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Refetch all team members to show pending invitation
      const membersResponse = await axios.get(`${API}/settings/team-members`);
      setTeamMembers(membersResponse.data);
      
      // Fetch avatars
      await fetchTeamMemberAvatars(membersResponse.data);
      
      // Reset form
      setNewTeamMember({
        name: '',
        email: '',
        role: 'Agent',
        phone: '',
        working_hours: {
          monday: { enabled: true, start: '09:00', end: '17:00' },
          tuesday: { enabled: true, start: '09:00', end: '17:00' },
          wednesday: { enabled: true, start: '09:00', end: '17:00' },
          thursday: { enabled: true, start: '09:00', end: '17:00' },
          friday: { enabled: true, start: '09:00', end: '17:00' },
          saturday: { enabled: false, start: '09:00', end: '17:00' },
          sunday: { enabled: false, start: '09:00', end: '17:00' }
        },
        assigned_products: []
      });
      setTeamMemberAvatarFile(null);
      setTeamMemberAvatarPreview(null);
      setShowAddTeamMember(false);
      
      alert(`Invitation sent successfully to ${newTeamMember.email}! They will receive an email to create their account.`);
    } catch (error) {
      console.error('Error sending invitation:', error);
      showToast('Failed to send invitation: ' + (error.response?.data?.detail || error.message), 'error');
    }
  };

  const deleteTeamMember = async (memberId) => {
    showConfirm(
      'Remove Team Member',
      'Are you sure you want to remove this team member?',
      async () => {
        try {
          await axios.delete(`${API}/settings/team-members/${memberId}`);
          setTeamMembers(teamMembers.filter(member => member.id !== memberId));
          showToast('Team member removed successfully!', 'success');
        } catch (error) {
          console.error('Error removing team member:', error);
          showToast('Failed to remove team member', 'error');
        }
      }
    );
  };

  const addProduct = async () => {
    try {
      const response = await axios.post(`${API}/settings/products`, newProduct);
      const productId = response.data.id;
      
      // Upload image if provided
      if (newProduct.image) {
        setUploadingProductImage(true);
        const formData = new FormData();
        formData.append('file', newProduct.image);
        try {
          await axios.post(`${API}/settings/products/${productId}/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } finally {
          setUploadingProductImage(false);
        }
      }
      
      // Refresh products list to get updated image data
      const productsResponse = await axios.get(`${API}/settings/products`);
      setProducts(productsResponse.data);
      
      setNewProduct({ name: '', description: '', price: '', pricing_tiers: [], duration_minutes: '', category: 'Photography', active: true, image: null, image_id: null });
      setShowPricingTiers(false);
      setNewPricingTier({ min_sqft: '', max_sqft: '', price: '', duration_minutes: '' });
      setShowAddProduct(false);
      showToast('Product added successfully!', 'success');
    } catch (error) {
      console.error('Error adding product:', error);
      setUploadingProductImage(false);
      showToast('Failed to add product', 'error');
    }
  };

  const deleteProduct = async (productId) => {
    showConfirm(
      'Delete Product',
      'Are you sure you want to delete this product?',
      async () => {
        try {
          await axios.delete(`${API}/settings/products/${productId}`);
          setProducts(products.filter(product => product.id !== productId));
          showToast('Product deleted successfully!', 'success');
        } catch (error) {
          console.error('Error deleting product:', error);
          showToast('Failed to delete product', 'error');
        }
      }
    );
  };

  const deleteAddon = async (addonId) => {
    const addon = addonProducts.find(a => a.id === addonId);
    showConfirm(
      'Delete Add-on Product',
      `Are you sure you want to delete "${addon?.name || 'this add-on'}"? This action cannot be undone.`,
      async () => {
        try {
          await axios.delete(`${API}/settings/addon-products/${addonId}`);
          setAddonProducts(addonProducts.filter(a => a.id !== addonId));
          showToast('Add-on product deleted successfully!', 'success');
        } catch (error) {
          console.error('Error deleting add-on:', error);
          showToast('Failed to delete add-on product', 'error');
        }
      }
    );
  };

  const deletePackage = async (packageId) => {
    const pkg = packages.find(p => p.id === packageId);
    showConfirm(
      'Delete Package',
      `Are you sure you want to delete "${pkg?.name || 'this package'}"? This action cannot be undone.`,
      async () => {
        try {
          await axios.delete(`${API}/settings/packages/${packageId}`);
          setPackages(packages.filter(p => p.id !== packageId));
          showToast('Package deleted successfully!', 'success');
        } catch (error) {
          console.error('Error deleting package:', error);
          showToast('Failed to delete package', 'error');
        }
      }
    );
  };

  // Edit Team Member Functions
  const openEditTeamMember = (member) => {
    setEditingTeamMember(member);
    setEditTeamMemberForm({
      name: member.name || '',
      email: member.email || '',
      role: member.role || 'Agent',
      phone: member.phone || '',
      avatar: member.avatar || null,
      working_hours: member.working_hours || {
        monday: { enabled: true, start: '09:00', end: '17:00' },
        tuesday: { enabled: true, start: '09:00', end: '17:00' },
        wednesday: { enabled: true, start: '09:00', end: '17:00' },
        thursday: { enabled: true, start: '09:00', end: '17:00' },
        friday: { enabled: true, start: '09:00', end: '17:00' },
        saturday: { enabled: false, start: '09:00', end: '17:00' },
        sunday: { enabled: false, start: '09:00', end: '17:00' }
      },
      assigned_products: member.assigned_products || [],
      assigned_packages: member.assigned_packages || [],
      permissions: member.permissions || null,
      home_base_address: member.home_base_address || '',
      territory_radius_miles: member.territory_radius_miles || 50,
      mileage_tiers: member.mileage_tiers || []
    });
    
    // Load existing avatar if available
    if (member.avatar) {
      const token = localStorage.getItem('token');
      if (teamMemberAvatarUrls[member.id]) {
        setExistingTeamMemberAvatarUrl(teamMemberAvatarUrls[member.id]);
      } else {
        // Use the avatar endpoint directly as image src
        const avatarUrl = `${API}/settings/team-members/${member.id}/avatar?token=${token}`;
        setExistingTeamMemberAvatarUrl(avatarUrl);
        setTeamMemberAvatarUrls(prev => ({...prev, [member.id]: avatarUrl}));
      }
    } else {
      setExistingTeamMemberAvatarUrl(null);
    }
    
    // Reset new avatar states
    setTeamMemberAvatarFile(null);
    setTeamMemberAvatarPreview(null);
    
    // Reset to first tab
    setEditTeamMemberTab('general');
    
    // Navigate to edit team member page instead of opening modal
    setEditingTeamMemberId(member.id);
    setActiveTab('edit-team-member');
  };

  const updateTeamMember = async () => {
    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('name', editTeamMemberForm.name);
      formData.append('email', editTeamMemberForm.email);
      formData.append('role', editTeamMemberForm.role);
      formData.append('phone', editTeamMemberForm.phone || '');
      formData.append('working_hours', JSON.stringify(editTeamMemberForm.working_hours));
      formData.append('assigned_products', JSON.stringify(editTeamMemberForm.assigned_products));
      formData.append('assigned_packages', JSON.stringify(editTeamMemberForm.assigned_packages || []));
      formData.append('permissions', JSON.stringify(editTeamMemberForm.permissions || null));
      formData.append('home_base_address', editTeamMemberForm.home_base_address || '');
      formData.append('territory_radius_miles', editTeamMemberForm.territory_radius_miles || 50);
      formData.append('mileage_tiers', JSON.stringify(editTeamMemberForm.mileage_tiers || []));
      
      // Add avatar if new one was selected
      if (teamMemberAvatarFile) {
        formData.append('avatar', teamMemberAvatarFile);
      }
      
      console.log('Updating team member:', editingTeamMember.id);
      console.log('Form data:', {
        name: editTeamMemberForm.name,
        email: editTeamMemberForm.email,
        role: editTeamMemberForm.role,
        phone: editTeamMemberForm.phone,
        working_hours: editTeamMemberForm.working_hours,
        assigned_products: editTeamMemberForm.assigned_products,
        has_avatar: !!teamMemberAvatarFile
      });
      
      // Don't manually set Content-Type for FormData - let axios handle it
      const response = await axios.put(`${API}/settings/team-members/${editingTeamMember.id}`, formData);
      
      console.log('Update response:', response.data);
      
      // Refetch all team members to ensure we have the latest data
      const membersResponse = await axios.get(`${API}/settings/team-members`);
      setTeamMembers(membersResponse.data);
      
      // Fetch avatars for all members including the updated one
      await fetchTeamMemberAvatars(membersResponse.data);
      
      // Reset form and states
      setShowEditTeamMember(false);
      setEditingTeamMember(null);
      setEditingTeamMemberId(null);
      setTeamMemberAvatarFile(null);
      setTeamMemberAvatarPreview(null);
      setExistingTeamMemberAvatarUrl(null);
      
      // Navigate back to team list
      setActiveTab('team');
      
      showToast('Team member updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating team member:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      // Better error message handling
      let errorMessage = 'Failed to update team member: ';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage += error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage += error.response.data.detail;
          } else {
            errorMessage += JSON.stringify(error.response.data.detail);
          }
        } else {
          errorMessage += JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      alert(errorMessage);
    }
  };

  // Edit Product Functions  
  const openEditProduct = (product) => {
    console.log('Opening edit for product:', JSON.stringify(product, null, 2));
    setEditingProduct(product);
    const formData = {
      name: product.name,
      description: product.description || '',
      price: product.price,
      pricing_tiers: product.pricing_tiers || [],
      duration_minutes: product.duration_minutes,
      category: product.category,
      active: product.is_active !== false,
      image: null,
      image_id: product.image_id || null
    };
    console.log('Setting editProductForm to:', JSON.stringify(formData, null, 2));
    setEditProductForm(formData);
    setEditShowPricingTiers(product.pricing_tiers && product.pricing_tiers.length > 0);
    setShowEditProduct(true);
  };

  const updateProduct = async () => {
    try {
      // Exclude the image field from the product data (sent separately)
      const { image, image_id, ...productData } = editProductForm;
      console.log('Updating product with data:', JSON.stringify(productData, null, 2));
      const response = await axios.put(`${API}/settings/products/${editingProduct.id}`, productData);
      
      // Upload image if new one is provided
      if (image) {
        console.log('Uploading new product image...');
        setUploadingProductImage(true);
        const formData = new FormData();
        formData.append('file', image);
        try {
          await axios.post(`${API}/settings/products/${editingProduct.id}/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          console.log('Image uploaded successfully');
        } finally {
          setUploadingProductImage(false);
        }
      }
      
      // Refresh products list to get updated image data
      const productsResponse = await axios.get(`${API}/settings/products`);
      setProducts(productsResponse.data);
      
      setShowEditProduct(false);
      setEditingProduct(null);
      setEditShowPricingTiers(false);
      setEditPricingTier({ min_sqft: '', max_sqft: '', price: '', duration_minutes: '' });
      showToast('Product updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating product:', error);
      setUploadingProductImage(false);
      showToast('Failed to update product', 'error');
    }
  };

  // Edit Product Pricing Tier Functions
  const addEditPricingTier = () => {
    const tier = {
      min_sqft: parseFloat(editPricingTier.min_sqft) || 0,
      max_sqft: parseFloat(editPricingTier.max_sqft) || 0,
      price: parseFloat(editPricingTier.price) || 0,
      duration_minutes: parseInt(editPricingTier.duration_minutes) || 0
    };

    if (tier.min_sqft >= tier.max_sqft) {
      alert('Minimum square footage must be less than maximum square footage');
      return;
    }

    if (!tier.duration_minutes) {
      alert('Duration is required for each pricing tier');
      return;
    }

    const updatedTiers = [...editProductForm.pricing_tiers, tier].sort((a, b) => a.min_sqft - b.min_sqft);
    setEditProductForm({...editProductForm, pricing_tiers: updatedTiers});
    setEditPricingTier({ min_sqft: '', max_sqft: '', price: '', duration_minutes: '' });
  };

  const removeEditPricingTier = (index) => {
    const updatedTiers = editProductForm.pricing_tiers.filter((_, i) => i !== index);
    setEditProductForm({...editProductForm, pricing_tiers: updatedTiers});
  };

  // Google Calendar Integration Functions
  const connectTeamMemberCalendar = async () => {
    if (!selectedMemberForCalendar) return;

    try {
      // Initiate OAuth2 flow
      const response = await axios.get(`${API}/calendar/oauth/authorize/${selectedMemberForCalendar.id}`);
      
      // Redirect to Google OAuth2 authorization URL
      window.open(response.data.authorization_url, '_blank', 'width=600,height=600');
      
      // Set up polling to check if authorization completed
      const checkAuthStatus = setInterval(async () => {
        try {
          // Check if team member's calendar is now connected
          const memberResponse = await axios.get(`${API}/settings/team-members`);
          const updatedMember = memberResponse.data.find(m => m.id === selectedMemberForCalendar.id);
          
          if (updatedMember && updatedMember.calendar_integration && updatedMember.calendar_integration.is_active) {
            clearInterval(checkAuthStatus);
            
            // Update team members state
            setTeamMembers(teamMembers.map(member => 
              member.id === selectedMemberForCalendar.id 
                ? { ...member, calendar_integration: updatedMember.calendar_integration }
                : member
            ));
            
            // Update editing member if it's the same one
            if (editingTeamMember && editingTeamMember.id === selectedMemberForCalendar.id) {
              setEditingTeamMember({
                ...editingTeamMember,
                calendar_integration: updatedMember.calendar_integration
              });
            }
            
            alert('Google Calendar connected successfully!');
            setShowCalendarModal(false);
            
            // Reopen Edit Team Member modal if we were editing
            if (editingTeamMember) {
              setShowEditTeamMember(true);
            }
          }
        } catch (error) {
          console.error('Error checking auth status:', error);
        }
      }, 2000); // Check every 2 seconds
      
      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(checkAuthStatus);
      }, 300000);
      
    } catch (error) {
      console.error('Error initiating Google Calendar connection:', error);
      alert('Failed to initiate Google Calendar connection');
    }
  };

  const disconnectTeamMemberCalendar = async (memberId) => {
    if (!window.confirm('Are you sure you want to disconnect this calendar?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/calendar/disconnect/${memberId}`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      // Update team members list — clear ALL connection-state fields so the UI
      // is consistent regardless of which legacy fields the DB doc had.
      setTeamMembers(teamMembers.map(member =>
        member.id === memberId
          ? {
              ...member,
              calendar_connected: false,
              calendar_tokens: null,
              calendar_integration: { is_active: false, status: 'disconnected' },
            }
          : member
      ));

      const msg = response?.data?.already_disconnected
        ? 'Calendar was already disconnected.'
        : 'Google Calendar disconnected successfully!';
      alert(msg);
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      const serverDetail = error?.response?.data?.detail;
      const statusLine = error?.response?.status ? ` (HTTP ${error.response.status})` : '';
      alert(`Failed to disconnect calendar${statusLine}${serverDetail ? `: ${serverDetail}` : ''}`);
    }
  };

  // Calculate pricing for all products (for display in product list)
  const calculateAllProductPricing = async (squareFootage) => {
    if (!squareFootage || squareFootage <= 0 || products.length === 0) {
      setProductPricing({});
      return;
    }

    try {
      // Calculate pricing for ALL products to show updated prices in the list
      const pricingPromises = products.map(product =>
        axios.get(`${API}/settings/products/${product.id}/calculate-price?square_footage=${squareFootage}`)
          .then(response => ({ productId: product.id, ...response.data }))
          .catch(error => ({ productId: product.id, error: error.message }))
      );
      
      const pricingResults = await Promise.all(pricingPromises);
      
      // Convert to object for easy lookup
      const pricingMap = {};
      pricingResults.forEach(result => {
        if (!result.error) {
          pricingMap[result.productId] = result;
        }
      });
      
      setProductPricing(pricingMap);
    } catch (error) {
      console.error('Error calculating all product pricing:', error);
      setProductPricing({});
    }
  };

  // Product pricing calculation for selected products (for appointment total)
  const calculateAppointmentPricing = async (productIds, squareFootage, productsArray = null) => {
    if (!productIds || productIds.length === 0 || !squareFootage || squareFootage <= 0) {
      setCalculatedPricing(null);
      return;
    }

    setCalculatingPrice(true);
    try {
      // Use provided products array or fall back to state
      const productsToUse = productsArray || products;

      // Calculate pricing for each selected product.
      // We use Promise.allSettled (not Promise.all) so a single 404 from the
      // products endpoint — e.g. when the user picked an add-on or package
      // whose ID isn't a product — doesn't wipe out pricing for all the real
      // products in the selection.
      const settled = await Promise.allSettled(
        productIds.map(productId =>
          axios.get(`${API}/settings/products/${productId}/calculate-price?square_footage=${squareFootage}`)
        )
      );
      const pricingResults = settled.map((s, i) => {
        if (s.status === 'fulfilled') {
          return { ok: true, id: productIds[i], data: s.value.data };
        }
        return { ok: false, id: productIds[i], data: null };
      });

      const successfulResults = pricingResults.filter(r => r.ok);

      // Combine pricing and duration from all products
      const combinedPricing = {
        total_price: successfulResults.reduce((sum, r) => sum + (r.data.calculated_price || 0), 0),
        total_duration: successfulResults.reduce((sum, r) => sum + (r.data.calculated_duration || 120), 0),
        base_total_price: successfulResults.reduce((sum, r) => sum + (r.data.base_price || 0), 0),
        base_total_duration: successfulResults.reduce((sum, r) => sum + (r.data.base_duration || 120), 0),
        products: successfulResults.map((r) => ({
          product_id: r.id,
          product_name: productsToUse.find(p => p.id === r.id)?.name || 'Unknown',
          ...r.data
        })),
        property_sqft: squareFootage
      };
      
      setCalculatedPricing(combinedPricing);
      
      // Update availability with new total duration if photographer and date are selected
      if (appointmentForm.photographer_id && appointmentForm.scheduled_date) {
        fetchPhotographerAvailability(appointmentForm.photographer_id, appointmentForm.scheduled_date, combinedPricing.total_duration);
      }
    } catch (error) {
      console.error('Error calculating pricing:', error);
      setCalculatedPricing(null);
    } finally {
      setCalculatingPrice(false);
    }
  };

  // Function to sync appointment to Google Calendar
  const syncAppointmentToCalendar = async (appointmentId) => {
    try {
      const response = await axios.post(`${API}/calendar/sync-appointment`, {
        appointment_id: appointmentId
      });
      
      if (response.data.success) {
        alert('Appointment synced to Google Calendar successfully!');
        // Optionally update appointment state to show it's synced
        setAppointments(appointments.map(appt => 
          appt.id === appointmentId 
            ? { ...appt, synced_to_calendar: true, calendar_event_id: response.data.calendar_event_id }
            : appt
        ));
      }
    } catch (error) {
      console.error('Error syncing appointment to calendar:', error);
      if (error.response?.status === 400) {
        alert('Please connect the photographer\'s Google Calendar first');
      } else {
        alert('Failed to sync appointment to Google Calendar');
      }
    }
  };

  const checkTeamMemberAvailability = async (memberId, date) => {
    try {
      const response = await axios.get(`${API}/calendar/team-member/${memberId}/availability?date=${date}`);
      return response.data;
    } catch (error) {
      console.error('Error checking availability:', error);
      return { available_slots: [], is_working_day: false };
    }
  };

  const handleAvailabilityCheck = async (memberId, date) => {
    if (!memberId || !date) return;
    
    setCheckingAvailability(true);
    try {
      const availability = await checkTeamMemberAvailability(memberId, date);
      setAvailabilityData({
        ...availabilityData,
        [memberId]: availability
      });
    } catch (error) {
      console.error('Error checking availability:', error);
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Pricing tier management functions
  const addPricingTier = () => {
    const tier = {
      min_sqft: parseFloat(newPricingTier.min_sqft) || 0,
      max_sqft: parseFloat(newPricingTier.max_sqft) || 0,
      price: parseFloat(newPricingTier.price) || 0,
      duration_minutes: parseInt(newPricingTier.duration_minutes) || 0
    };

    if (tier.min_sqft >= tier.max_sqft) {
      alert('Minimum square footage must be less than maximum square footage');
      return;
    }

    if (!tier.duration_minutes) {
      alert('Duration is required for each pricing tier');
      return;
    }

    const updatedTiers = [...newProduct.pricing_tiers, tier].sort((a, b) => a.min_sqft - b.min_sqft);
    setNewProduct({...newProduct, pricing_tiers: updatedTiers});
    setNewPricingTier({ min_sqft: '', max_sqft: '', price: '', duration_minutes: '' });
  };

  const removePricingTier = (index) => {
    const updatedTiers = newProduct.pricing_tiers.filter((_, i) => i !== index);
    setNewProduct({...newProduct, pricing_tiers: updatedTiers});
  };

  const calculateProductPrice = async (productId, squareFootage) => {
    try {
      const response = await axios.get(`${API}/settings/products/${productId}/calculate-price?square_footage=${squareFootage}`);
      return response.data;
    } catch (error) {
      console.error('Error calculating price:', error);
      return null;
    }
  };

  // Photographer availability functions
  const fetchPhotographerAvailability = async (photographerId, date, duration = null) => {
    if (!photographerId || !date) {
      setAvailableTimeSlots([]);
      setSelectedPhotographerAvailability(null);
      return;
    }

    setCheckingAvailability(true);
    try {
      const appointmentDuration = duration || calculatedPricing?.calculated_duration || 120;
      
      // Handle "Any Available" option
      if (photographerId === 'any') {
        // Get all photographers who can service the selected products
        const eligiblePhotographers = teamMembers.filter(member => {
          if (selectedProducts.length === 0) return true;
          const assignedProducts = member.assigned_products || [];
          return selectedProducts.every(productId => assignedProducts.includes(productId));
        });
        
        // Fetch availability for all eligible photographers
        const availabilityPromises = eligiblePhotographers.map(photographer =>
          axios.get(`${API}/availability/photographer/${photographer.id}?date=${date}&duration=${appointmentDuration}`)
            .then(response => ({
              photographer: photographer,
              slots: response.data.time_slots || []
            }))
            .catch(() => ({ photographer: photographer, slots: [] }))
        );
        
        const results = await Promise.all(availabilityPromises);
        
        // Combine all available slots and remove duplicates
        const allSlots = new Set();
        results.forEach(result => {
          result.slots.forEach(slot => allSlots.add(slot));
        });
        
        const sortedSlots = Array.from(allSlots).sort();
        setAvailableTimeSlots(sortedSlots);
        setSelectedPhotographerAvailability({
          available: sortedSlots.length > 0,
          time_slots: sortedSlots,
          any_photographer: true
        });
        
        setAvailableDates(prev => ({
          ...prev,
          [date]: sortedSlots.length > 0
        }));
      } else {
        // Single photographer
        const response = await axios.get(`${API}/availability/photographer/${photographerId}?date=${date}&duration=${appointmentDuration}`);
        setSelectedPhotographerAvailability(response.data);
        setAvailableTimeSlots(response.data.time_slots || []);
        
        setAvailableDates(prev => ({
          ...prev,
          [date]: (response.data.time_slots || []).length > 0
        }));
      }
    } catch (error) {
      console.error('Error fetching photographer availability:', error);
      setAvailableTimeSlots([]);
      setSelectedPhotographerAvailability(null);
      setAvailableDates(prev => ({
        ...prev,
        [date]: false
      }));
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Fetch availability for a month (for calendar view) - optimized for speed
  const fetchMonthAvailability = async (photographerId, month) => {
    if (!photographerId) return;
    
    setCheckingAvailability(true);
    
    const year = month.getFullYear();
    const monthNum = month.getMonth();
    const daysInMonth = new Date(year, monthNum + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const appointmentDuration = calculatedPricing?.total_duration || 120;
    const newAvailableDates = {};
    
    // Create array of dates to check (only future dates, limit to next 21 days for speed)
    const datesToCheck = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, monthNum, day);
      if (date >= today && datesToCheck.length < 21) {
        const dateStr = date.toISOString().split('T')[0];
        datesToCheck.push(dateStr);
      }
    }
    
    try {
      // Batch check availability with larger batches for speed
      const batchSize = 7;
      for (let i = 0; i < datesToCheck.length; i += batchSize) {
        const batch = datesToCheck.slice(i, i + batchSize);
        
        const results = await Promise.all(
          batch.map(async (dateStr) => {
            try {
              if (photographerId === 'any') {
                // Check if any photographer is available - check only first 2 for speed
                const eligiblePhotographers = teamMembers.filter(member => {
                  if (selectedProducts.length === 0) return true;
                  const assignedProducts = member.assigned_products || [];
                  return selectedProducts.every(productId => assignedProducts.includes(productId));
                }).slice(0, 2);
                
                const availabilityPromises = eligiblePhotographers.map(photographer =>
                  axios.get(`${API}/availability/photographer/${photographer.id}?date=${dateStr}&duration=${appointmentDuration}`, { timeout: 5000 })
                    .then(response => (response.data.time_slots || []).length > 0)
                    .catch(() => false)
                );
                
                const hasAvailability = (await Promise.all(availabilityPromises)).some(available => available);
                return { date: dateStr, available: hasAvailability };
              } else {
                const response = await axios.get(`${API}/availability/photographer/${photographerId}?date=${dateStr}&duration=${appointmentDuration}`, { timeout: 5000 });
                return { date: dateStr, available: (response.data.time_slots || []).length > 0 };
              }
            } catch {
              return { date: dateStr, available: true }; // Assume available if check fails
            }
          })
        );
        
        // Update state incrementally for smoother UX
        results.forEach(({ date, available }) => {
          newAvailableDates[date] = available;
        });
        setAvailableDates(prev => ({ ...prev, ...newAvailableDates }));
      }
    } catch (error) {
      console.error('Error fetching month availability:', error);
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Fetch photographer's appointments for a specific day (for the preview panel)
  const fetchPhotographerDayAppointments = async (photographerId, date) => {
    if (!photographerId || !date || photographerId === 'any') {
      setPhotographerDayAppointments([]);
      return;
    }

    setLoadingDayAppointments(true);
    try {
      // Get all appointments for this date
      const response = await axios.get(`${API}/appointments`);
      const allAppointments = response.data || [];
      
      // Filter appointments for this photographer and date
      const dayAppointments = allAppointments.filter(apt => {
        const aptDate = apt.scheduled_date?.split('T')[0];
        return apt.photographer_id === photographerId && aptDate === date && apt.status !== 'cancelled';
      });

      // Also fetch Google Calendar events for this photographer
      let googleCalendarEvents = [];
      try {
        const calendarResponse = await axios.get(`${API}/calendar/events`, {
          params: {
            team_member_id: photographerId,
            start_date: date,
            end_date: date
          }
        });
        
        if (calendarResponse.data?.events) {
          googleCalendarEvents = calendarResponse.data.events
            .filter(event => event.team_member_id === photographerId) // Filter for this photographer
            .map(event => {
              // Parse Google Calendar event times
              let startTime = '';
              let endTime = '';
              let durationMins = 60;
              
              const eventStart = event.start?.dateTime || event.start?.date || event.start;
              const eventEnd = event.end?.dateTime || event.end?.date || event.end;
              
              if (eventStart && eventStart.includes('T')) {
                startTime = eventStart.split('T')[1]?.substring(0, 5) || '';
              }
              if (eventEnd && eventEnd.includes('T')) {
                endTime = eventEnd.split('T')[1]?.substring(0, 5) || '';
              }
              
              // Calculate duration if we have both times
              if (startTime && endTime) {
                const [startH, startM] = startTime.split(':').map(Number);
                const [endH, endM] = endTime.split(':').map(Number);
                durationMins = (endH * 60 + endM) - (startH * 60 + startM);
              }
              
              return {
                id: event.id,
                title: event.summary || event.title || 'Google Calendar Event',
                scheduled_time: startTime,
                end_time: endTime,
                duration_minutes: durationMins > 0 ? durationMins : 60,
                is_google_calendar: true,
                status: 'google_calendar',
                property_address: event.location || '',
                client_name: event.description?.substring(0, 50) || 'Personal Event'
              };
            })
            .filter(event => event.scheduled_time); // Only include events with valid times
        }
      } catch (calErr) {
        console.log('Could not fetch Google Calendar events:', calErr);
      }

      // Combine appointments and calendar events
      const combinedEvents = [...dayAppointments, ...googleCalendarEvents];

      // Sort by time
      combinedEvents.sort((a, b) => {
        const timeA = a.scheduled_time || '00:00';
        const timeB = b.scheduled_time || '00:00';
        return timeA.localeCompare(timeB);
      });

      // Calculate driving times between ALL events (including Google Calendar if they have a location)
      const appointmentsWithDriveTime = [];
      let lastEventWithAddress = null;
      
      for (let i = 0; i < combinedEvents.length; i++) {
        const apt = combinedEvents[i];
        let driveTimeFromPrevious = null;
        
        // Calculate drive time if both current and previous event have addresses
        if (lastEventWithAddress && apt.property_address) {
          try {
            const prevAddress = encodeURIComponent(lastEventWithAddress.property_address);
            const currentAddress = encodeURIComponent(apt.property_address);
            const driveResponse = await axios.get(
              `${API}/drive-time?from_address=${prevAddress}&to_address=${currentAddress}`
            );
            driveTimeFromPrevious = driveResponse.data;
          } catch (e) {
            console.log('Could not calculate drive time:', e);
          }
        }
        
        // Track the last event with an address (regardless of type)
        if (apt.property_address) {
          lastEventWithAddress = apt;
        }
        
        appointmentsWithDriveTime.push({
          ...apt,
          driveTimeFromPrevious
        });
      }

      setPhotographerDayAppointments(appointmentsWithDriveTime);
    } catch (error) {
      console.error('Error fetching photographer day appointments:', error);
      setPhotographerDayAppointments([]);
    } finally {
      setLoadingDayAppointments(false);
    }
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}`, { status });
      fetchData();
    } catch (error) {
      console.error('Error updating appointment status:', error);
    }
  };

  const downloadMediaFile = (fileId, filename) => {
    window.open(`${API}/media/${fileId}`, '_blank');
  };

  const updateFileSharing = async (file) => {
    setSelectedFileForSharing(file);
    setShowSharingModal(true);
  };

  const copyShareLink = async (shareToken) => {
    if (!shareToken) return;
    
    const shareUrl = `${BACKEND_URL}/api/media/share/${shareToken}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Share link copied to clipboard!');
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Share link copied to clipboard!');
    }
  };

  // Enhanced media functions
  const handleBulkUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    const fileInput = e.target.querySelector('input[name="file"]');
    
    if (!fileInput.files || fileInput.files.length === 0) {
      alert('Please select files to upload');
      return;
    }

    // Append all files
    Array.from(fileInput.files).forEach(file => {
      formData.append('files', file);
    });
    
    formData.append('appointment_id', mediaUploadForm.appointment_id);
    formData.append('folder_name', mediaUploadForm.folder_name);
    formData.append('approval_required', 'true');

    try {
      const response = await axios.post(`${API}/media/bulk-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const results = response.data;
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      
      alert(`Bulk upload complete! ${successful} files uploaded successfully. ${failed} files failed.`);
      resetMediaUploadForm();
      if (selectedAppointment) {
        fetchAppointmentMedia(selectedAppointment.id);
      }
    } catch (error) {
      console.error('Error with bulk upload:', error);
      alert('Bulk upload failed: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleUploadClick = async () => {
    console.log('Upload button clicked');
    
    const fileInput = document.getElementById('fileInput');
    const folderSelect = document.getElementById('folderSelect');
    
    if (!fileInput.files || fileInput.files.length === 0) {
      alert('Please select files to upload');
      return;
    }

    if (!selectedAppointment) {
      alert('Please select an appointment first');
      return;
    }

    const files = Array.from(fileInput.files);
    const startTime = Date.now();
    
    // Initialize progress tracking
    const fileStatuses = files.map(file => ({
      name: file.name,
      size: file.size,
      status: 'pending'
    }));
    
    setUploadProgress({
      isUploading: true,
      currentFile: '',
      completedFiles: 0,
      totalFiles: files.length,
      overallProgress: 0,
      fileStatuses: fileStatuses,
      uploadSpeed: 0,
      timeRemaining: 0
    });

    try {
      // Upload files individually for better progress tracking
      const results = [];
      let totalBytesUploaded = 0;
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Update current file being uploaded
        setUploadProgress(prev => ({
          ...prev,
          currentFile: file.name,
          fileStatuses: prev.fileStatuses.map((status, index) => 
            index === i ? { ...status, status: 'uploading' } : status
          )
        }));

        // Add a delay to ensure UI updates are visible
        console.log(`Starting upload for file ${i + 1}/${files.length}: ${file.name}`);
        await new Promise(resolve => setTimeout(resolve, 1000));

        const formData = new FormData();
        formData.append('files', file);
        formData.append('appointment_id', selectedAppointment.id);
        formData.append('folder_name', folderSelect.value);

        try {
          const response = await axios.post(`${API}/media/bulk-upload`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            onUploadProgress: (progressEvent) => {
              const fileProgress = progressEvent.loaded / progressEvent.total;
              const currentFileBytes = fileProgress * file.size;
              const overallBytesUploaded = totalBytesUploaded + currentFileBytes;
              const overallProgress = Math.round((overallBytesUploaded / totalSize) * 100);
              
              // Calculate upload speed and ETA
              const elapsedTime = (Date.now() - startTime) / 1000;
              const uploadSpeed = overallBytesUploaded / elapsedTime; // bytes per second
              const remainingBytes = totalSize - overallBytesUploaded;
              const timeRemaining = remainingBytes > 0 ? Math.round(remainingBytes / uploadSpeed) : 0;
              
              setUploadProgress(prev => {
                const newProgress = {
                  ...prev,
                  overallProgress: overallProgress,
                  uploadSpeed: uploadSpeed / (1024 * 1024), // Convert to MB/s
                  timeRemaining: timeRemaining
                };
                console.log('Progress update:', newProgress);
                return newProgress;
              });
            }
          });

          // Mark file as completed
          totalBytesUploaded += file.size;
          const fileResult = response.data[0]; // Since we upload one file at a time
          results.push(fileResult);
          
          setUploadProgress(prev => ({
            ...prev,
            completedFiles: i + 1,
            fileStatuses: prev.fileStatuses.map((status, index) => 
              index === i ? { 
                ...status, 
                status: fileResult.success ? 'completed' : 'failed' 
              } : status
            )
          }));

        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          results.push({
            success: false,
            filename: file.name,
            message: 'Upload failed: ' + (error.response?.data?.detail || 'Network error')
          });
          
          setUploadProgress(prev => ({
            ...prev,
            completedFiles: i + 1,
            fileStatuses: prev.fileStatuses.map((status, index) => 
              index === i ? { ...status, status: 'failed' } : status
            )
          }));
        }
      }
      
      // Final progress update
      setUploadProgress(prev => ({
        ...prev,
        currentFile: '',
        overallProgress: 100,
        timeRemaining: 0
      }));

      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      
      // Show completion message after a brief delay
      setTimeout(() => {
        alert(`Upload complete! ${successful} files uploaded successfully. ${failed} files failed.`);
        
        // Reset everything
        fileInput.value = '';
        folderSelect.value = 'general';
        setUploadProgress({
          isUploading: false,
          currentFile: '',
          completedFiles: 0,
          totalFiles: 0,
          overallProgress: 0,
          fileStatuses: [],
          uploadSpeed: 0,
          timeRemaining: 0
        });
        setShowMediaUpload(false);
        
        // Refresh the media files
        if (selectedAppointment) {
          fetchAppointmentMedia(selectedAppointment.id);
        }
      }, 1000);

    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + (error.response?.data?.detail || 'Unknown error'));
      setUploadProgress({
        isUploading: false,
        currentFile: '',
        completedFiles: 0,
        totalFiles: 0,
        overallProgress: 0,
        fileStatuses: [],
        uploadSpeed: 0,
        timeRemaining: 0
      });
    }
  };

  const handleApprovalUpdate = async (fileId, status, feedback = '') => {
    try {
      await axios.put(`${API}/media/${fileId}/approval?approval_status=${status}&feedback=${encodeURIComponent(feedback)}`);
      
      if (selectedAppointment) {
        fetchAppointmentMedia(selectedAppointment.id);
      }
      alert(`File ${status} successfully!`);
    } catch (error) {
      console.error('Error updating approval:', error);
      alert('Failed to update approval: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleBulkApproval = async (status) => {
    if (selectedFiles.length === 0) {
      alert('Please select files first');
      return;
    }
    
    const feedback = prompt(`Enter feedback for bulk ${status}:`) || '';
    
    try {
      const fileIds = selectedFiles.map(f => f.id);
      await axios.put(`${API}/media/bulk-approval?approval_status=${status}&feedback=${encodeURIComponent(feedback)}`, null, {
        params: { file_ids: fileIds }
      });
      
      alert(`Bulk ${status} completed for ${selectedFiles.length} files!`);
      setSelectedFiles([]);
      setBulkOperationMode(false);
      if (selectedAppointment) {
        fetchAppointmentMedia(selectedAppointment.id);
      }
    } catch (error) {
      console.error('Error with bulk approval:', error);
      alert('Bulk approval failed: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const createEnhancedGallery = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    
    formData.append('appointment_id', selectedAppointment.id);
    formData.append('name', `${selectedAppointment.client_name} - ${selectedAppointment.property_address}`);
    formData.append('description', 'Professional real estate media gallery');
    formData.append('custom_branding', JSON.stringify(customBranding));
    formData.append('approval_required', 'true');
    formData.append('auto_deliver', 'false');

    try {
      const response = await axios.post(`${API}/galleries/enhanced`, formData);
      alert('Enhanced gallery created successfully!');
      setShowGalleryForm(false);
    } catch (error) {
      console.error('Error creating gallery:', error);
      alert('Failed to create gallery: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const deliverGallery = async (galleryId, clientEmail) => {
    const customMessage = prompt('Enter a custom message for the client (optional):') || '';
    
    try {
      await axios.post(`${API}/media/deliver/${galleryId}?client_email=${encodeURIComponent(clientEmail)}&custom_message=${encodeURIComponent(customMessage)}`);
      alert('Gallery delivered to client successfully!');
    } catch (error) {
      console.error('Error delivering gallery:', error);
      alert('Failed to deliver gallery: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };
  const handleEventDrop = async (args) => {
    const { event, start, end } = args;
    
    try {
      const newDate = moment(start).format('YYYY-MM-DD');
      const newTime = moment(start).format('HH:mm');
      
      // Show confirmation dialog
      const confirmReschedule = window.confirm(
        `Reschedule "${event.resource.client_name}" to ${moment(start).format('MMMM Do, YYYY [at] h:mm A')}?`
      );
      
      if (!confirmReschedule) return;
      
      // Call the reschedule API
      await axios.put(`${API}/appointments/${event.resource.appointment_id}/reschedule`, {
        scheduled_date: newDate,
        scheduled_time: newTime,
        send_notification: true
      });
      
      // Refresh calendar and appointments data
      await fetchCalendarData();
      await fetchData();
      
      alert('Appointment rescheduled successfully!');
      
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      
      if (error.response?.status === 409) {
        alert('Cannot reschedule: Time slot is already booked. Please choose a different time.');
      } else {
        alert('Failed to reschedule appointment: ' + (error.response?.data?.detail || 'Unknown error'));
      }
      
      // Refresh calendar to reset the visual state
      await fetchCalendarData();
    }
  };

  const openMediaGallery = (appointment) => {
    setSelectedAppointment(appointment);
    fetchAppointmentMedia(appointment.id);
    setActiveTab('media');
  };

  // Aryeo Import Functions
  const fetchAryeoToken = async () => {
    try {
      const response = await axios.get(`${API}/settings/aryeo-token`);
      if (response.data.has_token) {
        setHasAryeoToken(true);
        setAryeoToken(response.data.masked_token);
      } else {
        setHasAryeoToken(false);
        setAryeoToken('');
      }
    } catch (error) {
      console.error('Error fetching Aryeo token:', error);
    }
  };

  const saveAryeoToken = async () => {
    if (!aryeoTokenInput.trim()) {
      alert('Please enter a token');
      return;
    }
    
    setSavingToken(true);
    try {
      const response = await axios.post(`${API}/settings/aryeo-token`, {
        token: aryeoTokenInput
      });
      if (response.data.success) {
        alert('Aryeo API token saved successfully!');
        setShowTokenInput(false);
        setAryeoTokenInput('');
        await fetchAryeoToken();
        // Reset connection status so user tests the new token
        setImportConnectionStatus(null);
      }
    } catch (error) {
      console.error('Error saving Aryeo token:', error);
      alert('Failed to save token: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSavingToken(false);
    }
  };

  const deleteAryeoToken = async () => {
    if (!window.confirm('Are you sure you want to delete your Aryeo API token?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/settings/aryeo-token`);
      if (response.data.success) {
        alert('Aryeo API token deleted successfully');
        // Refresh token status from server
        await fetchAryeoToken();
        // Reset connection status
        setImportConnectionStatus(null);
      }
    } catch (error) {
      console.error('Error deleting Aryeo token:', error);
      alert('Failed to delete token: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const testAryeoConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await axios.post(`${API}/import/test-connection`);
      if (response.data.success) {
        setImportConnectionStatus('connected');
        alert('Successfully connected to Aryeo API!');
      }
    } catch (error) {
      console.error('Error testing Aryeo connection:', error);
      setImportConnectionStatus('error');
      alert('Failed to connect to Aryeo API: ' + (error.response?.data?.detail || 'Connection failed'));
    } finally {
      setTestingConnection(false);
    }
  };

  const previewAryeoData = async (dataType, page = null) => {
    setLoadingPreview(true);
    try {
      const currentPage = page || importPagination[dataType]?.page || 1;
      const limit = importPagination[dataType]?.limit || 50;
      const offset = (currentPage - 1) * limit;
      
      const response = await axios.get(`${API}/import/preview?data_type=${dataType}&limit=${limit}&offset=${offset}`);
      setPreviewData(prev => ({
        ...prev,
        [dataType]: response.data
      }));
      setShowRecordsList(prev => ({
        ...prev,
        [dataType]: true
      }));
      
      // Update pagination
      if (page) {
        setImportPagination(prev => ({
          ...prev,
          [dataType]: { ...prev[dataType], page }
        }));
      }
    } catch (error) {
      console.error(`Error previewing ${dataType}:`, error);
      alert(`Failed to preview ${dataType}: ` + (error.response?.data?.detail || 'Preview failed'));
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggleRecordSelection = (dataType, recordId) => {
    setSelectedRecords(prev => {
      const current = prev[dataType] || [];
      const isSelected = current.includes(recordId);
      return {
        ...prev,
        [dataType]: isSelected 
          ? current.filter(id => id !== recordId)
          : [...current, recordId]
      };
    });
  };

  const selectAllRecords = (dataType) => {
    if (!previewData[dataType]) return;
    
    const allIds = previewData[dataType].preview_data.map(record => 
      record.id || record.uuid || Math.random().toString()
    );
    setSelectedRecords(prev => ({
      ...prev,
      [dataType]: allIds
    }));
  };

  const clearSelection = (dataType) => {
    setSelectedRecords(prev => ({
      ...prev,
      [dataType]: []
    }));
  };

  const deselectAllRecords = (dataType) => {
    clearSelection(dataType);
  };

  const importAryeoData = async (dataType) => {
    const selectedIds = selectedRecords[dataType] || [];
    
    if (selectedIds.length === 0) {
      alert('Please select at least one record to import');
      return;
    }
    
    setImporting(true);
    setImportResults(null);
    
    try {
      const endpoint = {
        clients: '/import/clients',
        listings: '/import/listings', 
        orders: '/import/orders',
        products: '/import/products'
      }[dataType];
      
      if (!endpoint) {
        throw new Error('Invalid data type');
      }
      
      // Send selected IDs to backend for selective import
      const response = await axios.post(`${API}${endpoint}`, {
        selected_ids: selectedIds,
        limit: selectedIds.length
      });
      
      setImportResults(response.data);
      
      // Refresh data based on import type
      if (dataType === 'clients') {
        fetchData();
      } else if (dataType === 'products') {
        // Reload products
        fetchSettings();
      }
      
      // Clear selection after successful import
      setSelectedRecords(prev => ({
        ...prev,
        [dataType]: []
      }));
      
      // Show detailed results
      const message = response.data.message + 
        (response.data.errors && response.data.errors.length > 0 
          ? `\n\nErrors:\n${response.data.errors.slice(0, 3).join('\n')}` 
          : '');
      alert(message);
      
      // Reload preview to show imported status
      if (previewData[dataType]) {
        loadPreviewData(dataType);
      }
      
    } catch (error) {
      console.error(`Error importing ${dataType}:`, error);
      alert(`Failed to import ${dataType}: ` + (error.response?.data?.detail || 'Import failed'));
    } finally {
      setImporting(false);
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" color="blue" />
      </div>
    );
  }

// DashboardContext value - provides shared state to extracted page components
  const contextValue = {
    user, permissions, hasPermission, isMasterAdmin, isImpersonating, setIsImpersonating,
    originalAdminToken, setOriginalAdminToken, logout,
    activeTab, setActiveTab,
    appointments: safeAppointments, clients: safeClients, teamMembers: safeTeamMembers,
    products, packages, addonProducts, tasks: safeTasks, mediaFiles: safeMediaFiles,
    availableTags, setAvailableTags, serviceTypes,
    calendarEvents, setCalendarEvents, googleCalendarEvents, setGoogleCalendarEvents,
    businessHours, setBusinessHours, scheduleBlocks, setScheduleBlocks,
    setAppointments, setClients, setTeamMembers, setProducts, setPackages,
    setAddonProducts, setTasks, setMediaFiles,
    stats, setStats,
    dashboardSections, setDashboardSections, showDashboardCustomize, setShowDashboardCustomize,
    tempDashboardSections, setTempDashboardSections, draggedSection, setDraggedSection,
    todaysAppointmentsView, setTodaysAppointmentsView,
    dashboardQuotes, setDashboardQuotes, expandedQuoteId, setExpandedQuoteId,
    selectedDashboardQuote, setSelectedDashboardQuote, quoteActionLoading, setQuoteActionLoading,
    quoteFilterTab, setQuoteFilterTab,
    calendarView, setCalendarView, calendarMonth, setCalendarMonth,
    photographerFilters, setPhotographerFilters, getPhotographerColor, togglePhotographerFilter,
    dayDriveTimes, loadingDriveTimes,
    showBlockModal, setShowBlockModal, editingBlock, setEditingBlock,
    blockForm, setBlockForm, selectedBlock, setSelectedBlock,
    showBlockDetails, setShowBlockDetails,
    appointmentFilters, setAppointmentFilters, showAppointmentFilters, setShowAppointmentFilters,
    showAppointmentForm, setShowAppointmentForm, editingAppointment, setEditingAppointment,
    editAppointment, selectedListingForEdit, setSelectedListingForEdit,
    appointmentForm, setAppointmentForm, selectedProducts, setSelectedProducts,
    calculatedPricing, setCalculatedPricing, calculatingPrice, setCalculatingPrice,
    productPricing, setProductPricing, isReschedulingAppointment, setIsReschedulingAppointment,
    showAppointmentDetails, setShowAppointmentDetails, selectedAppointmentDetails, setSelectedAppointmentDetails,
    appointmentThumbnails, setAppointmentThumbnails,
    selectedAppointment, setSelectedAppointment,
    appointmentClientSearchQuery, setAppointmentClientSearchQuery,
    appointmentClientSearchResults, setAppointmentClientSearchResults,
    showAppointmentClientSearch, setShowAppointmentClientSearch,
    showCreateClientInAppointment, setShowCreateClientInAppointment,
    newClientInAppointmentForm, setNewClientInAppointmentForm,
    addressSuggestions, setAddressSuggestions, showAddressSuggestions, setShowAddressSuggestions,
    availableDates, setAvailableDates, loadingAvailableDates, setLoadingAvailableDates,
    checkingAvailability, setCheckingAvailability,
    selectedPhotographerAvailability, setSelectedPhotographerAvailability,
    availableTimeSlots, setAvailableTimeSlots,
    timeOverrideEnabled, setTimeOverrideEnabled,
    productSearchQuery, setProductSearchQuery, showProductDropdown, setShowProductDropdown,
    photographerDayAppointments, setPhotographerDayAppointments, loadingDayAppointments, setLoadingDayAppointments,
    clientSearchHighlightIndex, setClientSearchHighlightIndex,
    addressSearchHighlightIndex, setAddressSearchHighlightIndex,
    productSearchHighlightIndex, setProductSearchHighlightIndex,
    showQuoteBuilder, setShowQuoteBuilder,
    quoteAvailableSlots, setQuoteAvailableSlots, loadingQuoteSlots, setLoadingQuoteSlots,
    selectedQuoteSlots, setSelectedQuoteSlots, sendingQuote, setSendingQuote, quoteSent, setQuoteSent,
    selectedListingForEdit, setSelectedListingForEdit,
    propertyClients, setPropertyClients,
    mediaExpanded, setMediaExpanded, imageThumbnailSize, setImageThumbnailSize,
    slideshowOpen, setSlideshowOpen, slideshowIndex, setSlideshowIndex,
    propertyDetailsExpanded, setPropertyDetailsExpanded,
    listingTasksExpanded, setListingTasksExpanded,
    showMediaUpload, setShowMediaUpload, showSharingModal, setShowSharingModal,
    selectedFiles, setSelectedFiles, bulkOperationMode, setBulkOperationMode,
    approvalFilter, setApprovalFilter, showGalleryForm, setShowGalleryForm,
    customBranding, setCustomBranding,
    selectedFileForSharing, setSelectedFileForSharing,
    showEditAddressModal, setShowEditAddressModal, editAddressForm, setEditAddressForm,
    showDeleteConfirm, setShowDeleteConfirm, mediaToDelete, setMediaToDelete,
    showAddClientModal, setShowAddClientModal, selectedClientToAdd, setSelectedClientToAdd,
    clientSearchQuery, setClientSearchQuery, clientSearchResults, setClientSearchResults,
    showCreateNewClient, setShowCreateNewClient, newClientForm, setNewClientForm,
    showMediaManager, setShowMediaManager, mediaManagerAppointment, setMediaManagerAppointment,
    draggingIndex, setDraggingIndex, dragOverIndex, setDragOverIndex, isMediaCollapsed, setIsMediaCollapsed,
    uploadProgress, setUploadProgress,
    uploadingCompanyLogo, setUploadingCompanyLogo,
    companyLogoFile, setCompanyLogoFile, companyLogoPreview, setCompanyLogoPreview,
    existingCompanyLogoUrl, setExistingCompanyLogoUrl,
    editingClient, setEditingClient,
    showClientForm, setShowClientForm, clientFormActiveTab, setClientFormActiveTab,
    clientForm, setClientForm,
    avatarPreview, setAvatarPreview, existingAvatarUrl, setExistingAvatarUrl,
    clientAvatarUrls, setClientAvatarUrls,
    showTaskForm, setShowTaskForm, editingTask, setEditingTask,
    taskForm, setTaskForm, taskSortBy, setTaskSortBy,
    showCompletedTasks, setShowCompletedTasks,
    activeTasksExpanded, setActiveTasksExpanded, futureTasksExpanded, setFutureTasksExpanded,
    tasksSummary, setTasksSummary, taskSectionsExpanded, setTaskSectionsExpanded,
    teamViewType, setTeamViewType,
    showAddTeamMember, setShowAddTeamMember, newTeamMember, setNewTeamMember,
    editingTeamMember, setEditingTeamMember, editTeamMemberTab, setEditTeamMemberTab,
    editTeamMemberForm, setEditTeamMemberForm,
    showEditTeamMember, setShowEditTeamMember, editingTeamMemberId, setEditingTeamMemberId,
    teamMemberAvatarUrls, setTeamMemberAvatarUrls,
    teamMemberAvatarFile, setTeamMemberAvatarFile,
    teamMemberAvatarPreview, setTeamMemberAvatarPreview,
    existingTeamMemberAvatarUrl, setExistingTeamMemberAvatarUrl,
    avatarLoadingStates, setAvatarLoadingStates,
    showCalendarModal, setShowCalendarModal,
    selectedMemberForCalendar, setSelectedMemberForCalendar,
    calendarConnectionData, setCalendarConnectionData,
    showAddProduct, setShowAddProduct, newProduct, setNewProduct,
    showEditProduct, setShowEditProduct, editingProduct, setEditingProduct,
    editProductForm, setEditProductForm,
    showPricingTiers, setShowPricingTiers, newPricingTier, setNewPricingTier,
    editShowPricingTiers, setEditShowPricingTiers, editPricingTier, setEditPricingTier,
    productsViewType, setProductsViewType, productsTab, setProductsTab,
    compactView, setCompactView, packageProductSearch, setPackageProductSearch,
    collapsedCategories, setCollapsedCategories,
    uploadingProductImage, setUploadingProductImage,
    showAddPackage, setShowAddPackage, editingPackage, setEditingPackage,
    newPackage, setNewPackage,
    showAddAddon, setShowAddAddon, editingAddon, setEditingAddon, newAddon, setNewAddon,
    draggedItem, setDraggedItem, draggedType, setDraggedType,
    handleItemDragStart, handleItemDragEnd, handleItemDragOver,
    handleDropProduct, handleDropAddon, handleDropPackage,
    orderForms, setOrderForms, orderFormSubmissions, setOrderFormSubmissions,
    showOrderFormBuilder, setShowOrderFormBuilder, editingOrderForm, setEditingOrderForm,
    newOrderForm, setNewOrderForm,
    notificationTemplates, setNotificationTemplates,
    editingTemplate, setEditingTemplate,
    settingsTab, setSettingsTab,
    accountSettings, setAccountSettings,
    workingHours, setWorkingHours,
    dropboxSettings, setDropboxSettings,
    showDropboxBrowser, setShowDropboxBrowser, dropboxFiles, setDropboxFiles,
    dropboxCurrentPath, setDropboxCurrentPath, dropboxSelectedFiles, setDropboxSelectedFiles,
    automatedTaskRules, setAutomatedTaskRules,
    showAddTaskRule, setShowAddTaskRule, editingTaskRule, setEditingTaskRule,
    newTaskRule, setNewTaskRule,
    importConnectionStatus, setImportConnectionStatus, testingConnection, setTestingConnection,
    previewData, setPreviewData, loadingPreview, setLoadingPreview,
    importing, setImporting, importResults, setImportResults,
    aryeoToken, setAryeoToken, aryeoTokenInput, setAryeoTokenInput,
    hasAryeoToken, setHasAryeoToken, showTokenInput, setShowTokenInput,
    savingToken, setSavingToken,
    importPagination, setImportPagination,
    selectedRecords, setSelectedRecords, showRecordsList, setShowRecordsList,
    adminData, setAdminData,
    showDeleteBusinessModal, setShowDeleteBusinessModal,
    businessToDelete, setBusinessToDelete, deletingBusiness, setDeletingBusiness,
    selectedPendingSubmission, setSelectedPendingSubmission,
    showPendingDetails, setShowPendingDetails,
    showRejectModal, setShowRejectModal, rejectReason, setRejectReason,
    showProposeTimeModal, setShowProposeTimeModal,
    proposedDate, setProposedDate, proposedTime, setProposedTime, proposalNote, setProposalNote,
    showEventDetails, setShowEventDetails, selectedEvent, setSelectedEvent,
    darkMode, setDarkMode,
    showToast, showConfirm, hideToast, hideConfirm,
    toast, confirmDialog,
    isMobileMenuOpen, setIsMobileMenuOpen,
    isSidebarCollapsed, setIsSidebarCollapsed,
    searchQuery, setSearchQuery, searchResults, setSearchResults,
    showSearchResults, setShowSearchResults, showCommandCenter, setShowCommandCenter,
    selectedSearchIndex, setSelectedSearchIndex, searchLoading, setSearchLoading,
    fetchData, fetchSettings, fetchTasks, fetchTasksSummary,
    fetchCalendarData, fetchGoogleCalendarEvents, fetchAppointmentMedia,
    fetchAryeoToken, saveAryeoToken, deleteAryeoToken, testAryeoConnection,
    previewAryeoData, importAryeoData,
    toggleRecordSelection, selectAllRecords, clearSelection, deselectAllRecords,
    loadMyTeamMemberData, getTaskAssignee,
    performSearch, searchClients, searchAppointmentClients, selectAppointmentClient,
    createNewClientInAppointment, searchAddressSuggestions, selectAddressSuggestion,
    addClientToProperty, createNewClient,
    getTodaysAppointments, formatTableDate, formatTableTime, formatAppointmentDate,
    updateAppointmentStatus, openNewAppointmentForm,
    calculateAppointmentPricing, calculateAllProductPricing, calculateProductPrice,
    fetchPhotographerAvailability, fetchMonthAvailability, fetchPhotographerDayAppointments,
    checkTeamMemberAvailability, handleAvailabilityCheck,
    syncAppointmentToCalendar,
    addTeamMember, deleteTeamMember, updateTeamMember, openEditTeamMember,
    addProduct, deleteProduct, updateProduct, openEditProduct,
    deleteAddon, deletePackage,
    addPricingTier, removePricingTier, addEditPricingTier, removeEditPricingTier,
    connectTeamMemberCalendar, disconnectTeamMemberCalendar,
    updateWorkingHours,
    downloadMediaFile, updateFileSharing, copyShareLink,
    handleBulkUpload, handleUploadClick, handleApprovalUpdate, handleBulkApproval,
    createEnhancedGallery, deliverGallery,
    handleEventDrop, openMediaGallery,
    handleDragStart, handleDragOver, handleDragEnter, handleDragLeave, handleDrop, saveMediaOrder,
    handleCommandCenterSearch, handleCommandCenterResultClick,
    handleCommandCenterClose, handleCommandCenterClear,
    handleSearchSelect, handleSearchKeyDown,
    getAvatarUrl, fetchAvatarBlob,
    editTask, editClient, updateAccountSettings, updateTaskStatus, handleTaskSubmit, deleteTask,
    handleAppointmentSubmit, fetchQuoteAvailability, toggleQuoteSlot, sendAppointmentQuote,
    resetAppointmentForm,
    API, BACKEND_URL, moment, axios,
    photographerColors,
    packageSearchIndex, setPackageSearchIndex,
    availabilityData, setAvailabilityData,
    newAppointment, setNewAppointment,
    mediaUploadForm, setMediaUploadForm,
    pendingSubmissions: orderFormSubmissions.filter(s => s.status === 'pending_review'),
  };
  return (
    <DashboardContext.Provider value={contextValue}>
    <div className={`clara-app-layout ${darkMode ? 'dark-mode' : ''}`}>
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast} 
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.show}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={() => {
          if (confirmDialog.onConfirm) confirmDialog.onConfirm();
          hideConfirm();
        }}
        onClose={hideConfirm}
        confirmText="Delete"
        cancelText="Cancel"
        isDanger={true}
      />
      
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Clara Labs Left Sidebar */}
      <Sidebar
        user={user}
        permissions={permissions}
        hasPermission={hasPermission}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        setShowAppointmentForm={setShowAppointmentForm}
        openNewAppointmentForm={openNewAppointmentForm}
        fetchSettings={fetchSettings}
        products={products}
        teamMembers={teamMembers}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        logout={logout}
        isBlurred={showCommandCenter}
        onCommandCenterOpen={() => setShowCommandCenter(true)}
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
        isMasterAdmin={isMasterAdmin}
        pendingSubmissionsCount={orderFormSubmissions.filter(s => s.status === 'pending_review').length}
        logoUrl={accountSettings.logo_url}
        businessName={accountSettings.business_name}
      />

      {/* Main Content Area */}
      <div className={`clara-main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Desktop Hamburger Menu - Always visible on calendar page */}
        {activeTab === 'calendar' && (
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="fixed top-4 z-[200] bg-gray-800 hover:bg-gray-700 text-white p-3 rounded-lg shadow-lg transition-all hidden md:flex items-center justify-center"
            style={{ 
              width: '48px', 
              height: '48px',
              left: isSidebarCollapsed ? '16px' : '256px'
            }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="fixed top-0 left-0 right-0 z-[1000] bg-orange-500 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span className="font-medium">Viewing as: {user?.business_name || user?.user_name}</span>
            </div>
            <button
              onClick={() => {
                const originalToken = localStorage.getItem('originalAdminToken');
                if (originalToken) {
                  localStorage.setItem('token', originalToken);
                  localStorage.removeItem('originalAdminToken');
                  setIsImpersonating(false);
                  setOriginalAdminToken(null);
                  window.location.reload();
                }
              }}
              className="px-4 py-1 bg-white text-orange-600 text-sm font-medium rounded-lg hover:bg-orange-50 transition-colors"
            >
              Exit View As
            </button>
          </div>
        )}
        
        {/* Mobile Header with Command Center Button */}
        <div className={`mobile-header ${darkMode ? 'dark' : ''}`}>
          <button
            onClick={() => setShowCommandCenter(true)}
            className="flex-1 mr-3 px-4 py-2 rounded-lg text-left text-white transition-all flex items-center justify-between shadow-md"
            style={{ background: 'linear-gradient(135deg, #1f2937 0%, #9333ea 100%)' }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-sm">Search...</span>
            </div>
            <kbd className="px-2 py-1 text-xs font-semibold text-white bg-black bg-opacity-20 rounded">
              ⌘K
            </kbd>
          </button>
          <button 
            className={`hamburger-menu ${darkMode ? 'dark' : ''}`}
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        <div className={`clara-content-area ${activeTab === 'calendar' ? 'calendar-fullscreen' : ''}`}>

        {/* Calendar Tab */}
        {activeTab === 'calendar' && <CalendarPage />}


        {/* Global Reject Booking Modal - Accessible from both Calendar and Appointments pages */}
        <CalendarEventModals />


        {/* Global Propose New Time Modal - Accessible from both Calendar and Appointments pages */}

        {/* Admin Panel Tab - Master Admin Only */}
        {activeTab === 'admin' && isMasterAdmin && <AdminPage />}

        {/* Delete Business Confirmation Modal */}
        <DeleteBusinessModal />


        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && <DashboardHome />}

        {/* Dashboard Customize Modal */}
        {showDashboardCustomize && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[150]"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setShowDashboardCustomize(false);
              }
            }}
            tabIndex={-1}
            ref={(el) => el && el.focus()}
          >
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Customize Dashboard</h3>
                <p className="text-sm text-gray-500 mt-1">Toggle sections and drag to reorder</p>
              </div>
              
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                <div className="space-y-2">
                  {tempDashboardSections.map((section, index) => (
                    <div
                      key={section.id}
                      draggable
                      onDragStart={() => setDraggedSection(index)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (draggedSection === null) return;
                        const newSections = [...tempDashboardSections];
                        const draggedItem = newSections[draggedSection];
                        newSections.splice(draggedSection, 1);
                        newSections.splice(index, 0, draggedItem);
                        setTempDashboardSections(newSections);
                        setDraggedSection(null);
                      }}
                      onDragEnd={() => setDraggedSection(null)}
                      className={`flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-move hover:bg-gray-100 transition-colors ${
                        draggedSection === index ? 'opacity-50 border-blue-400' : ''
                      }`}
                    >
                      {/* Drag Handle */}
                      <div className="text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                        </svg>
                      </div>
                      
                      {/* Section Name */}
                      <span className="flex-1 text-sm font-medium text-gray-700">{section.name}</span>
                      
                      {/* Toggle */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTempDashboardSections(prev => 
                            prev.map(s => s.id === section.id ? { ...s, visible: !s.visible } : s)
                          );
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          section.visible ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            section.visible ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowDashboardCustomize(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setDashboardSections(tempDashboardSections);
                    localStorage.setItem('dashboardSections', JSON.stringify(tempDashboardSections));
                    setShowDashboardCustomize(false);
                    showToast('Dashboard layout saved!', 'success');
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Listings Tab - Using extracted ListingsPage component */}
        {activeTab === 'media' && (
          <ListingsPage
            appointments={appointments}
            clients={clients}
            tasks={tasks}
            teamMembers={teamMembers}
            appointmentThumbnails={appointmentThumbnails}
            products={products}
            availableTags={availableTags}
            setActiveTab={setActiveTab}
            setSelectedListingForEdit={setSelectedListingForEdit}
            setPropertyClients={setPropertyClients}
            setShowAppointmentForm={setShowAppointmentForm}
            setEditingTask={setEditingTask}
            setTaskForm={setTaskForm}
            setShowTaskForm={setShowTaskForm}
            setAppointments={setAppointments}
            fetchAppointmentMedia={fetchAppointmentMedia}
            formatAppointmentDate={formatAppointmentDate}
            showToast={showToast}
            API={API}
            axios={axios}
          />
        )}

        {/* Edit Listing Dedicated Page */}
        {activeTab === 'edit-listing' && selectedListingForEdit && <EditListingPage />}
        

            {/* Upload Modal with Progress - Clara Labs Style */}
        <MediaModals />

          </div>
        {/* Clients Tab */}
        {/* Clients Tab - Using extracted ClientsPage component.
            Also mounted (hidden) when `showClientForm` is true so the
            client-edit drawer can be opened from other pages (e.g. Dashboard
            "Recent Clients" tile). The Sheet drawer inside ClientsPage uses a
            Radix Portal so it still renders visibly to document.body even when
            this wrapper is display:none. */}
        {(activeTab === 'clients' || showClientForm) && (
          <div style={activeTab === 'clients' ? undefined : { display: 'none' }}>
          <ClientsPage
            clients={clients}
            appointments={appointments}
            showClientForm={showClientForm}
            editingClient={editingClient}
            clientForm={clientForm}
            clientFormActiveTab={clientFormActiveTab}
            clientAvatarUrls={clientAvatarUrls}
            uploadingCompanyLogo={uploadingCompanyLogo}
            setShowClientForm={setShowClientForm}
            setEditingClient={setEditingClient}
            setClientForm={setClientForm}
            setClientFormActiveTab={setClientFormActiveTab}
            setActiveTab={setActiveTab}
            setSelectedListingForEdit={setSelectedListingForEdit}
            setPropertyClients={setPropertyClients}
            setShowAppointmentForm={setShowAppointmentForm}
            setEditingAppointment={setEditingAppointment}
            setAppointmentForm={setAppointmentForm}
            setSelectedProducts={setSelectedProducts}
            setAppointmentClientSearchQuery={setAppointmentClientSearchQuery}
            hasPermission={hasPermission}
            handleClientSubmit={handleClientSubmit}
            resetClientForm={resetClientForm}
            fetchAvatarBlob={fetchAvatarBlob}
            fetchAppointmentMedia={fetchAppointmentMedia}
            deleteClient={deleteClientDirect}
          />
          </div>
        )}

        {/* Tasks Tab - Using extracted TasksPage component */}
        {activeTab === 'tasks' && (
          <TasksPage
            tasks={tasks}
            appointments={appointments}
            teamMemberAvatarUrls={teamMemberAvatarUrls}
            taskSortBy={taskSortBy}
            showCompletedTasks={showCompletedTasks}
            futureTasksExpanded={futureTasksExpanded}
            activeTasksExpanded={activeTasksExpanded}
            setActiveTab={setActiveTab}
            setSettingsTab={setSettingsTab}
            setEditingTask={setEditingTask}
            setTaskForm={setTaskForm}
            setShowTaskForm={setShowTaskForm}
            setTaskSortBy={setTaskSortBy}
            setShowCompletedTasks={setShowCompletedTasks}
            setFutureTasksExpanded={setFutureTasksExpanded}
            setActiveTasksExpanded={setActiveTasksExpanded}
            getTaskAssignee={getTaskAssignee}
            editTask={editTask}
            updateTaskStatus={updateTaskStatus}
          />
        )}

        {/* Appointments Tab - Using extracted AppointmentsPage component */}
        {activeTab === 'appointments' && (
          <AppointmentsPage
            appointments={appointments}
            clients={clients}
            teamMembers={teamMembers}
            orderFormSubmissions={orderFormSubmissions}
            serviceTypes={serviceTypes}
            availableTags={availableTags}
            appointmentFilters={appointmentFilters}
            showAppointmentFilters={showAppointmentFilters}
            setActiveTab={setActiveTab}
            setAppointmentFilters={setAppointmentFilters}
            setShowAppointmentFilters={setShowAppointmentFilters}
            setCalendarMonth={setCalendarMonth}
            setCalendarView={setCalendarView}
            setOrderFormSubmissions={setOrderFormSubmissions}
            setAppointments={setAppointments}
            setSelectedPendingSubmission={setSelectedPendingSubmission}
            setShowRejectModal={setShowRejectModal}
            setRejectReason={setRejectReason}
            setShowProposeTimeModal={setShowProposeTimeModal}
            setProposedDate={setProposedDate}
            setProposedTime={setProposedTime}
            setProposalNote={setProposalNote}
            setShowAppointmentForm={setShowAppointmentForm}
            setEditingAppointment={setEditingAppointment}
            setAppointmentForm={setAppointmentForm}
            setSelectedProducts={setSelectedProducts}
            setAppointmentClientSearchQuery={setAppointmentClientSearchQuery}
            hasPermission={hasPermission}
            formatTableTime={formatTableTime}
            showToast={showToast}
            API={API}
            axios={axios}
          />
        )}


        {/* Appointment Form Modal */}
        <AppointmentFormModal />


        {/* Team Tab - Standalone */}
        {activeTab === 'team' && <TeamPage />}

        {/* Edit Team Member Page */}
        {activeTab === 'edit-team-member' && editingTeamMember && <EditTeamMemberPage />}

        {/* Products Tab - Standalone */}
        {/* Products Tab - Using extracted ProductsPage component */}
        {activeTab === 'products' && (
          <ProductsPage
            products={products}
            addons={addonProducts}
            packages={packages}
            productsTab={productsTab}
            compactView={compactView}
            productsViewType={productsViewType}
            showAddProduct={showAddProduct}
            showAddAddon={showAddAddon}
            showAddPackage={showAddPackage}
            editingProduct={editingProduct}
            editingAddon={editingAddon}
            editingPackage={editingPackage}
            setProductsTab={setProductsTab}
            setCompactView={setCompactView}
            setProductsViewType={setProductsViewType}
            setShowAddProduct={setShowAddProduct}
            setShowAddAddon={setShowAddAddon}
            setShowAddPackage={setShowAddPackage}
            setEditingProduct={setEditingProduct}
            setEditingAddon={setEditingAddon}
            setEditingPackage={setEditingPackage}
            setActiveTab={setActiveTab}
            setSettingsTab={setSettingsTab}
            hasPermission={hasPermission}
            deleteProduct={deleteProduct}
            deleteAddon={deleteAddon}
            deletePackage={deletePackage}
            openEditProduct={openEditProduct}
            API={API}
          />
        )}

        {/* Order Form Tab */}
        {activeTab === 'orderform' && <OrderFormPage />}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && <NotificationsPage />}

        {/* Payroll Tab */}
        {activeTab === 'payroll' && <PayrollPage showToast={showToast} />}

        {/* Settings Tab */}
        {activeTab === 'settings' && <SettingsPage />}

        {/* Add Team Member Modal */}
        <AddTeamMemberModal />


        {/* Add Product Modal */}
        <ProductModals />


        {/* Google Calendar Connection Modal */}
        {showCalendarModal && selectedMemberForCalendar && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-[150]">
            <div className="clara-card max-w-md w-full">
              <div className="modal-header-fixed px-8 py-6 border-b" style={{ borderColor: 'var(--clara-border)' }}>
                <h3 className="clara-heading-3" style={{ margin: 0 }}>Connect Google Calendar</h3>
                <p className="clara-body-sm mt-2" style={{ color: 'var(--clara-medium-gray)' }}>
                  For: {selectedMemberForCalendar.name}
                </p>
              </div>
              <div className="modal-content-scroll p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="clara-body font-medium mb-2" style={{ color: 'var(--clara-success)' }}>🔗 Real Google Calendar Integration</h4>
                    <p className="clara-body-sm mb-3" style={{ color: 'var(--clara-secondary)' }}>
                      This will connect to your actual Google Calendar using OAuth2 authentication:
                    </p>
                    <ul className="clara-body-sm space-y-1" style={{ color: 'var(--clara-secondary)' }}>
                      <li>• ✅ Real OAuth2 authentication with Google</li>
                      <li>• ✅ Check actual calendar availability</li>
                      <li>• ✅ Sync appointments in real-time</li>
                      <li>• ✅ Prevent double-booking conflicts</li>
                      <li>• ✅ Automatic calendar event creation</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="clara-body font-medium mb-2" style={{ color: 'var(--clara-primary)' }}>How it works:</h4>
                    <ol className="clara-body-sm space-y-1" style={{ color: 'var(--clara-secondary)' }}>
                      <li>1. Click "Connect Calendar" to open Google authorization</li>
                      <li>2. Sign in to your Google account</li>
                      <li>3. Grant calendar access permissions</li>
                      <li>4. Calendar integration will be activated automatically</li>
                    </ol>
                  </div>
                  
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="clara-body font-medium mb-2" style={{ color: '#D97706' }}>⚠️ Important Notes:</h4>
                    <ul className="clara-body-sm space-y-1" style={{ color: 'var(--clara-secondary)' }}>
                      <li>• Make sure pop-ups are enabled in your browser</li>
                      <li>• Use the same Google account you want to connect</li>
                      <li>• Calendar permissions can be revoked anytime from Google settings</li>
                    </ul>
                  </div>

                  {/* OAuth2 flow - no manual input needed */}
                </div>
              </div>
              <div className="modal-footer-fixed px-8 py-6 border-t" style={{ borderColor: 'var(--clara-border)' }}>
                <div className="flex space-x-4">
                  <button
                    onClick={connectTeamMemberCalendar}
                    className="flex-1 clara-btn-primary py-3"
                  >
                    🔗 Connect to Google Calendar
                  </button>
                  <button
                    onClick={() => {
                      setShowCalendarModal(false);
                      // Only reopen Edit Team Member modal for admins (not photographers viewing their own settings)
                      if (editingTeamMember && hasPermission('settings', 'manage_business')) {
                        setShowEditTeamMember(true);
                      }
                    }}
                    className="flex-1 clara-btn-secondary py-3"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Team Member Modal */}
        {showEditTeamMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-[150]">
            <div className="clara-card w-full max-w-[95vw] md:max-w-4xl rounded-lg overflow-hidden" style={{ border: 'none', outline: 'none' }}>
              <div className="modal-header-fixed px-8 py-4" style={{ backgroundColor: '#374051' }}>
                <h3 className="text-lg font-semibold text-white" style={{ margin: 0, color: '#ffffff' }}>Edit Team Member</h3>
              </div>
              
              {/* Team Member Info Bar - Always Visible */}
              <div className="px-8 py-4 bg-gray-50 border-b" style={{ borderColor: 'var(--clara-border)' }}>
                <div className="flex items-center gap-4">
                  {existingTeamMemberAvatarUrl || teamMemberAvatarPreview ? (
                    <img 
                      src={teamMemberAvatarPreview || existingTeamMemberAvatarUrl}
                      alt={editTeamMemberForm.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-400 font-semibold">
                      {editTeamMemberForm.name ? editTeamMemberForm.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '?'}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">{editTeamMemberForm.name || 'Team Member'}</p>
                    <p className="text-sm text-gray-600">{editTeamMemberForm.email} • {editTeamMemberForm.role}</p>
                  </div>
                </div>
              </div>
              
              {/* Tab Navigation */}
              <div className="border-b" style={{ borderColor: 'var(--clara-border)', backgroundColor: '#f9fafb' }}>
                <div className="flex px-8">
                  <button
                    onClick={() => setEditTeamMemberTab('general')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      editTeamMemberTab === 'general'
                        ? 'border-blue-600 text-blue-400'
                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                    }`}
                  >
                    General
                  </button>
                  <button
                    onClick={() => setEditTeamMemberTab('schedule')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      editTeamMemberTab === 'schedule'
                        ? 'border-blue-600 text-blue-400'
                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                    }`}
                  >
                    Schedule
                  </button>
                  <button
                    onClick={() => setEditTeamMemberTab('territory')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      editTeamMemberTab === 'territory'
                        ? 'border-blue-600 text-blue-400'
                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                    }`}
                  >
                    Territory
                  </button>
                  <button
                    onClick={() => setEditTeamMemberTab('services')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      editTeamMemberTab === 'services'
                        ? 'border-blue-600 text-blue-400'
                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                    }`}
                  >
                    Services
                  </button>
                  <button
                    onClick={() => setEditTeamMemberTab('territory')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      editTeamMemberTab === 'territory'
                        ? 'border-blue-600 text-blue-400'
                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                    }`}
                  >
                    Territory
                  </button>
                  <button
                    onClick={() => setEditTeamMemberTab('permissions')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      editTeamMemberTab === 'permissions'
                        ? 'border-blue-600 text-blue-400'
                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                    }`}
                  >
                    Permissions
                  </button>
                </div>
              </div>

              <div className="modal-content-scroll p-8">
                {/* General Tab */}
                {editTeamMemberTab === 'general' && (
                <div className="space-y-6">
                  {/* Re-send Invite Link */}
                  {editingTeamMember && editingTeamMember.invitation_status !== 'accepted' && !editingTeamMember.user_id && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-yellow-800">Invitation pending</p>
                        <p className="text-xs text-yellow-600">This team member hasn't accepted their invitation yet.</p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/team/${editingTeamMember.id}/resend-invite`);
                            showToast('Invitation re-sent successfully!', 'success');
                          } catch (err) {
                            showToast('Failed to resend invitation', 'error');
                          }
                        }}
                        className="px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-100 hover:bg-yellow-200 rounded-lg transition-colors"
                      >
                        Re-send Invite
                      </button>
                    </div>
                  )}
                  
                  {/* Avatar Upload */}
                  <div className="flex items-center gap-6">
                    {teamMemberAvatarPreview ? (
                      <img
                        src={teamMemberAvatarPreview}
                        alt="Avatar preview"
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    ) : existingTeamMemberAvatarUrl ? (
                      <img
                        src={existingTeamMemberAvatarUrl}
                        alt="Avatar"
                        className="w-20 h-20 rounded-full object-cover"
                        onError={(e) => { 
                          e.target.onerror = null;
                          setExistingTeamMemberAvatarUrl(null);
                        }}
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-xl">
                        {editTeamMemberForm.name ? editTeamMemberForm.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '?'}
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        id="edit-team-avatar-upload"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            const file = e.target.files[0];
                            setTeamMemberAvatarFile(file);
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setTeamMemberAvatarPreview(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="hidden"
                      />
                      <label
                        htmlFor="edit-team-avatar-upload"
                        className="clara-btn-secondary px-4 py-2 cursor-pointer inline-block"
                      >
                        {editTeamMemberForm.avatar || teamMemberAvatarPreview ? 'Change Photo' : 'Upload Photo'}
                      </label>
                      {teamMemberAvatarPreview && (
                        <button
                          type="button"
                          onClick={() => {
                            setTeamMemberAvatarFile(null);
                            setTeamMemberAvatarPreview(null);
                          }}
                          className="ml-2 text-sm text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      )}
                      <p className="text-xs mt-1" style={{ color: 'var(--clara-text-muted)' }}>
                        Max file size: 5MB (JPG, PNG, GIF)
                      </p>
                    </div>
                  </div>

                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block clara-body-sm font-medium mb-2" style={{ color: 'var(--clara-secondary)' }}>Name</label>
                      <input
                        type="text"
                        value={editTeamMemberForm.name}
                        onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, name: e.target.value})}
                        className="clara-input w-full"
                      />
                    </div>
                    <div>
                      <label className="block clara-body-sm font-medium mb-2" style={{ color: 'var(--clara-secondary)' }}>Email</label>
                      <input
                        type="email"
                        value={editTeamMemberForm.email}
                        onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, email: e.target.value})}
                        className="clara-input w-full"
                      />
                    </div>
                    <div>
                      <label className="block clara-body-sm font-medium mb-2" style={{ color: 'var(--clara-secondary)' }}>Role</label>
                      <select
                        value={editTeamMemberForm.role}
                        onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, role: e.target.value})}
                        className="clara-input w-full"
                      >
                        <option value="Agent">Agent</option>
                        <option value="Photographer">Photographer</option>
                        <option value="Videographer">Videographer</option>
                        <option value="Photo Editor">Photo Editor</option>
                        <option value="Video Editor">Video Editor</option>
                        <option value="Manager">Manager</option>
                        <option value="Admin">Admin</option>
                      </select>
                    </div>
                    <div>
                      <label className="block clara-body-sm font-medium mb-2" style={{ color: 'var(--clara-secondary)' }}>Phone</label>
                      <input
                        type="tel"
                        value={editTeamMemberForm.phone}
                        onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, phone: e.target.value})}
                        className="clara-input w-full"
                      />
                    </div>
                  </div>

                  {/* Account Status */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block clara-body-sm font-medium mb-1" style={{ color: 'var(--clara-secondary)' }}>Account Status</label>
                      <div className="flex items-center gap-2">
                        {editingTeamMember?.invitation_status === 'accepted' || editingTeamMember?.user_id ? (
                          <>
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                              ✓ Active
                            </span>
                            <span className="text-sm text-gray-600">
                              Team member has accepted invitation and has an active account
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-300 rounded-full text-sm font-medium">
                              ⏳ Invited
                            </span>
                            <span className="text-sm text-gray-600">
                              {editingTeamMember?.invitation_status === 'pending' 
                                ? 'Waiting for team member to accept invitation' 
                                : 'Legacy team member - send invitation to activate'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    {!(editingTeamMember?.invitation_status === 'accepted' || editingTeamMember?.user_id) && (
                      <button
                        onClick={async () => {
                          try {
                            // Always use resend endpoint - it handles both new invitations and resends
                            await axios.post(`${API}/team/resend-invitation/${editingTeamMember.id}`);
                            alert('Invitation sent successfully! Check your email.');
                            
                            // Refresh team members to show updated status
                            const response = await axios.get(`${API}/settings/team-members`);
                            setTeamMembers(response.data);
                            
                            // Update the editing team member state
                            const updatedMember = response.data.find(m => m.id === editingTeamMember.id);
                            if (updatedMember) {
                              setEditingTeamMember(updatedMember);
                            }
                          } catch (error) {
                            alert('Failed to send invitation: ' + (error.response?.data?.detail || error.message));
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium whitespace-nowrap"
                      >
                        {editingTeamMember?.invitation_status === 'pending' ? 'Resend Invitation' : 'Send Invitation'}
                      </button>
                    )}
                  </div>
                </div>

                </div>
                )}

                {/* Territory Tab */}
                {editTeamMemberTab === 'territory' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Home Base Address */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      Home Base Address
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Starting location for calculating drive time and distance to shoots</p>
                    <input
                      type="text"
                      value={editTeamMemberForm.home_base_address || ''}
                      onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, home_base_address: e.target.value})}
                      placeholder="Enter home base address..."
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Service Territory Radius */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      Service Territory
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Maximum distance this photographer will travel for shoots. Orders outside this radius will not show this photographer as available.</p>
                    
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Radius (miles)</label>
                        <input
                          type="number"
                          min="1"
                          max="500"
                          value={editTeamMemberForm.territory_radius_miles || 50}
                          onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, territory_radius_miles: parseFloat(e.target.value) || 50})}
                          className="w-32 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            <strong className="text-blue-900 dark:text-blue-200">{editTeamMemberForm.territory_radius_miles || 50} mile</strong> service area from home base
                          </p>
                          {editTeamMemberForm.home_base_address && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">From: {editTeamMemberForm.home_base_address}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Territory Info */}
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                    <div className="flex gap-3">
                      <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">How territory filtering works</p>
                        <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">When a client enters their property address on the booking form, the system calculates the distance from each photographer's home base. Only photographers whose territory covers the property location will be shown as available for scheduling.</p>
                      </div>
                    </div>
                  </div>
                </div>
                )}
                {/* Permissions Tab */}
                {editTeamMemberTab === 'permissions' && (
                <div className="space-y-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="block clara-body font-medium mb-3" style={{ color: 'var(--clara-secondary)' }}>
                    Access Permissions
                  </label>
                  <p className="text-xs text-gray-500 mb-4">Control what this team member can view and modify</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Dashboard */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">📊 Dashboard</span>
                      </div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editTeamMemberForm.permissions?.dashboard?.view !== false}
                          onChange={(e) => setEditTeamMemberForm({
                            ...editTeamMemberForm,
                            permissions: {
                              ...editTeamMemberForm.permissions,
                              dashboard: { view: e.target.checked }
                            }
                          })}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-600">View Dashboard</span>
                      </label>
                    </div>

                    {/* Clients */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">👥 Clients</span>
                      </div>
                      <div className="space-y-1">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.clients?.view !== false}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                clients: { ...editTeamMemberForm.permissions?.clients, view: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">View Clients</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.clients?.create === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                clients: { ...editTeamMemberForm.permissions?.clients, create: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Create Clients</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.clients?.edit === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                clients: { ...editTeamMemberForm.permissions?.clients, edit: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Edit Clients</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.clients?.delete === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                clients: { ...editTeamMemberForm.permissions?.clients, delete: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Delete Clients</span>
                        </label>
                      </div>
                    </div>

                    {/* Appointments */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">📅 Appointments</span>
                      </div>
                      <div className="space-y-1">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.appointments?.view !== false}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                appointments: { ...editTeamMemberForm.permissions?.appointments, view: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">View Appointments</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.appointments?.view_all === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                appointments: { ...editTeamMemberForm.permissions?.appointments, view_all: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">View All Appointments (vs. only assigned)</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.appointments?.create === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                appointments: { ...editTeamMemberForm.permissions?.appointments, create: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Create Appointments</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.appointments?.edit === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                appointments: { ...editTeamMemberForm.permissions?.appointments, edit: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Edit Appointments</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.appointments?.delete === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                appointments: { ...editTeamMemberForm.permissions?.appointments, delete: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Delete Appointments</span>
                        </label>
                      </div>
                    </div>

                    {/* Listings */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">🏠 Listings</span>
                      </div>
                      <div className="space-y-1">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.listings?.view !== false}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                listings: { ...editTeamMemberForm.permissions?.listings, view: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">View Listings</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.listings?.edit === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                listings: { ...editTeamMemberForm.permissions?.listings, edit: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Edit Listings</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.listings?.manage_media === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                listings: { ...editTeamMemberForm.permissions?.listings, manage_media: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Manage Media</span>
                        </label>
                      </div>
                    </div>

                    {/* Team Management */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">👤 Team Management</span>
                      </div>
                      <div className="space-y-1">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.team?.view === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                team: { ...editTeamMemberForm.permissions?.team, view: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">View Team Members</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.team?.manage === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                team: { ...editTeamMemberForm.permissions?.team, manage: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Add/Edit/Remove Team Members</span>
                        </label>
                      </div>
                    </div>

                    {/* Settings */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">⚙️ Settings</span>
                      </div>
                      <div className="space-y-1">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.settings?.view_products === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                settings: { ...editTeamMemberForm.permissions?.settings, view_products: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">View Products/Services</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.settings?.manage_products === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                settings: { ...editTeamMemberForm.permissions?.settings, manage_products: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Manage Products/Services</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.settings?.manage_business === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                settings: { ...editTeamMemberForm.permissions?.settings, manage_business: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Manage Business Settings</span>
                        </label>
                      </div>
                    </div>

                    {/* Tasks */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">✓ Tasks</span>
                      </div>
                      <div className="space-y-1">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.tasks?.view !== false}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                tasks: { ...editTeamMemberForm.permissions?.tasks, view: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">View Tasks</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.tasks?.view_all === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                tasks: { ...editTeamMemberForm.permissions?.tasks, view_all: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">View All Tasks (vs. only assigned)</span>
                        </label>
                        <label className="flex items-center ml-6">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.permissions?.tasks?.manage === true}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                ...editTeamMemberForm.permissions,
                                tasks: { ...editTeamMemberForm.permissions?.tasks, manage: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Create/Edit/Delete Tasks</span>
                        </label>
                      </div>
                    </div>

                    {/* Quick Presets */}
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs font-medium text-gray-500 mb-2">Quick Presets:</p>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => {
                            setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                dashboard: { view: true },
                                clients: { view: true, create: true, edit: true, delete: true },
                                appointments: { view: true, view_all: true, create: true, edit: true, delete: true },
                                listings: { view: true, edit: true, manage_media: true },
                                team: { view: true, manage: true },
                                settings: { view_products: true, manage_products: true, manage_business: true },
                                tasks: { view: true, view_all: true, manage: true }
                              }
                            });
                          }}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        >
                          👑 Admin
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                dashboard: { view: true },
                                clients: { view: true, create: true, edit: true, delete: false },
                                appointments: { view: true, view_all: true, create: true, edit: true, delete: false },
                                listings: { view: true, edit: true, manage_media: true },
                                team: { view: true, manage: false },
                                settings: { view_products: true, manage_products: false, manage_business: false },
                                tasks: { view: true, view_all: true, manage: true }
                              }
                            });
                          }}
                          className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                        >
                          📊 Manager
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                dashboard: { view: true },
                                clients: { view: true, create: false, edit: false, delete: false },
                                appointments: { view: true, view_all: false, create: false, edit: false, delete: false },
                                listings: { view: true, edit: false, manage_media: false },
                                team: { view: false, manage: false },
                                settings: { view_products: true, manage_products: false, manage_business: false },
                                tasks: { view: true, view_all: false, manage: false }
                              }
                            });
                          }}
                          className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                        >
                          👁️ Viewer
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              permissions: {
                                dashboard: { view: true },
                                clients: { view: false, create: false, edit: false, delete: false },
                                appointments: { view: true, view_all: false, create: false, edit: false, delete: false },
                                listings: { view: false, edit: false, manage_media: false },
                                team: { view: false, manage: false },
                                settings: { view_products: false, manage_products: false, manage_business: false },
                                tasks: { view: true, view_all: false, manage: true }
                              }
                            });
                          }}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          📸 Photographer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
                )}

                {/* Services Tab */}
                {editTeamMemberTab === 'services' && (
                <div className="space-y-6">
                {/* Assigned Packages */}
                <div>
                  <h4 className="clara-body font-medium mb-4 text-gray-900 dark:text-white">Assigned Packages</h4>
                  {packages.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {packages.filter(p => p.is_active !== false).map((pkg) => (
                        <label key={pkg.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.assigned_packages?.includes(pkg.id)}
                            onChange={(e) => {
                              const currentPackages = editTeamMemberForm.assigned_packages || [];
                              const updatedPackages = e.target.checked
                                ? [...currentPackages, pkg.id]
                                : currentPackages.filter(id => id !== pkg.id);
                              setEditTeamMemberForm({...editTeamMemberForm, assigned_packages: updatedPackages});
                            }}
                            className="mr-2"
                          />
                          <span className="clara-body-sm">{pkg.name}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>
                      No packages available. Create packages first to assign them to team members.
                    </p>
                  )}
                </div>

                {/* Assigned Services */}
                <div>
                  <h4 className="clara-body font-medium mb-4 text-gray-900 dark:text-white">Assigned Services</h4>
                  {products.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {products.filter(p => p.is_active !== false).map((product) => (
                        <label key={product.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.assigned_products.includes(product.id)}
                            onChange={(e) => {
                              const updatedProducts = e.target.checked
                                ? [...editTeamMemberForm.assigned_products, product.id]
                                : editTeamMemberForm.assigned_products.filter(id => id !== product.id);
                              setEditTeamMemberForm({...editTeamMemberForm, assigned_products: updatedProducts});
                            }}
                            className="mr-2"
                          />
                          <span className="clara-body-sm">{product.name}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>
                      No services available. Add services first to assign them to team members.
                    </p>
                  )}
                </div>
                </div>
                )}

                {/* Territory Tab */}
                {editTeamMemberTab === 'territory' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - Form Controls */}
                  <div className="space-y-6">
                  {/* Home Base Address */}
                  <div className="clara-card p-6 rounded-lg border" style={{ borderColor: 'var(--clara-border)' }}>
                    <h4 className="clara-body font-medium mb-4 text-white">
                      🏠 Home Base Address
                    </h4>
                    <p className="text-sm text-gray-400 mb-4">
                      Enter the address where this team member is based. This will be used to calculate service territory.
                    </p>
                    <input
                      type="text"
                      placeholder="Enter home base address..."
                      value={editTeamMemberForm.home_base_address || ''}
                      onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, home_base_address: e.target.value})}
                      className="clara-input w-full"
                    />
                  </div>

                  {/* Service Territory Radius */}
                  <div className="clara-card p-6 rounded-lg border" style={{ borderColor: 'var(--clara-border)' }}>
                    <h4 className="clara-body font-medium mb-4 text-white">
                      📍 Service Territory
                    </h4>
                    <p className="text-sm text-gray-400 mb-4">
                      Set the maximum distance this team member will travel for appointments.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Service Radius (miles)</label>
                        <input
                          type="range"
                          min="5"
                          max="150"
                          step="5"
                          value={editTeamMemberForm.territory_radius_miles || 50}
                          onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, territory_radius_miles: parseFloat(e.target.value) || 50})}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>5 mi</span>
                          <span className="text-blue-400 font-semibold text-base">{editTeamMemberForm.territory_radius_miles || 50} miles</span>
                          <span>150 mi</span>
                        </div>
                      </div>
                      <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-700/50">
                        <p className="text-sm text-blue-300">
                          This team member will be available for bookings within a <strong className="text-blue-200">{editTeamMemberForm.territory_radius_miles || 50} mile</strong> radius from their home base.
                        </p>
                        {editTeamMemberForm.home_base_address && (
                          <p className="text-xs text-blue-400 mt-1">📍 From: {editTeamMemberForm.home_base_address}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Territory Info */}
                  <div className="p-4 bg-yellow-900/30 rounded-lg border border-yellow-700/50">
                    <p className="text-sm text-yellow-300 font-medium">⚠️ How territory filtering works</p>
                    <p className="text-xs text-yellow-400 mt-1">
                      When a client enters their property address on the booking form, the system calculates the distance from each photographer's home base. Only photographers whose territory covers the property location will be shown as available for scheduling.
                    </p>
                  </div>
                  </div>

                  {/* Right Column - Map */}
                  <div className="clara-card p-6 rounded-lg border" style={{ borderColor: 'var(--clara-border)', minHeight: '450px' }}>
                    <h4 className="clara-body font-medium mb-4 text-white">
                      🗺️ Coverage Area Preview
                    </h4>
                    <TerritoryMap 
                      address={editTeamMemberForm.home_base_address}
                      radiusMiles={editTeamMemberForm.territory_radius_miles || 50}
                    />
                  </div>
                </div>
                )}

                {/* Schedule Tab */}
                {editTeamMemberTab === 'schedule' && (
                <div className="space-y-6">
                {/* Working Hours */}
                <div>
                  <h4 className="clara-body font-medium mb-4" style={{ color: 'var(--clara-primary)' }}>Working Hours</h4>
                  <div className="space-y-3">
                    {Object.entries(editTeamMemberForm.working_hours).map(([day, hours]) => (
                      <div key={day} className="grid grid-cols-4 gap-3 items-center">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={hours.enabled}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              working_hours: {
                                ...editTeamMemberForm.working_hours,
                                [day]: { ...hours, enabled: e.target.checked }
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="clara-body-sm capitalize">{day}</span>
                        </div>
                        <div>
                          <input
                            type="time"
                            value={hours.start}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              working_hours: {
                                ...editTeamMemberForm.working_hours,
                                [day]: { ...hours, start: e.target.value }
                              }
                            })}
                            disabled={!hours.enabled}
                            className="clara-input w-full text-sm"
                          />
                        </div>
                        <div>
                          <input
                            type="time"
                            value={hours.end}
                            onChange={(e) => setEditTeamMemberForm({
                              ...editTeamMemberForm,
                              working_hours: {
                                ...editTeamMemberForm.working_hours,
                                [day]: { ...hours, end: e.target.value }
                              }
                            })}
                            disabled={!hours.enabled}
                            className="clara-input w-full text-sm"
                          />
                        </div>
                        <div className="text-xs" style={{ color: 'var(--clara-medium-gray)' }}>
                          {hours.enabled ? `${hours.start} - ${hours.end}` : 'Off'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Google Calendar Integration */}
                <div>
                  <h4 className="clara-body font-medium mb-4" style={{ color: 'var(--clara-primary)' }}>Google Calendar Integration</h4>
                  {editingTeamMember && editingTeamMember.calendar_integration && editingTeamMember.calendar_integration.is_active ? (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-green-900 dark:text-green-100">Connected</p>
                            <p className="text-sm text-green-700 dark:text-green-300">
                              {editingTeamMember.calendar_integration.connected_email || 'Google Calendar'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => disconnectTeamMemberCalendar(editingTeamMember.id)}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
                        >
                          Disconnect
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">Not Connected</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Connect to sync availability and events</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedMemberForCalendar(editingTeamMember);
                            setShowEditTeamMember(false);
                            setShowCalendarModal(true);
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                        >
                          Connect Calendar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                </div>
                )}
              </div>
              <div className="modal-footer-fixed px-8 py-6 border-t" style={{ borderColor: 'var(--clara-border)' }}>
                <div className="flex space-x-4">
                  <button
                    onClick={updateTeamMember}
                    className="flex-1 clara-btn-primary py-3"
                  >
                    Update Team Member
                  </button>
                  <button
                    onClick={() => {
                      setShowEditTeamMember(false);
                      setEditingTeamMember(null);
                    }}
                    className="flex-1 clara-btn-secondary py-3"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Product Modal */}

        {/* Media Manager Modal */}

        {/* Google Calendar Event Details Modal */}


        {/* Edit Address Modal */}
        {showEditAddressModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[150]">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
              <div className="px-6 py-4 bg-gray-700 rounded-t-xl">
                <h3 className="text-lg font-semibold text-white">
                  Edit Property Address
                </h3>
              </div>
              <form onSubmit={handleEditAddress} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Property Address *</label>
                  <textarea
                    value={editAddressForm}
                    onChange={(e) => setEditAddressForm(e.target.value)}
                    className="clara-input w-full"
                    rows="3"
                    placeholder="Enter full property address"
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditAddressModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    Save Address
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmDialog
          isOpen={showDeleteConfirm && mediaToDelete !== null}
          onClose={() => {
            setShowDeleteConfirm(false);
            setMediaToDelete(null);
          }}
          onConfirm={() => {
            deleteMediaFile(mediaToDelete.id);
            setShowDeleteConfirm(false);
            setMediaToDelete(null);
          }}
          title="Confirm Delete"
          message="Are you sure you want to delete this file?"
          subtitle={mediaToDelete ? (
            <>
              <span className="font-medium">{mediaToDelete.original_filename}</span>
              <br />
              This action cannot be undone.
            </>
          ) : null}
          confirmText="Yes"
          cancelText="No"
        />

        {/* Image Slideshow Modal */}
        {slideshowOpen && (() => {
          const images = mediaFiles.filter(m => m.mime_type?.startsWith('image/'));
          const currentImage = images[slideshowIndex];
          console.log('Slideshow open:', slideshowOpen, 'Current image:', currentImage);
          
          if (!currentImage) {
            return (
              <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center" style={{ zIndex: 9999 }}>
                <button
                  onClick={() => setSlideshowOpen(false)}
                  className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <p className="text-white">No image available</p>
              </div>
            );
          }
          
          // Use thumbnailUrl if available, otherwise construct authenticated URL
          const imageUrl = currentImage.thumbnailUrl || `${BACKEND_URL}/api/media/${currentImage.id}/thumbnail?size=1200`;
          
          return (
            <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center" style={{ zIndex: 9999 }}>
              <button
                onClick={() => setSlideshowOpen(false)}
                className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Previous Button */}
              {slideshowIndex > 0 && (
                <button
                  onClick={() => setSlideshowIndex(slideshowIndex - 1)}
                  className="absolute left-4 text-white hover:text-gray-300 p-2 bg-black bg-opacity-50 rounded-full"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}

              {/* Image */}
              <div className="max-w-full max-h-[90vh] flex flex-col items-center px-20">
                <img
                  src={imageUrl}
                  alt={currentImage?.original_filename}
                  className="max-w-full max-h-[80vh] object-contain"
                />
                <div className="mt-4 text-center">
                  <p className="text-white text-lg font-medium mb-2">{currentImage?.original_filename}</p>
                  <p className="text-gray-400 text-sm">
                    Image {slideshowIndex + 1} of {images.length}
                  </p>
                </div>
              </div>

              {/* Next Button */}
              {slideshowIndex < images.length - 1 && (
                <button
                  onClick={() => setSlideshowIndex(slideshowIndex + 1)}
                  className="absolute right-4 text-white hover:text-gray-300 p-2 bg-black bg-opacity-50 rounded-full"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {/* Keyboard navigation hint */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-gray-400 text-sm">
                Use arrow keys or click buttons to navigate • Press ESC to close
              </div>
            </div>
          );
        })()}

        {/* Create/Edit Package Modal */}

        {/* Add/Edit Add-on Product Modal */}

        {/* Add Client Modal */}
        {showAddClientModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[150]">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
              <div className="px-6 py-4 bg-blue-600 rounded-t-xl flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white">
                  {showCreateNewClient ? 'Create New Client' : 'Add Client to Property'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddClientModal(false);
                    setShowCreateNewClient(false);
                    setClientSearchQuery('');
                    setClientSearchResults([]);
                    setNewClientForm({ name: '', email: '', phone: '', company: '' });
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {!showCreateNewClient ? (
                <div className="p-6 space-y-4">
                  {/* Search Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Search Existing Clients
                    </label>
                    <input
                      type="text"
                      value={clientSearchQuery}
                      onChange={(e) => setClientSearchQuery(e.target.value)}
                      placeholder="Search by name, email, or company..."
                      className="clara-input w-full"
                      autoFocus
                    />
                  </div>

                  {/* Search Results */}
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {clientSearchQuery.trim() === '' ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                        Start typing to search for clients
                      </p>
                    ) : clientSearchResults.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                        No clients found
                      </p>
                    ) : (
                      clientSearchResults
                        .filter(c => c.id !== selectedListingForEdit?.client_id && !propertyClients.includes(c.id))
                        .map(client => (
                          <div
                            key={client.id}
                            onClick={() => addClientToProperty(client.id)}
                            className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors border border-gray-200 dark:border-gray-600"
                          >
                            <div className="flex items-center gap-3">
                              {client.avatar && clientAvatarUrls[client.avatar] ? (
                                <img
                                  src={clientAvatarUrls[client.avatar]}
                                  alt={client.name}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                                  {client.name?.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {client.name}
                                </p>
                                {client.email && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    {client.email}
                                  </p>
                                )}
                                {client.company && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {client.company}
                                  </p>
                                )}
                              </div>
                              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </div>
                          </div>
                        ))
                    )}
                  </div>

                  {/* Create New Client Button */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => setShowCreateNewClient(true)}
                      className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create New Client
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={createNewClient} className="p-6 space-y-4">
                  {/* New Client Form */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={newClientForm.name}
                      onChange={(e) => setNewClientForm({ ...newClientForm, name: e.target.value })}
                      className="clara-input w-full"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newClientForm.email}
                      onChange={(e) => setNewClientForm({ ...newClientForm, email: e.target.value })}
                      className="clara-input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={newClientForm.phone}
                      onChange={(e) => setNewClientForm({ ...newClientForm, phone: e.target.value })}
                      className="clara-input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      value={newClientForm.company}
                      onChange={(e) => setNewClientForm({ ...newClientForm, company: e.target.value })}
                      className="clara-input w-full"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateNewClient(false);
                        setNewClientForm({ name: '', email: '', phone: '', company: '' });
                      }}
                      className="flex-1 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                    >
                      Create & Add
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}



        {/* Automated Task Rule Modal */}



        {/* Task Form Modal */}
        <TaskModals />


        {/* Appointment Details Modal */}
        <AppointmentDetailsModal />

        </div>

      {/* Command Center - Cmd+K */}
      <CommandCenter
        isOpen={showCommandCenter}
        onClose={handleCommandCenterClose}
        searchQuery={searchQuery}
        onSearchChange={handleCommandCenterSearch}
        searchResults={searchResults}
        onResultClick={handleCommandCenterResultClick}
        onClearSearch={handleCommandCenterClear}
        clientAvatarUrls={clientAvatarUrls}
      />
    </div>
    </DashboardContext.Provider>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [customDomainBusinessId, setCustomDomainBusinessId] = useState(null);
  const [checkingCustomDomain, setCheckingCustomDomain] = useState(true);

  // Check if we're on a custom domain (not est8.media or localhost).
  // CRITICAL: this MUST never block the app render forever. We use:
  //   1. A `finally` block so any path through this function eventually
  //      clears `checkingCustomDomain`.
  //   2. An AbortController + 4-second hard timeout on the network lookup.
  //   3. An expanded fast-path list (with subdomain matches) so known hosts
  //      skip the network call entirely.
  // History: this code was the root cause of a previously-fixed "production
  // shows blank forever" bug. The earlier fix added the fast-path list but
  // forgot to time-bound the network branch — putting the bug right back the
  // moment the lookup endpoint became slow.
  useEffect(() => {
    const FAST_PATH_HOSTS = (h) => (
      h === 'localhost'
      || h === '127.0.0.1'
      || h === 'est8.media'
      || h.endsWith('.est8.media')
      || h.endsWith('.emergentagent.com')
      || h.endsWith('.vercel.app')
      || h.endsWith('.netlify.app')
    );

    const checkCustomDomain = async () => {
      const hostname = window.location.hostname;
      if (FAST_PATH_HOSTS(hostname)) {
        return; // finally clears the spinner
      }
      // Custom domain — look up the business with a hard timeout
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 4000);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || ''}/api/public/lookup-domain/${hostname}`,
          { signal: controller.signal }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.business_id) {
            setCustomDomainBusinessId(data.business_id);
          }
        }
      } catch (error) {
        // Aborted / network / DNS — don't block the app; just continue as
        // a "normal" (non-custom-domain) page.
        console.warn('Custom-domain lookup failed; continuing without it:', error?.message || error);
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    checkCustomDomain().finally(() => setCheckingCustomDomain(false));
  }, []);

  const handleLoginSuccess = (token, userData) => {
    setIsAuthenticated(true);
    // AuthProvider will handle the actual login
  };

  // Show loading while checking custom domain
  if (checkingCustomDomain) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If on a custom domain, show the booking page for that business
  if (customDomainBusinessId) {
    return <PublicScheduling businessId={customDomainBusinessId} />;
  }

  // Check for special pages
  const urlParams = new URLSearchParams(window.location.search);
  const pathname = window.location.pathname;
  
  if (pathname === '/accept-invitation' && urlParams.get('token')) {
    return <AcceptInvitation />;
  }
  
  if (pathname === '/forgot-password') {
    return <ForgotPassword />;
  }
  
  if (pathname === '/reset-password' && urlParams.get('token')) {
    return <ResetPassword />;
  }

  // Client portal signup from invitation
  if (pathname === '/client-signup' && urlParams.get('token')) {
    return <ClientSignup />;
  }

  // Property website pages - /property/:slug and /property/:slug/modern
  if (pathname.startsWith('/property/')) {
    const pathParts = pathname.replace('/property/', '').split('/');
    const slug = pathParts[0];
    const template = pathParts[1] || 'flow'; // default to 'flow' template
    if (slug) {
      return <PropertyWebsite slug={slug} template={template} />;
    }
  }

  // Public scheduling page - /b/:businessId (short URL)
  if (pathname.startsWith('/b/')) {
    const businessId = pathname.replace('/b/', '');
    if (businessId) {
      return <PublicScheduling businessId={businessId} />;
    }
  }

  // Public scheduling page - /schedule/:businessId (full URL)
  if (pathname.startsWith('/schedule/')) {
    const businessId = pathname.replace('/schedule/', '');
    if (businessId) {
      return <PublicScheduling businessId={businessId} />;
    }
  }

  return (
    <AuthProvider>
      <div className="App">
        <AuthConsumer>
          {({ user, permissions, hasPermission, login, logout, loading }) => {
            if (loading) {
              return (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              );
            }

            if (!user) {
              return <LoginForm onSuccess={login} />;
            }

            // Check if user is a client - show ClientPortal instead of Dashboard
            if (user.role === 'client') {
              return <ClientPortal onLogout={logout} />;
            }

            return <Dashboard />;
          }}
        </AuthConsumer>
      </div>
    </AuthProvider>
  );
}

export default App;
